import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FaUser, FaBook, FaTasks, FaBullseye, FaSearch, FaSpinner, FaChartLine, FaFileAlt, FaCode, FaCogs, FaCalculator, FaUsers, FaBriefcase, FaBullhorn, FaGlobe, FaShareAlt, FaNewspaper, FaStar, FaUserPlus, FaHeart, FaClock, FaTrash, FaEdit, FaPlus, FaThumbtack, FaTimes, FaRobot } from 'react-icons/fa';
import AddButton from '../../components/AddButton';
import Modal from '../../components/Modal';
import BBALoader from '../../components/BBALoader';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getAccounts, getCrmContactDisplayPhotoUrl, getCrmTeamMemberDisplayPhotoUrl, getCrmCompanyLogoDisplayUrl, getAccountById, getPrimaryUserIdForAccount, getCompanyIdForUser, findPrimaryPortalUserDocs } from '../../services/firestoreService';
import { getAllCourses, getAllUserCourseProgress } from '../../services/firebase/courses';
import { getQuestionsByActualAccountId } from '../../services/firebase/questions';
import { getFiresByActualAccountId } from '../../services/firebase/fires';
import { fetchAccountCalls } from '../../services/departmentCallsService';
import clientRoadmapService from '../../services/clientRoadmapService';
import { transformClientRoadmapTemplates } from '../../utils/roadmapDashboardTasks';
import DocumentViewerModal from '../../components/DocumentViewerModal';
import MeetingSummaryContent from '../../components/MeetingSummaryContent';
import { getTasksForPortalUser, createTask, toggleTaskCompletion } from '../../services/taskService';
import { getInternalUsers } from '../../services/userService';
import { getUserGoals, updateUserGoal, addUserGoal, deleteUserGoal } from '../../services/firebase/userGoalsService';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, setDoc, addDoc, deleteDoc, serverTimestamp, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import JobListingsContent from '../../components/JobListingsContent';
import MissionVisionValuesContent from '../../components/MissionVisionValuesContent';
import HRHiringContent from '../../components/HRHiringContent';
import LeadsCalculatorContent from '../../components/LeadsCalculatorContent';
import LeadsROIContent from '../../components/LeadsROIContent';
import { addNewsletterCampaign, updateNewsletterCampaign, deleteNewsletterCampaign, getUserNewsletterData, saveUserNewsletterData } from '../../services/newsletterService';
import { getReviewsData } from '../../services/reviewsService';
import { isInternalUser } from '../../services/accessControlService';
import { isKickoffEligibleClient } from '../../utils/crmClientListFilters';
import { fetchPortalUsersLinkedToAccounts, fetchClientEventRosterMembers } from '../../services/accountTeamMembersService';
import SalesAnalyticsTab from '../../components/sales/SalesAnalyticsTab';
import SalesResourcesTab from '../../components/sales/SalesResourcesTab';
import DesignPricingPage from '../DesignPricing';
import BuildPaymentCalculatorPage from '../BuildPaymentCalculator';
import { SALES_TABS } from '../../utils/salesTabs';
import FinanceMaterialsTab from '../../components/finance/FinanceMaterialsTab';
import { FINANCE_TABS } from '../../utils/financeTabs';
import TechStackPage from '../TechStack';
import AutomationPage from '../Automation';
import JobTreadImportsPage from '../JobTreadImports';
import ClaudeSkillsPage from '../ClaudeSkills';
import AgentsPage from '../Agents';
import { TECHNOLOGY_TABS } from '../../utils/technologyTabs';
import SOPsPage from '../SOPs';
import PortalKPIsPage from '../PortalKPIs';
import JobDescriptionsPage from '../JobDescriptions';
import OrgChartPage from '../OrgChart';
import { OPERATIONS_TABS } from '../../utils/operationsTabs';
import MarketingAnalyticsTab from '../../components/marketing/MarketingAnalyticsTab';
import FinanceAnalyticsTab from '../../components/finance/FinanceAnalyticsTab';
import MarginCalculator from '../MarginCalculator';
import OverheadCalculator from '../OverheadCalculator';
import LaborBurdenCalculator from '../LaborBurden';
import PortalGoalsGrid from '../../components/portal/PortalGoalsGrid';
import { getPortalEmoji } from '../../utils/portalEmojis';
import WebsitePage from '../Website';
import ReviewsDashboard from '../Reviews';
import WebsiteCalculatorsPage from '../WebsiteCalculators';
import LeadsCalculatorPage from '../LeadsCalculator';
import { MARKETING_TABS } from '../../utils/marketingTabs';
import CompanyDirection from '../CompanyDirection';
import HRHiring from '../HRHiring';
import HRHiringStatusCard from '../../components/hr/HRHiringStatusCard';
import HRMaterialsTab from '../../components/hr/HRMaterialsTab';
import { HR_TABS } from '../../utils/hrTabs';
import { CheckCircle2, Clock, Edit2, Trash2, X, Star, Plus, Users, FileText, Check, Eye, MousePointer, Activity, MessageCircle, Camera, Hash, Tag, MapPin, Phone, Globe, Image, CheckCircle, ListTodo, ClipboardList, Calendar as CalendarIcon, PlayCircle, Lock, Map as MapIcon, ExternalLink } from 'lucide-react';
import PortalBackground from '../../components/PortalBackground';

// Snapshot-shaped list of the account's primary portal user docs (best match first).
// Replaces exact `where('email', '==', contact.email)` lookups, which missed users whose
// stored email differs in case and could pick a stale same-email doc from a deleted account.
const getAccountPortalUserSnapshot = async (account, { role } = {}) => {
  const docs = (await findPrimaryPortalUserDocs(account)).filter((d) => !role || d.data()?.role === role);
  return { empty: docs.length === 0, docs, size: docs.length };
};

// Emoji per department sub-tab, sourced from the canonical portal tab configs so
// the CRM Portal View mirrors the portal hub navs exactly. Keyed by the CRM tab id
// (which matches the portal id, except Sales "materials" maps to the portal "resources").
const OPERATIONS_TAB_EMOJI = Object.fromEntries(OPERATIONS_TABS.map((t) => [t.id, t.emoji]));
const salesEmoji = (portalId) => SALES_TABS.find((t) => t.id === portalId)?.emoji;
const SALES_TAB_EMOJI = {
  analytics: salesEmoji('analytics'),
  goals: salesEmoji('goals'),
  materials: salesEmoji('resources'),
  'design-pricing': salesEmoji('design-pricing'),
  'build-payments': salesEmoji('build-payments'),
};
const techEmoji = (portalId) => TECHNOLOGY_TABS.find((t) => t.id === portalId)?.emoji;
const TECHNOLOGY_TAB_EMOJI = {
  agents: techEmoji('agents'),
  automations: techEmoji('automation'),
  'tech-stack': techEmoji('tech-stack'),
  goals: techEmoji('goals'),
  'jobtread-imports': techEmoji('jobtread-imports'),
  'claude-skills': techEmoji('claude-skills'),
};
const hrEmoji = (portalId) => HR_TABS.find((t) => t.id === portalId)?.emoji;
const HR_TAB_EMOJI = {
  hiring: hrEmoji('hiring'),
  materials: hrEmoji('materials'),
  direction: hrEmoji('direction'),
};
const marketingEmoji = (portalId) => MARKETING_TABS.find((t) => t.id === portalId)?.emoji;
const MARKETING_TAB_EMOJI = {
  analytics: marketingEmoji('analytics'),
  goals: marketingEmoji('goals'),
  website: marketingEmoji('website'),
  reviews: marketingEmoji('reviews'),
  calculators: marketingEmoji('calculators'),
  'leads-calculator': marketingEmoji('leads-calculator'),
};
const financeEmoji = (portalId) => FINANCE_TABS.find((t) => t.id === portalId)?.emoji;
const FINANCE_TAB_EMOJI = {
  analytics: financeEmoji('analytics'),
  goals: financeEmoji('goals'),
  margin: financeEmoji('margin'),
  overhead: financeEmoji('overhead-calculator'),
  'labor-burden': financeEmoji('labor-burden'),
  materials: financeEmoji('materials'),
};
// General sub-tab emojis — courses/tasks/collection come from the canonical portal
// emoji map; questions/fires have no canonical glyph so are set here.
const GENERAL_TAB_EMOJI = {
  roadmap: getPortalEmoji('roadmap'),
  courses: getPortalEmoji('courses'),
  tasks: getPortalEmoji('tasks'),
  questions: '❓',
  fires: '🔥',
  collection: getPortalEmoji('collection'),
};
// Departments whose CRM tab bar uses the portal's pill-style hub nav (emoji pills).
const DEPARTMENT_TAB_EMOJI = {
  General: GENERAL_TAB_EMOJI,
  Operations: OPERATIONS_TAB_EMOJI,
  Sales: SALES_TAB_EMOJI,
  Technology: TECHNOLOGY_TAB_EMOJI,
  Finance: FINANCE_TAB_EMOJI,
  Marketing: MARKETING_TAB_EMOJI,
  HR: HR_TAB_EMOJI,
};

// Add Newsletter Campaign Modal Component
const AddNewsletterModal = ({ show, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    totalEmails: '',
    frequency: '',
    startDate: ''
  });

  console.log('AddNewsletterModal render - show:', show);

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd({
      ...formData,
      status: 'active',
      subscribers: 0,
      sent: 0
    });
    setFormData({ name: '', description: '', totalEmails: '', frequency: '', startDate: '' });
  };

  if (!show) return null;
    
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md mx-auto">
        <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white text-center">Add Newsletter Campaign</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Campaign Name</label>
            <input
              type="text"
              required
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <textarea
              required
              rows={3}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Describe your newsletter campaign..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total Emails in Campaign</label>
            <input
              type="number"
              required
              min="1"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              value={formData.totalEmails}
              onChange={(e) => setFormData({...formData, totalEmails: e.target.value})}
              placeholder="Enter total number of emails planned"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Frequency</label>
            <select
              required
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              value={formData.frequency}
              onChange={(e) => setFormData({...formData, frequency: e.target.value})}
            >
              <option value="">Select Frequency</option>
              <option value="Daily">Daily</option>
              <option value="Weekly">Weekly</option>
              <option value="Bi-weekly">Bi-weekly</option>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="One-time">One-time</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
            <input
              type="date"
              required
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              value={formData.startDate}
              onChange={(e) => setFormData({...formData, startDate: e.target.value})}
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
            >
              Add Campaign
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Newsletter Details Modal Component
const NewsletterDetailsModal = ({ campaign, onClose, onUpdate, onDelete }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [editMode, setEditMode] = useState(false);
  const [metrics, setMetrics] = useState(campaign?.metrics || {});
  const [campaignData, setCampaignData] = useState({
    name: '',
    description: '',
    platform: '',
    startDate: '',
    endDate: '',
    status: '',
    totalEmails: 0,
    frequency: '',
    subscribers: 0
  });

  useEffect(() => {
    if (campaign) {
      setMetrics(campaign.metrics);
      setCampaignData({
        name: campaign.name || '',
        description: campaign.description || '',
        platform: campaign.platform || '',
        startDate: campaign.startDate || '',
        endDate: campaign.endDate || '',
        status: campaign.status || '',
        totalEmails: campaign.totalEmails || 0,
        frequency: campaign.frequency || '',
        subscribers: campaign.subscribers || 0
      });
    }
  }, [campaign]);

  const handleSave = () => {
    const updateData = {
      ...campaignData,
      metrics: {
        ...metrics,
        openRate: metrics.openRate || 0,
        clickRate: metrics.clickRate || 0,
        bounceRate: metrics.bounceRate || 0,
        unsubscribeRate: metrics.unsubscribeRate || 0,
        revenue: metrics.revenue || 0
      }
    };
    console.log('NewsletterDetailsModal - Updating campaign with ID:', campaign.id);
    console.log('NewsletterDetailsModal - Campaign object:', campaign);
    onUpdate(campaign.id, updateData);
    setEditMode(false);
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${campaign.name}"? This action cannot be undone.`)) {
      onDelete(campaign.id);
      onClose();
    }
  };

  if (!campaign) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex-1"></div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center flex-1">{campaign.name}</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex-1 flex justify-end"
          >
            ✕
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 mb-6">
          <button
            onClick={() => setActiveTab('details')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'details'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('metrics')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'metrics'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Metrics
          </button>
        </div>
        
        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Name</label>
                {editMode ? (
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    value={campaignData.name}
                    onChange={(e) => setCampaignData({...campaignData, name: e.target.value})}
                  />
                ) : (
                  <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                    <span className="text-gray-900 dark:text-white">{campaignData.name}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Date</label>
                {editMode ? (
                  <input
                    type="date"
                    className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    value={campaignData.startDate}
                    onChange={(e) => setCampaignData({...campaignData, startDate: e.target.value})}
                  />
                ) : (
                  <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                    <span className="text-gray-900 dark:text-white">{campaignData.startDate}</span>
                  </div>
                )}
              </div>
              {campaignData.status === 'completed' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">End Date</label>
                  {editMode ? (
                    <input
                      type="date"
                      className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      value={campaignData.endDate}
                      onChange={(e) => setCampaignData({...campaignData, endDate: e.target.value})}
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                      <span className="text-gray-900 dark:text-white">{campaignData.endDate}</span>
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                {editMode ? (
                  <select
                    className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    value={campaignData.status}
                    onChange={(e) => setCampaignData({...campaignData, status: e.target.value})}
                  >
                    <option value="">Select Status</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="completed">Completed</option>
                  </select>
                ) : (
                  <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                    <span className="text-gray-900 dark:text-white">{campaignData.status.charAt(0).toUpperCase() + campaignData.status.slice(1)}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Total Emails</label>
                {editMode ? (
                  <input
                    type="number"
                    className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    value={campaignData.totalEmails}
                    onChange={(e) => setCampaignData({...campaignData, totalEmails: parseInt(e.target.value) || 0})}
                  />
                ) : (
                  <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                    <span className="text-gray-900 dark:text-white">{campaignData.totalEmails}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Frequency</label>
                {editMode ? (
                  <select
                    className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    value={campaignData.frequency}
                    onChange={(e) => setCampaignData({...campaignData, frequency: e.target.value})}
                  >
                    <option value="">Select Frequency</option>
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Bi-weekly">Bi-weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="One-time">One-time</option>
                  </select>
                ) : (
                  <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                    <span className="text-gray-900 dark:text-white">{campaignData.frequency}</span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
              {editMode ? (
                <textarea
                  className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  rows="3"
                  value={campaignData.description}
                  onChange={(e) => setCampaignData({...campaignData, description: e.target.value})}
                />
              ) : (
                <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                  <span className="text-gray-900 dark:text-white">{campaignData.description || 'No description provided'}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Metrics Tab */}
        {activeTab === 'metrics' && (
          <div className="space-y-6">
            {editMode ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Open Rate (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      value={metrics.openRate || 0}
                      onChange={(e) => setMetrics({...metrics, openRate: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Click Rate (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      value={metrics.clickRate || 0}
                      onChange={(e) => setMetrics({...metrics, clickRate: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bounce Rate (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      value={metrics.bounceRate || 0}
                      onChange={(e) => setMetrics({...metrics, bounceRate: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Unsubscribe Rate (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      value={metrics.unsubscribeRate || 0}
                      onChange={(e) => setMetrics({...metrics, unsubscribeRate: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.openRate?.toFixed(1) || 0}%</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Open Rate</p>
                </div>
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.clickRate?.toFixed(1) || 0}%</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Click Rate</p>
                </div>
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.bounceRate?.toFixed(1) || 0}%</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Bounce Rate</p>
                </div>
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.unsubscribeRate?.toFixed(1) || 0}%</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Unsubscribe Rate</p>
                </div>
              </div>
            )}
          </div>
        )}
        
        <div className="flex justify-between mt-6">
          <button
            onClick={handleDelete}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
          >
            Delete Campaign
          </button>
          <div className="flex space-x-3">
            <button
              onClick={() => setEditMode(!editMode)}
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
            >
              {editMode ? 'Cancel Edit' : 'Edit'}
            </button>
            {editMode && (
              <button
                onClick={handleSave}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
              >
                Save
              </button>
            )}
            <button
              onClick={onClose}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contact to Build SOP template and metadata for Operations department

const PortalView = () => {
  const { currentUser } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data states
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [portalUserId, setPortalUserId] = useState(null);
  const [internalUsers, setInternalUsers] = useState([]);
  const [selectedView, setSelectedView] = useState('courses');
  const [selectedDepartment, setSelectedDepartment] = useState('General');
  
  // Track previous location to detect navigation changes
  const prevLocationRef = React.useRef(location.pathname);
  const isUserAction = React.useRef(false);
  const syncingFromUrlRef = React.useRef(false);
  
  // Helper function to update URL based on current state
  const updateURL = useCallback((accountId, department, tab, content = null) => {
    if (accountId && department && tab) {
      const path = content 
        ? `/crm/portalview/${accountId}/${encodeURIComponent(department)}/${encodeURIComponent(tab)}/${encodeURIComponent(content)}`
        : `/crm/portalview/${accountId}/${encodeURIComponent(department)}/${encodeURIComponent(tab)}`;
      navigate(path, { replace: true });
    }
  }, [navigate]);
  
  const selectAccount = useCallback((account) => {
    if (account) {
      setPortalUser(null);
      setLoadingPortalUser(true);
      setSelectedAccount(account);
    } else {
      setLoadingPortalUser(false);
      setPortalUser(null);
      setSelectedAccount(null);
    }
  }, []);

  // Sync URL params with state when pathname changes (navigation/back) OR when we have URL params but state doesn't match (e.g. remount after back)
  useEffect(() => {
    const pathnameChanged = prevLocationRef.current !== location.pathname;

    if (isUserAction.current) {
      isUserAction.current = false;
      prevLocationRef.current = location.pathname;
      return;
    }

    // Wait for accounts to load before syncing account (e.g. after back from AutomationDetail)
    if (params.accountId && accounts.length === 0) {
      return;
    }

    const urlDepartment = params.department ? decodeURIComponent(params.department) : '';
    const urlTab = params.tab ? decodeURIComponent(params.tab) : '';
    const stateMatchesUrl = selectedAccount?.id === params.accountId && selectedDepartment === urlDepartment && selectedView === urlTab;

    // Sync from URL when: pathname changed (navigated/back) OR we have full URL but state doesn't match (e.g. remounted with URL, still showing "pick an account")
    if (!pathnameChanged && stateMatchesUrl) {
      prevLocationRef.current = location.pathname;
      return;
    }

    prevLocationRef.current = location.pathname;
    syncingFromUrlRef.current = true;

    // Sync account from URL
    if (params.accountId && accounts.length > 0) {
      const account = accounts.find(acc => acc.id === params.accountId);
      if (account && account.id !== selectedAccount?.id) {
        selectAccount(account);
      }
    }

    if (params.department && urlDepartment !== selectedDepartment) {
      setSelectedDepartment(urlDepartment);
    }

    if (params.tab && urlTab !== selectedView) {
      setSelectedView(urlTab);
    }
  }, [location.pathname, params.accountId, params.department, params.tab, accounts, selectedAccount?.id, selectedDepartment, selectedView]);

  // Update URL when state changes (user selections) - skip when we just synced from URL to avoid glitching
  useEffect(() => {
    if (syncingFromUrlRef.current) {
      syncingFromUrlRef.current = false;
      return;
    }
    // Only update URL if we have all required state AND it differs from URL params
    if (selectedAccount?.id && selectedDepartment && selectedView) {
      const urlAccountId = params.accountId || '';
      const urlDepartment = params.department ? decodeURIComponent(params.department) : '';
      const urlTab = params.tab ? decodeURIComponent(params.tab) : '';
      
      // Only update if state differs from URL params
      if (urlAccountId !== selectedAccount.id || 
          urlDepartment !== selectedDepartment || 
          urlTab !== selectedView) {
        updateURL(selectedAccount.id, selectedDepartment, selectedView, params.content || null);
      }
    }
  }, [selectedAccount?.id, selectedDepartment, selectedView, updateURL, params.accountId, params.department, params.tab, params.content]);
  const [searchTerm, setSearchTerm] = useState('');
  const [accountTeamNames, setAccountTeamNames] = useState({});
  // Full portal-user roster per account (id → [{uid, name, profileImage, isPrimary}]) for the selection cards.
  const [accountTeamMembers, setAccountTeamMembers] = useState({});
  const [headerTeamMembers, setHeaderTeamMembers] = useState([]);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] = useState(false);
  const [isInternal, setIsInternal] = useState(false);
  const [pinnedAccountIds, setPinnedAccountIds] = useState([]);
  const [taskFilter, setTaskFilter] = useState('todo'); // 'todo', 'done'
  const [tasksPerPage] = useState(9);
  const [currentPage, setCurrentPage] = useState(1);
  // Internal users can check tasks off and create new ones on the client's behalf.
  const [togglingTaskIds, setTogglingTaskIds] = useState([]);
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [newTaskForm, setNewTaskForm] = useState({ title: '', description: '', dueDate: '' });

  // Enhanced projects functionality for forecast view

  // Reset pagination when filter changes
  const handleTaskFilterChange = (filter) => {
    setTaskFilter(filter);
    setCurrentPage(1);
  };
  
  // Portal user data states
  const [portalUser, setPortalUser] = useState(null);
  const [loadingPortalUser, setLoadingPortalUser] = useState(false);
  const [courses, setCourses] = useState([]);
  const [courseProgress, setCourseProgress] = useState([]);
  const [tasks, setTasks] = useState([]);
  // General department — client Questions, Fires, and Collection (docs + calls).
  const [generalQuestions, setGeneralQuestions] = useState([]);
  const [generalFires, setGeneralFires] = useState([]);
  const [collectionDocs, setCollectionDocs] = useState([]);
  const [collectionCalls, setCollectionCalls] = useState([]);
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [generalExtrasLoading, setGeneralExtrasLoading] = useState(false);
  // Search + status filter for the Questions and Fires views.
  const [questionSearch, setQuestionSearch] = useState('');
  const [questionStatusFilter, setQuestionStatusFilter] = useState('all');
  const [fireSearch, setFireSearch] = useState('');
  const [fireStatusFilter, setFireStatusFilter] = useState('all');
  // Collection: in-page document viewer + call meeting-summary modal.
  const [collectionViewerDoc, setCollectionViewerDoc] = useState(null);
  const [callSummary, setCallSummary] = useState(null);
  const [operationsGoals, setOperationsGoals] = useState([]);
  const [operationsGoalsLoading, setOperationsGoalsLoading] = useState(false);
  const [isOperationsGoalsModalOpen, setIsOperationsGoalsModalOpen] = useState(false);
  const [financeGoals, setFinanceGoals] = useState([]);
  const [financeGoalsLoading, setFinanceGoalsLoading] = useState(false);
  const [isFinanceGoalsModalOpen, setIsFinanceGoalsModalOpen] = useState(false);
  const [newFinanceGoal, setNewFinanceGoal] = useState({
    metricName: '',
    description: '',
    target: 0,
    actual: 0,
    unit: 'usd',
    department: 'Finance'
  });
  const [isAddFinanceGoalModalOpen, setIsAddFinanceGoalModalOpen] = useState(false);
  const [editingFinanceGoal, setEditingFinanceGoal] = useState(null);
  const [isEditFinanceGoalModalOpen, setIsEditFinanceGoalModalOpen] = useState(false);
  const [marketingGoals, setMarketingGoals] = useState([]);
  const [marketingGoalsLoading, setMarketingGoalsLoading] = useState(false);
  const [isMarketingGoalsModalOpen, setIsMarketingGoalsModalOpen] = useState(false);
  const [newMarketingGoal, setNewMarketingGoal] = useState({
    metricName: '',
    description: '',
    target: 0,
    actual: 0,
    unit: 'number',
    department: 'Marketing'
  });
  const [isAddMarketingGoalModalOpen, setIsAddMarketingGoalModalOpen] = useState(false);
  const [editingMarketingGoal, setEditingMarketingGoal] = useState(null);
  const [isEditMarketingGoalModalOpen, setIsEditMarketingGoalModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [newTarget, setNewTarget] = useState('');
  const [newCurrent, setNewCurrent] = useState('');
  const [isUpdatingCurrent, setIsUpdatingCurrent] = useState(false);
  const [isAddOperationsGoalModalOpen, setIsAddOperationsGoalModalOpen] = useState(false);
  const [isEditOperationsGoalModalOpen, setIsEditOperationsGoalModalOpen] = useState(false);
  const [editingOperationsGoal, setEditingOperationsGoal] = useState(null);
  const [newOperationsGoal, setNewOperationsGoal] = useState({
    metricName: '',
    description: '',
    target: 0,
    actual: 0,
    unit: 'usd',
    department: 'Operations'
  });
  
  // Partner programs data states
  
  // Close ratio data states
  
  // Marketing leads data states
  const [leadsEntries, setLeadsEntries] = useState([]);
  const [leadsTimeFilter, setLeadsTimeFilter] = useState('currentYear');
  const [showLeadsAddPopup, setShowLeadsAddPopup] = useState(false);
  const [showLeadsEditPopup, setShowLeadsEditPopup] = useState(false);
  const [leadsFormData, setLeadsFormData] = useState({
    leadsCount: '',
    month: new Date().toLocaleString('default', { month: 'long' }),
    year: new Date().getFullYear().toString()
  });

  // Leads Calculator data states
  const [leadsCalculatorData, setLeadsCalculatorData] = useState({
    revenue: '',
    averageJobSize: '',
    closeRatio: '',
    qualifiedLeadPercent: '',
    twoYearsAgo: '',
    oneYearAgo: '',
    currentYearProjection: '',
    leads: []
  });
  const [leadsCalculatorLoading, setLeadsCalculatorLoading] = useState(false);
  const [editingLeadsEntry, setEditingLeadsEntry] = useState(null);
  
  // Leads ROI data states
  const [leadsROIData, setLeadsROIData] = useState({
    analysisData: {},
    summary: {
      totalLeads: 0,
      totalSpend: 0,
      totalRevenue: 0,
      averageROI: 0,
      costPerLead: 0,
      revenuePerLead: 0
    }
  });
  const [leadsROILoading, setLeadsROILoading] = useState(false);
  
  // Website data states
  const [websiteData, setWebsiteData] = useState({
    lastScanned: '',
    seoScore: 0,
    pageHealth: 0,
    contentScore: 0,
    issues: [],
    pages: [],
    audits: []
  });
  
  // Website audit data states - no hardcoded sample data
  
  // Social media data states
  const [socialData, setSocialData] = useState({
    facebook: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
    instagram: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
    tiktok: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
    youtube: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 }
  });
  const [platformEntries, setPlatformEntries] = useState({
    facebook: [],
    instagram: [],
    tiktok: [],
    youtube: []
  });
  const [socialTimeFilter, setSocialTimeFilter] = useState('currentYear');
  const [showSocialEditPopup, setShowSocialEditPopup] = useState(false);
  const [showSocialAddPopup, setShowSocialAddPopup] = useState(false);
  const [editingSocialPlatform, setEditingSocialPlatform] = useState(null);
  const [editingSocialEntry, setEditingSocialEntry] = useState(null);
  const [socialFormData, setSocialFormData] = useState({
    followers: '',
    views: '',
    likes: '',
    comments: '',
    shares: '',
    month: '',
    year: new Date().getFullYear().toString()
  });
  
  // Paid media campaign data states
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showAddCampaign, setShowAddCampaign] = useState(false);
  
  // Newsletter campaign data states
  const [newsletterCampaigns, setNewsletterCampaigns] = useState([]);
  const [showNewsletterAddPopup, setShowNewsletterAddPopup] = useState(false);
  const [selectedNewsletterCampaign, setSelectedNewsletterCampaign] = useState(null);

  // Reviews data states
  const [reviewsData, setReviewsData] = useState({
    categoryIds: [],
    additionalIds: [],
    totalPhotos: 0,
    totalReviews: 0,
    newThisMonth: 0,
    averageRating: 0,
    ratingDistribution: [],
    fiveStarReviews: 0,
    competitors: [],
    businessMetrics: {
      totalTopics: 0,
      topicReviews: 0,
      address: false,
      phone: false,
      website: false,
      logo: false,
      hours: false,
      claimed: false,
      attributes: 0
    }
  });
  
  // Time range filter for charts
  const [timeRange, setTimeRange] = useState('currentYear');
  
  // Awards data states
  
  // Org chart data states

  // Hiring Outline state (for future members)

  // Org chart Analyze tab state

  // 1099 contractor states
  
  // Predefined 1099 contractor options

  // Member modal: Details/Roles tabs and roles state

  // Zoom and pan states for org chart
  
  // Job Listings data states
  const [jobListingsData, setJobListingsData] = useState({
    jobContent: {
      companyIntroduction: '',
      whyJoinUs: '',
      pleaseNote: 'This position is open to U.S. citizens, lawful permanent residents (Green Card holders), or individuals with valid Employment Authorization Documents (EADs). We are unable to sponsor employment visas at this time.',
      equalOpportunity: false,
      benefits: []
    },
    jobListings: []
  });
  const [jobListingsLoading, setJobListingsLoading] = useState(true);
  
  // Debt data states
  
  // Overhead data states
  const [overheadData, setOverheadData] = useState({
    expenses: {
      'Operational Overhead': [],
      'Operation Payroll': [],
      'Asset Payments': [],
      'Subscriptions': [],
      'Insurance': [],
      'Debt Service': [],
      'Soft Debt': []
    },
    timePeriod: 'Annual',
    summaryViewMode: 'box'
  });
  const [overheadSummary, setOverheadSummary] = useState({
    fixedOverhead: 0,
    softOverhead: 0,
    totalOverhead: 0
  });
  const [overheadTimePeriod, setOverheadTimePeriod] = useState('Annual');
  
  // Overhead editing states
  const [overheadEditing, setOverheadEditing] = useState(false);
  const [overheadSaving, setOverheadSaving] = useState(false);
  const [overheadMessage, setOverheadMessage] = useState('');
  
  // Forecast data states
  
  // GPM/Margin Calculator data states
  
  
  
  
  
  // Labor Burden data states
  const [laborBurdenEmployees, setLaborBurdenEmployees] = useState([]);
  const [laborBurdenSummaryStats, setLaborBurdenSummaryStats] = useState({
    hourly: 0,
    daily: 0,
    weekly: 0,
    monthly: 0,
    annual: 0
  });
  const [laborBurdenWorkDays, setLaborBurdenWorkDays] = useState(251);
  const [laborBurdenPto, setLaborBurdenPto] = useState(10);
  const [laborBurdenHolidays, setLaborBurdenHolidays] = useState(7);
  const [laborBurdenHourlyRate, setLaborBurdenHourlyRate] = useState(30);
  const [laborBurdenHourlyExtraCosts, setLaborBurdenHourlyExtraCosts] = useState(15);
  const [laborBurdenUnbillablePercentage, setLaborBurdenUnbillablePercentage] = useState(10);
  const [laborBurdenSalaryType, setLaborBurdenSalaryType] = useState('annual');
  const [laborBurdenSalaryRate, setLaborBurdenSalaryRate] = useState(62400);
  const [laborBurdenSalaryExtraCosts, setLaborBurdenSalaryExtraCosts] = useState(15);

  // Calculated values for hourly employee
  const [laborBurdenTotalHourlyBurden, setLaborBurdenTotalHourlyBurden] = useState(0);
  const [laborBurdenHourlyDayRate, setLaborBurdenHourlyDayRate] = useState(0);
  const [laborBurdenHourlyWeekRate, setLaborBurdenHourlyWeekRate] = useState(0);
  const [laborBurdenHourlyMonthRate, setLaborBurdenHourlyMonthRate] = useState(0);
  const [laborBurdenHourlyAnnualRate, setLaborBurdenHourlyAnnualRate] = useState(0);

  // Calculated values for salaried employee
  const [laborBurdenTotalSalaryBurden, setLaborBurdenTotalSalaryBurden] = useState({ weekly: 0, monthly: 0, annual: 0 });
  const [laborBurdenSalaryHourRate, setLaborBurdenSalaryHourRate] = useState(0);
  const [laborBurdenSalaryDayRate, setLaborBurdenSalaryDayRate] = useState(0);
  const [laborBurdenSalaryWeekRate, setLaborBurdenSalaryWeekRate] = useState(0);

  const [showLaborBurdenAddModal, setShowLaborBurdenAddModal] = useState(false);
  const [showLaborBurdenEditModal, setShowLaborBurdenEditModal] = useState(false);
  const [editingLaborBurdenEmployee, setEditingLaborBurdenEmployee] = useState(null);
  const [newLaborBurdenEmployee, setNewLaborBurdenEmployee] = useState({
    type: 'hourly',
    name: '',
    jobTitle: '',
    rate: 0
  });
  const [laborBurdenSearchQuery, setLaborBurdenSearchQuery] = useState('');
  const [laborBurdenActiveTab, setLaborBurdenActiveTab] = useState('myCompany');
  
  // Margin Calculator data states
  const [marginCalculatorInputs, setMarginCalculatorInputs] = useState({
    taxRate: 0.20,
    profitabilityDesire: 0.11,
    startingGPM: 0.30,
    overhead: 260000,
    trueGPM: 0.25,
  });
  const [marginCalculatorActiveTab, setMarginCalculatorActiveTab] = useState('annual');
  const [marginCalculatorScenarios, setMarginCalculatorScenarios] = useState([]);
  const [marginCalculatorLoading, setMarginCalculatorLoading] = useState(true);

  // Check if user is internal and load pinned accounts
  useEffect(() => {
    const checkUserTypeAndLoadPinned = async () => {
      if (!currentUser) return;
      
      try {
        const internalStatus = await isInternalUser(currentUser.uid);
        setIsInternal(internalStatus);
        
        if (internalStatus) {
          // Load pinned accounts from Firestore
          const userDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const pinned = userData.pinnedAccountIds || [];
            setPinnedAccountIds(pinned);
          }
        }
      } catch (error) {
        console.error('Error checking user type or loading pinned accounts:', error);
      }
    };

    checkUserTypeAndLoadPinned();
  }, [currentUser]);

  // Load accounts on component mount
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const loadedAccounts = await getAccounts();
        const eligibleAccounts = loadedAccounts.filter(isKickoffEligibleClient);

        const accountsWithPortalStatus = await Promise.all(
          eligibleAccounts.map(async (account) => {
            const hasPortalUser = await checkPortalUserExists(account);
            return {
              ...account,
              hasPortalUser
            };
          })
        );

        const activePortalAccounts = accountsWithPortalStatus.filter((account) => account.hasPortalUser);

        setAccounts(activePortalAccounts);

        try {
          const membersMap = await fetchPortalUsersLinkedToAccounts(activePortalAccounts);
          const membersObj = Object.fromEntries([...membersMap.entries()]);
          setAccountTeamMembers(membersObj);
          // Non-primary member names power the search filter.
          setAccountTeamNames(
            Object.fromEntries(
              Object.entries(membersObj).map(([id, members]) => [
                id,
                members.filter((m) => !m.isPrimary).map((m) => m.name),
              ])
            )
          );
        } catch (teamErr) {
          console.warn('Could not load team members for portal account list:', teamErr);
          setAccountTeamMembers({});
          setAccountTeamNames({});
        }

        setLoading(false);
      } catch (error) {
        console.error('Error loading accounts:', error);
        setError('Failed to load accounts');
        setLoading(false);
      }
    };

    loadAccounts();
    
    // Load internal users for task creator names
    const loadInternalUsers = async () => {
      try {
        const users = await getInternalUsers();
        setInternalUsers(users);
      } catch (error) {
        console.error('Error loading internal users:', error);
      }
    };
    loadInternalUsers();
  }, []);

  // Calculate portalUserId when selectedAccount changes
  useEffect(() => {
    const calculatePortalUserId = async () => {
      if (!selectedAccount) {
        setPortalUserId(null);
        return;
      }

      try {
        // Check if current user is a portal user (client role) for this account
        const currentUserQuery = query(
          collection(db, 'users'),
          where('email', '==', currentUser?.email)
        );
        const currentUserSnapshot = await getDocs(currentUserQuery);
        
        if (!currentUserSnapshot.empty) {
          const currentUserData = currentUserSnapshot.docs[0].data();
          if (currentUserData.role === 'client' && currentUserData.accountId === selectedAccount.id) {
            // Current user is the portal user for this account - use their UID
            setPortalUserId(currentUser.uid);
            return;
          }
        }
        
        // Current user is not the portal user - find the portal user by email
        const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
        
        if (!usersSnapshot.empty) {
          const portalUserDoc = usersSnapshot.docs[0];
          setPortalUserId(portalUserDoc.id);
        } else {
          setPortalUserId(null);
        }
      } catch (error) {
        console.error('Error calculating portalUserId:', error);
        setPortalUserId(null);
      }
    };

    calculatePortalUserId();
  }, [selectedAccount, currentUser]);

  // Load the selected account's team roster (photos + names) for the header,
  // mirroring the Client Overview team members section.
  useEffect(() => {
    let cancelled = false;
    if (!selectedAccount?.id) {
      setHeaderTeamMembers([]);
      return undefined;
    }
    (async () => {
      try {
        const members = await fetchClientEventRosterMembers(selectedAccount);
        if (cancelled) return;
        const primaryEmail = String(selectedAccount.contact?.email || selectedAccount.email || '').trim().toLowerCase();
        const additional = (members || []).filter(
          (m) => !m.isPrimary && String(m.email || '').trim().toLowerCase() !== primaryEmail
        );
        setHeaderTeamMembers(additional);
      } catch (err) {
        console.warn('Could not load team members for portal view header:', err);
        if (!cancelled) setHeaderTeamMembers([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAccount?.id]);

  // Reset newsletter modal state when switching away from newsletter tab
  useEffect(() => {
    if (selectedDepartment !== 'Marketing' || selectedView !== 'newsletter') {
      setShowNewsletterAddPopup(false);
      setSelectedNewsletterCampaign(null);
    }
  }, [selectedDepartment, selectedView]); // Force refresh

  // Check if portal user exists for an account
  const checkPortalUserExists = async (account) => {
    try {
      const usersSnapshot = await getAccountPortalUserSnapshot(account);
      return !usersSnapshot.empty;
    } catch (error) {
      console.error('Error checking portal user:', error);
      return false;
    }
  };

  // Load portal user data when account is selected
  // Load courses data for selected account
  const loadCoursesData = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      console.log('Loading courses for account:', selectedAccount.contact.email);
      const allCourses = await getAllCourses();
      console.log('All courses loaded:', allCourses.length, 'courses');
      
      // For now, show all courses regardless of program/stage
      // This ensures we can see all available courses and their progress
      setCourses(allCourses);
      console.log('Courses set:', allCourses.length);

      // Load course progress for this account's portal user
      // Use the same user lookup logic as other data loading
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (!usersSnapshot.empty) {
        const userDoc = usersSnapshot.docs[0];
        console.log('Loading course progress for user:', userDoc.id);
        const userProgress = await getAllUserCourseProgress(userDoc.id);
        console.log('Course progress loaded (raw):', userProgress);
        
        // Convert progress map to array, keeping courseId information
        const progressArray = Object.keys(userProgress).map(courseId => ({
          ...userProgress[courseId],
          courseId: courseId
        }));
        
        console.log('Course progress loaded (array):', progressArray.length, 'progress records');
        console.log('Progress data:', progressArray);
        setCourseProgress(progressArray);
      } else {
        console.log('No portal user found in users collection, setting empty progress');
        setCourseProgress([]);
      }
    } catch (error) {
      console.error('Error loading courses data:', error);
      setCourses([]);
      setCourseProgress([]);
    }
  };

  // Load the client's General-department extras: Questions + Fires (created by
  // this account's users) and their Collection (client-visible docs + calls).
  const loadGeneralExtras = async (accountId) => {
    if (!accountId) return;
    setGeneralExtrasLoading(true);
    try {
      // The roadmap lives at accounts/{companyId}.roadmapTemplates, where companyId
      // is the client user's stored accountId/clientId — which can differ from the
      // CRM accounts doc id. Resolve it the same way the portal does (via the
      // account's primary user), falling back to the accounts doc id.
      let roadmapCompanyId = accountId;
      try {
        const primaryUid = await getPrimaryUserIdForAccount(accountId);
        const resolved = primaryUid ? await getCompanyIdForUser(primaryUid) : null;
        if (resolved) roadmapCompanyId = resolved;
      } catch (e) {
        // fall back to accountId
      }

      const [questions, fires, account, calls, roadmap] = await Promise.all([
        getQuestionsByActualAccountId(accountId).catch(() => []),
        getFiresByActualAccountId(accountId).catch(() => []),
        getAccountById(accountId).catch(() => null),
        fetchAccountCalls(accountId).catch(() => []),
        clientRoadmapService.getClientRoadmap(roadmapCompanyId).catch(() => null),
      ]);
      setGeneralQuestions(questions || []);
      setGeneralFires(fires || []);
      const docs = (account?.customDocuments || []).filter(
        (d) => d.visibility === 'client' || !d.visibility
      );
      setCollectionDocs(docs);
      setCollectionCalls(calls || []);
      setRoadmapItems(transformClientRoadmapTemplates(roadmap) || []);
    } catch (error) {
      console.error('Error loading General extras:', error);
      setGeneralQuestions([]);
      setGeneralFires([]);
      setCollectionDocs([]);
      setCollectionCalls([]);
      setRoadmapItems([]);
    } finally {
      setGeneralExtrasLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAccount) {
      loadPortalUserData(selectedAccount.id);
      loadGeneralExtras(selectedAccount.id);
      loadLeadsCalculatorData(selectedAccount.id);
      loadLeadsROIData(selectedAccount.id);
      loadWebsiteDataFromFirebase(selectedAccount.id);
      loadOperationsGoalsData(selectedAccount.id);
      loadFinanceGoalsData(selectedAccount.id);
      loadMarketingGoalsData(selectedAccount.id);
      loadSocialMediaDataFromFirebase(selectedAccount.id);
      loadPaidMediaCampaignsFromFirebase(selectedAccount.id);
      loadNewsletterCampaignsFromFirebase(selectedAccount.id);
      loadReviewsDataFromFirebase(selectedAccount.id);
      loadJobListingsFromFirebase(selectedAccount.id);
      loadLeadsEntriesFromFirebase(selectedAccount.id);
      loadLaborBurdenDataFromFirebase(selectedAccount.id);
      loadMarginCalculatorDataFromFirebase(selectedAccount.id);
      loadOverheadDataFromFirebase(selectedAccount.id);

      // Load courses data
      loadCoursesData(selectedAccount.id);
    }
  }, [selectedAccount?.id]);

  // Load courses when courses tab is selected
  useEffect(() => {
    if (selectedView === 'courses' && selectedAccount && courses.length === 0) {
      console.log('Courses tab selected, loading courses...');
      loadCoursesData(selectedAccount.id);
    }
  }, [selectedView, selectedAccount, courses.length]);

  // Refresh leads calculator data when leads calculator tab is selected
  useEffect(() => {
    if (selectedView === 'leads-calculator' && selectedAccount) {
      console.log('Leads calculator tab selected, refreshing data...');
      loadLeadsCalculatorData(selectedAccount.id);
    }
  }, [selectedView, selectedAccount]);

  // Calculate overhead summary when time period or expenses change (but not during editing)
  useEffect(() => {
    if (overheadEditing) return; // Don't recalculate during editing to prevent input focus loss
    
    let fixedTotal = 0;
    let softTotal = 0;
    
    // Calculate fixed and soft overhead
    Object.entries(overheadData.expenses || {}).forEach(([category, categoryExpenses]) => {
      categoryExpenses.forEach(expense => {
        const adjustedAmount = calculateAdjustedAmount(expense.amount, expense.frequency);
        
        // Soft overhead: Debt Service and Soft Debt categories
        if (category === 'Soft Debt' || category === 'Debt Service') {
          softTotal += adjustedAmount;
        } else {
          // Fixed overhead: all other categories
          fixedTotal += adjustedAmount;
        }
      });
    });
    
    setOverheadSummary({
      fixedOverhead: fixedTotal,
      softOverhead: softTotal,
      totalOverhead: fixedTotal + softTotal
    });
  }, [overheadData.expenses, overheadTimePeriod, overheadEditing]);

  // Reset selected view when department changes
  useEffect(() => {
    const departmentTabs = getDepartmentTabs(selectedDepartment);
    if (departmentTabs.length > 0) {
      setSelectedView(departmentTabs[0].id);
    }
  }, [selectedDepartment]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isAccountDropdownOpen && !event.target.closest('.account-dropdown')) {
        setIsAccountDropdownOpen(false);
      }
      if (isDepartmentDropdownOpen && !event.target.closest('.department-dropdown')) {
        setIsDepartmentDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAccountDropdownOpen, isDepartmentDropdownOpen]);

  const loadPortalUserData = useCallback(async (accountId) => {
    try {
      setLoading(true);
      setError(null);

      // Check if portal user exists for this account
      const hasPortalUser = await checkPortalUserExists(selectedAccount);
      
      if (!hasPortalUser) {
        // No portal user found - clear portal data
        setPortalUser(null);
        setCourses([]);
        setCourseProgress([]);
        setTasks([]);
        setLoading(false);
        return;
      }

      // Portal user exists - load real data
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      let userDoc = null;
      if (!usersSnapshot.empty) {
        userDoc = usersSnapshot.docs[0];
        const userData = userDoc.data();
        
        setPortalUser({
          id: userDoc.id,
          firstName: userData.firstName || selectedAccount.contact.firstName,
          lastName: userData.lastName || selectedAccount.contact.lastName,
          email: userData.email || selectedAccount.contact.email
        });
      }


      // Load real tasks data for this account (match what the client sees in portal)
      try {
        if (userDoc) {
          console.log('Loading portal tasks for userId:', userDoc.id);
          const portalTasks = await getTasksForPortalUser(userDoc.id, accountId);
          console.log('Loaded portal tasks:', portalTasks);
          setTasks(portalTasks);
        } else {
          setTasks([]);
        }
      } catch (error) {
        console.error('Error loading tasks data:', error);
        setTasks([]);
      }

    } catch (error) {
      console.error('Error loading portal user data:', error);
      setError('Failed to load portal user data');
    } finally {
      setLoading(false);
      setLoadingPortalUser(false);
    }
  }, [currentUser, selectedAccount]);

  // --- Tasks: internal users can check tasks off and create them for the client ---
  const handleToggleTaskCompleted = async (task) => {
    if (!task?.id || togglingTaskIds.includes(task.id)) return;
    const nextCompleted = !task.completed;

    setTogglingTaskIds(prev => [...prev, task.id]);
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, completed: nextCompleted } : t)));

    try {
      await toggleTaskCompletion(task.id, nextCompleted);
    } catch (error) {
      console.error('Error toggling task completion:', error);
      // Roll the optimistic update back so the UI keeps matching Firestore.
      setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, completed: !nextCompleted } : t)));
      setError('Failed to update the task. Please try again.');
    } finally {
      setTogglingTaskIds(prev => prev.filter(id => id !== task.id));
    }
  };

  const openNewTaskModal = () => {
    setNewTaskForm({ title: '', description: '', dueDate: '' });
    setIsNewTaskModalOpen(true);
  };

  const closeNewTaskModal = () => {
    if (creatingTask) return;
    setIsNewTaskModalOpen(false);
  };

  const handleCreateAccountTask = async (e) => {
    e.preventDefault();
    if (!newTaskForm.title.trim() || !selectedAccount?.id || creatingTask) return;

    setCreatingTask(true);
    try {
      // Assign to every portal user on the account so the client sees it in their portal.
      const accountUsersSnapshot = await getDocs(
        query(collection(db, 'users'), where('accountId', '==', selectedAccount.id))
      );
      const assigneeIds = [...new Set([
        ...accountUsersSnapshot.docs.map(d => d.id),
        ...(portalUser?.id ? [portalUser.id] : [])
      ])];

      if (!assigneeIds.length) {
        setError('No portal users are linked to this account, so the task would not be visible to anyone.');
        return;
      }

      // Store the due date as a Date (Firestore Timestamp) like the portal Tasks page does,
      // parsed at local midnight so the day doesn't shift a timezone backwards.
      const dueDate = newTaskForm.dueDate ? new Date(`${newTaskForm.dueDate}T00:00:00`) : null;

      const createdTask = await createTask({
        title: newTaskForm.title.trim(),
        description: newTaskForm.description.trim(),
        dueDate,
        assignees: assigneeIds,
        accountId: selectedAccount.id,
        companyId: selectedAccount.id,
        companyName: selectedAccount.company || null,
        completed: false,
        createdBy: currentUser?.uid || 'Internal'
      });

      setTasks(prev => [{ ...createdTask, completed: false }, ...prev]);
      setTaskFilter('todo');
      setCurrentPage(1);
      setIsNewTaskModalOpen(false);
      setNewTaskForm({ title: '', description: '', dueDate: '' });
      setError(null);
    } catch (error) {
      console.error('Error creating task:', error);
      setError(error.message || 'Failed to create the task. Please try again.');
    } finally {
      setCreatingTask(false);
    }
  };

  // Enhanced project management functions

  // Load leads calculator data from Firebase
  const loadLeadsCalculatorData = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      setLeadsCalculatorLoading(true);
      
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        // No portal user found - clear leads calculator data
        setLeadsCalculatorData({
          revenue: selectedAccount.estimatedRevenue || '',
          averageJobSize: '',
          closeRatio: '',
          qualifiedLeadPercent: '',
          twoYearsAgo: '',
          oneYearAgo: '',
          currentYearProjection: '',
          leads: []
        });
        setLeadsCalculatorLoading(false);
        return;
      }
      
      const portalUserDoc = usersSnapshot.docs[0];
      const userData = portalUserDoc.data();
      
      // Debug logging to understand data structure
      console.log('Portal user data for leads calculator:', {
        salesData: userData.salesData,
        businessAssessment: userData.businessAssessment,
        leadsCalculator: userData.leadsCalculator,
        selectedAccount: selectedAccount,
        allUserData: userData
      });
      
      // Extract relevant data for leads calculator from multiple sources
      // Priority: Most recent data first (leadsCalculator > businessAssessment > salesData > selectedAccount)
      const extractedData = {
        revenue: userData.leadsCalculator?.formData?.revenue ||
                userData.businessAssessment?.revenueData?.currentYearProjection ||
                userData.salesData?.currentYearProjection || 
                selectedAccount.estimatedRevenue || '',
        averageJobSize: userData.leadsCalculator?.formData?.averageJobSize ||
                       userData.businessAssessment?.averageJobSize || 
                       userData.salesData?.averageJobSize || '',
        closeRatio: userData.leadsCalculator?.formData?.closeRatio ||
                   userData.businessAssessment?.closeRatio || 
                   userData.salesData?.closeRatio || '',
        qualifiedLeadPercent: userData.leadsCalculator?.formData?.qualifiedLeadPercent ||
                            userData.businessAssessment?.qualifiedLeadPercent || 
                            userData.salesData?.qualifiedLeadPercent || '',
        twoYearsAgo: userData.salesData?.twoYearsAgo || 
                    userData.businessAssessment?.revenueData?.twoYearsAgo || '',
        oneYearAgo: userData.salesData?.oneYearAgo || 
                   userData.businessAssessment?.revenueData?.oneYearAgo || '',
        currentYearProjection: userData.salesData?.currentYearProjection || 
                              userData.businessAssessment?.revenueData?.currentYearProjection || '',
        leads: userData.salesData?.leads || [],
        // Include saved results if available
        savedResults: userData.leadsCalculator?.results || null
      };
      
      console.log('Extracted leads calculator data:', extractedData);
      console.log('Data source priority check:', {
        'leadsCalculator.formData.revenue': userData.leadsCalculator?.formData?.revenue,
        'businessAssessment.revenueData.currentYearProjection': userData.businessAssessment?.revenueData?.currentYearProjection,
        'salesData.currentYearProjection': userData.salesData?.currentYearProjection,
        'selectedAccount.estimatedRevenue': selectedAccount.estimatedRevenue,
        'final_revenue': extractedData.revenue
      });
      
      // If no data found in Firebase, try to load from localStorage as fallback
      if (!extractedData.averageJobSize && !extractedData.closeRatio && !extractedData.qualifiedLeadPercent) {
        try {
          const savedData = localStorage.getItem(`leadsCalculator_${portalUserDoc.id}`);
          if (savedData) {
            const parsedData = JSON.parse(savedData);
            if (parsedData.formData) {
              extractedData.averageJobSize = parsedData.formData.averageJobSize || '';
              extractedData.closeRatio = parsedData.formData.closeRatio || '';
              extractedData.qualifiedLeadPercent = parsedData.formData.qualifiedLeadPercent || '';
            }
          }
        } catch (error) {
          console.error('Error loading from localStorage:', error);
        }
      }
      
      setLeadsCalculatorData(extractedData);
      
    } catch (error) {
      console.error('Error loading leads calculator data:', error);
    } finally {
      setLeadsCalculatorLoading(false);
    }
  };

  // Load leads ROI data from Firebase for the selected account's portal user
  const loadLeadsROIData = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      setLeadsROILoading(true);
      
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        // No portal user found - clear leads ROI data
        setLeadsROIData({
          analysisData: {},
          summary: {
            totalLeads: 0,
            totalSpend: 0,
            totalRevenue: 0,
            averageROI: 0,
            costPerLead: 0,
            revenuePerLead: 0
          }
        });
        setLeadsROILoading(false);
        return;
      }
      
      const portalUserDoc = usersSnapshot.docs[0];
      const userData = portalUserDoc.data();
      
      // Debug logging to understand data structure
      console.log('Portal user data for leads ROI:', {
        leadsROI: userData.leadsROI,
        selectedAccount: selectedAccount,
        allUserData: userData
      });
      
      // Extract leads ROI data
      const extractedData = {
        analysisData: userData.leadsROI?.analysisData || {},
        summary: userData.leadsROI?.summary || {
          totalLeads: 0,
          totalSpend: 0,
          totalRevenue: 0,
          averageROI: 0,
          costPerLead: 0,
          revenuePerLead: 0
        }
      };
      
      setLeadsROIData(extractedData);
      console.log('Extracted leads ROI data:', extractedData);
      
    } catch (error) {
      console.error('Error loading leads ROI data:', error);
    } finally {
      setLeadsROILoading(false);
    }
  };

  // Load website data from Firebase for the selected account's portal user

  const loadOperationsGoalsData = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      setOperationsGoalsLoading(true);
      
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (usersSnapshot.empty) {
        console.log('No portal user found for email:', selectedAccount.contact.email);
        setOperationsGoals([]);
        return;
      }

      const portalUserDoc = usersSnapshot.docs[0];
      const portalUserId = portalUserDoc.id;
      
      // Load goals for this user
      const userGoals = await getUserGoals(portalUserId);
      setOperationsGoals(userGoals.Operations || []);
    } catch (error) {
      console.error('Error loading operations goals:', error);
      setOperationsGoals([]);
    } finally {
      setOperationsGoalsLoading(false);
    }
  };

  const loadFinanceGoalsData = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      setFinanceGoalsLoading(true);
      
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (usersSnapshot.empty) {
        console.log('No portal user found for email:', selectedAccount.contact.email);
        setFinanceGoals([]);
        return;
      }

      const portalUserDoc = usersSnapshot.docs[0];
      const portalUserId = portalUserDoc.id;
      
      // Load goals for this user
      const userGoals = await getUserGoals(portalUserId);
      setFinanceGoals(userGoals.Finance || []);
    } catch (error) {
      console.error('Error loading finance goals:', error);
      setFinanceGoals([]);
    } finally {
      setFinanceGoalsLoading(false);
    }
  };

  const loadMarketingGoalsData = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      setMarketingGoalsLoading(true);
      
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (usersSnapshot.empty) {
        console.log('No portal user found for email:', selectedAccount.contact.email);
        setMarketingGoals([]);
        return;
      }

      const portalUserDoc = usersSnapshot.docs[0];
      const portalUserId = portalUserDoc.id;
      
      // Load goals for this user
      const userGoals = await getUserGoals(portalUserId);
      setMarketingGoals(userGoals.Marketing || []);
    } catch (error) {
      console.error('Error loading marketing goals:', error);
      setMarketingGoals([]);
    } finally {
      setMarketingGoalsLoading(false);
    }
  };

  // Goals modal functions

  const handleTargetChange = (e) => {
    setNewTarget(e.target.value);
  };

  const handleCurrentChange = (e) => {
    setNewCurrent(e.target.value);
  };

  // Add goal modal functions

  // Edit goal modal functions

  // Operations Goals modal functions
  const openOperationsGoalsModal = (goal) => {
    setSelectedGoal(goal);
    const initialTarget = goal.unit === 'usd' ? `$${goal.target.toLocaleString()}` : goal.unit === 'percent' ? `${goal.target}%` : goal.target;
    const initialCurrent = goal.unit === 'usd' ? `$${goal.actual.toLocaleString()}` : goal.unit === 'percent' ? `${goal.actual}%` : goal.actual;
    setNewTarget(initialTarget);
    setNewCurrent(initialCurrent);
    setIsUpdatingCurrent(false);
    setIsOperationsGoalsModalOpen(true);
  };

  const closeOperationsGoalsModal = () => {
    setIsOperationsGoalsModalOpen(false);
    setSelectedGoal(null);
    setNewTarget('');
    setNewCurrent('');
  };

  const handleOperationsTargetSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal || !selectedAccount?.contact?.email) return;

    // Convert formatted string back to a number
    const targetValue = Number(String(newTarget).replace(/[^0-9.-]+/g,""));

    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        // Update in Firebase
        await updateUserGoal(portalUserId, 'Operations', selectedGoal.id, { target: targetValue });
        
        // Update local state
        setOperationsGoals(prev => prev.map(goal => 
          goal.id === selectedGoal.id 
            ? { ...goal, target: targetValue }
            : goal
        ));
      }
    } catch (error) {
      console.error("Error updating operations goal target: ", error);
    }

    closeOperationsGoalsModal();
  };

  const handleOperationsCurrentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal || !selectedAccount?.contact?.email) return;

    // Convert formatted string back to a number
    const currentValue = Number(String(newCurrent).replace(/[^0-9.-]+/g,""));

    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        // Update in Firebase
        await updateUserGoal(portalUserId, 'Operations', selectedGoal.id, { actual: currentValue });
        
        // Update local state
        setOperationsGoals(prev => prev.map(goal => 
          goal.id === selectedGoal.id 
            ? { ...goal, actual: currentValue }
            : goal
        ));
      }
    } catch (error) {
      console.error("Error updating operations goal current value: ", error);
    }

    closeOperationsGoalsModal();
  };

  // Technology Goals modal functions

  // Finance Goals modal functions
  const openFinanceGoalsModal = (goal) => {
    setSelectedGoal(goal);
    const initialTarget = goal.unit === 'usd' ? `$${goal.target.toLocaleString()}` : goal.unit === 'percent' ? `${goal.target}%` : goal.target;
    const initialCurrent = goal.unit === 'usd' ? `$${goal.actual.toLocaleString()}` : goal.unit === 'percent' ? `${goal.actual}%` : goal.actual;
    setNewTarget(initialTarget);
    setNewCurrent(initialCurrent);
    setIsUpdatingCurrent(false);
    setIsFinanceGoalsModalOpen(true);
  };

  const closeFinanceGoalsModal = () => {
    setIsFinanceGoalsModalOpen(false);
    setSelectedGoal(null);
    setNewTarget('');
    setNewCurrent('');
  };

  const handleFinanceTargetSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal || !selectedAccount?.contact?.email) return;

    // Convert formatted string back to a number
    const targetValue = Number(String(newTarget).replace(/[^0-9.-]+/g,""));

    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        // Update in Firebase
        await updateUserGoal(portalUserId, 'Finance', selectedGoal.id, { target: targetValue });
        
        // Update local state
        setFinanceGoals(prev => prev.map(goal => 
          goal.id === selectedGoal.id 
            ? { ...goal, target: targetValue }
            : goal
        ));
      }
    } catch (error) {
      console.error("Error updating finance goal target: ", error);
    }

    closeFinanceGoalsModal();
  };

  const handleFinanceCurrentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal || !selectedAccount?.contact?.email) return;

    // Convert formatted string back to a number
    const currentValue = Number(String(newCurrent).replace(/[^0-9.-]+/g,""));

    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        // Update in Firebase
        await updateUserGoal(portalUserId, 'Finance', selectedGoal.id, { actual: currentValue });
        
        // Update local state
        setFinanceGoals(prev => prev.map(goal => 
          goal.id === selectedGoal.id 
            ? { ...goal, actual: currentValue }
            : goal
        ));
      }
    } catch (error) {
      console.error("Error updating finance goal current value: ", error);
    }

    closeFinanceGoalsModal();
  };

  const openAddFinanceGoalModal = () => {
    setNewFinanceGoal({
      metricName: '',
      description: '',
      target: 0,
      actual: 0,
      unit: 'usd',
      department: 'Finance'
    });
    setIsAddFinanceGoalModalOpen(true);
  };

  const closeAddFinanceGoalModal = () => {
    setIsAddFinanceGoalModalOpen(false);
    setNewFinanceGoal({
      metricName: '',
      description: '',
      target: 0,
      actual: 0,
      unit: 'usd',
      department: 'Finance'
    });
  };

  const handleNewFinanceGoalChange = (field, value) => {
    setNewFinanceGoal(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddFinanceGoal = async (e) => {
    e.preventDefault();
    if (!selectedAccount?.contact?.email) return;

    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        const goalToAdd = {
          id: Date.now().toString(),
          metricName: newFinanceGoal.metricName,
          description: newFinanceGoal.description,
          target: parseFloat(newFinanceGoal.target) || 0,
          actual: parseFloat(newFinanceGoal.actual) || 0,
          unit: newFinanceGoal.unit
        };
        
        // Add to Firebase
        await addUserGoal(portalUserId, newFinanceGoal.department, goalToAdd);
        
        // Update local state
        setFinanceGoals(prev => [...prev, goalToAdd]);
        
        closeAddFinanceGoalModal();
      }
    } catch (error) {
      console.error("Error adding goal: ", error);
    }
  };

  const handleEditFinanceGoal = (goal) => {
    setEditingFinanceGoal({ ...goal });
    setIsEditFinanceGoalModalOpen(true);
    closeFinanceGoalsModal();
  };

  const closeEditFinanceGoalModal = () => {
    setIsEditFinanceGoalModalOpen(false);
    setEditingFinanceGoal(null);
  };

  const handleEditFinanceGoalChange = (field, value) => {
    setEditingFinanceGoal(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveFinanceGoalEdit = async (e) => {
    e.preventDefault();
    if (!selectedAccount?.contact?.email || !editingFinanceGoal) return;

    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        // Update in Firebase
        await updateUserGoal(portalUserId, 'Finance', editingFinanceGoal.id, {
          metricName: editingFinanceGoal.metricName,
          description: editingFinanceGoal.description,
          target: parseFloat(editingFinanceGoal.target) || 0,
          actual: parseFloat(editingFinanceGoal.actual) || 0,
          unit: editingFinanceGoal.unit
        });
        
        // Update local state
        setFinanceGoals(prev => prev.map(goal => 
          goal.id === editingFinanceGoal.id 
            ? { ...editingFinanceGoal, target: parseFloat(editingFinanceGoal.target) || 0, actual: parseFloat(editingFinanceGoal.actual) || 0 }
            : goal
        ));
        
        closeEditFinanceGoalModal();
      }
    } catch (error) {
      console.error("Error updating goal: ", error);
    }
  };

  const handleDeleteFinanceGoal = async (goalId) => {
    if (window.confirm('Are you sure you want to delete this goal? This action cannot be undone.')) {
      try {
        // Find the portal user for this account
        const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
        
        if (!usersSnapshot.empty) {
          const portalUserDoc = usersSnapshot.docs[0];
          const portalUserId = portalUserDoc.id;
          
          // Delete from Firebase
          await deleteUserGoal(portalUserId, 'Finance', goalId);
          
          // Update local state
          setFinanceGoals(prev => prev.filter(goal => goal.id !== goalId));
          
          closeEditFinanceGoalModal();
        }
      } catch (error) {
        console.error("Error deleting goal: ", error);
      }
    }
  };

  // Marketing Goals modal functions
  const openMarketingGoalsModal = (goal) => {
    setSelectedGoal(goal);
    const initialTarget = goal.unit === 'usd' ? `$${goal.target.toLocaleString()}` : goal.unit === 'percent' ? `${goal.target}%` : goal.target;
    const initialCurrent = goal.unit === 'usd' ? `$${goal.actual.toLocaleString()}` : goal.unit === 'percent' ? `${goal.actual}%` : goal.actual;
    setNewTarget(initialTarget);
    setNewCurrent(initialCurrent);
    setIsUpdatingCurrent(false);
    setIsMarketingGoalsModalOpen(true);
  };

  const closeMarketingGoalsModal = () => {
    setIsMarketingGoalsModalOpen(false);
    setSelectedGoal(null);
    setNewTarget('');
    setNewCurrent('');
  };

  const handleMarketingTargetSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal || !selectedAccount?.contact?.email) return;

    // Convert formatted string back to a number
    const targetValue = Number(String(newTarget).replace(/[^0-9.-]+/g,""));

    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        // Update in Firebase
        await updateUserGoal(portalUserId, 'Marketing', selectedGoal.id, { target: targetValue });
        
        // Update local state
        setMarketingGoals(prev => prev.map(goal => 
          goal.id === selectedGoal.id 
            ? { ...goal, target: targetValue }
            : goal
        ));
      }
    } catch (error) {
      console.error("Error updating marketing goal target: ", error);
    }

    closeMarketingGoalsModal();
  };

  const handleMarketingCurrentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal || !selectedAccount?.contact?.email) return;

    // Convert formatted string back to a number
    const currentValue = Number(String(newCurrent).replace(/[^0-9.-]+/g,""));

    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        // Update in Firebase
        await updateUserGoal(portalUserId, 'Marketing', selectedGoal.id, { actual: currentValue });
        
        // Update local state
        setMarketingGoals(prev => prev.map(goal => 
          goal.id === selectedGoal.id 
            ? { ...goal, actual: currentValue }
            : goal
        ));
      }
    } catch (error) {
      console.error("Error updating marketing goal current value: ", error);
    }

    closeMarketingGoalsModal();
  };

  const openAddMarketingGoalModal = () => {
    setNewMarketingGoal({
      metricName: '',
      description: '',
      target: 0,
      actual: 0,
      unit: 'number',
      department: 'Marketing'
    });
    setIsAddMarketingGoalModalOpen(true);
  };

  const closeAddMarketingGoalModal = () => {
    setIsAddMarketingGoalModalOpen(false);
    setNewMarketingGoal({
      metricName: '',
      description: '',
      target: 0,
      actual: 0,
      unit: 'number',
      department: 'Marketing'
    });
  };

  const handleNewMarketingGoalChange = (field, value) => {
    setNewMarketingGoal(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddMarketingGoal = async (e) => {
    e.preventDefault();
    if (!selectedAccount?.contact?.email) return;

    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        const goalToAdd = {
          id: Date.now().toString(),
          metricName: newMarketingGoal.metricName,
          description: newMarketingGoal.description,
          target: parseFloat(newMarketingGoal.target) || 0,
          actual: parseFloat(newMarketingGoal.actual) || 0,
          unit: newMarketingGoal.unit
        };
        
        // Add to Firebase
        await addUserGoal(portalUserId, newMarketingGoal.department, goalToAdd);
        
        // Update local state
        setMarketingGoals(prev => [...prev, goalToAdd]);
        
        closeAddMarketingGoalModal();
      }
    } catch (error) {
      console.error("Error adding goal: ", error);
    }
  };

  const handleEditMarketingGoal = (goal) => {
    setEditingMarketingGoal({ ...goal });
    setIsEditMarketingGoalModalOpen(true);
    closeMarketingGoalsModal();
  };

  const closeEditMarketingGoalModal = () => {
    setIsEditMarketingGoalModalOpen(false);
    setEditingMarketingGoal(null);
  };

  const handleEditMarketingGoalChange = (field, value) => {
    setEditingMarketingGoal(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveMarketingGoalEdit = async (e) => {
    e.preventDefault();
    if (!selectedAccount?.contact?.email || !editingMarketingGoal) return;

    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        // Update in Firebase
        await updateUserGoal(portalUserId, 'Marketing', editingMarketingGoal.id, {
          metricName: editingMarketingGoal.metricName,
          description: editingMarketingGoal.description,
          target: parseFloat(editingMarketingGoal.target) || 0,
          actual: parseFloat(editingMarketingGoal.actual) || 0,
          unit: editingMarketingGoal.unit
        });
        
        // Update local state
        setMarketingGoals(prev => prev.map(goal => 
          goal.id === editingMarketingGoal.id 
            ? { ...editingMarketingGoal, target: parseFloat(editingMarketingGoal.target) || 0, actual: parseFloat(editingMarketingGoal.actual) || 0 }
            : goal
        ));
        
        closeEditMarketingGoalModal();
      }
    } catch (error) {
      console.error("Error updating goal: ", error);
    }
  };

  const handleDeleteMarketingGoal = async (goalId) => {
    if (window.confirm('Are you sure you want to delete this goal? This action cannot be undone.')) {
      try {
        // Find the portal user for this account
        const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
        
        if (!usersSnapshot.empty) {
          const portalUserDoc = usersSnapshot.docs[0];
          const portalUserId = portalUserDoc.id;
          
          // Delete from Firebase
          await deleteUserGoal(portalUserId, 'Marketing', goalId);
          
          // Update local state
          setMarketingGoals(prev => prev.filter(goal => goal.id !== goalId));
          
          closeEditMarketingGoalModal();
        }
      } catch (error) {
        console.error("Error deleting goal: ", error);
      }
    }
  };

  const openAddOperationsGoalModal = () => {
    setNewOperationsGoal({
      metricName: '',
      description: '',
      target: 0,
      actual: 0,
      unit: 'usd',
      department: 'Operations'
    });
    setIsAddOperationsGoalModalOpen(true);
  };

  const closeAddOperationsGoalModal = () => {
    setIsAddOperationsGoalModalOpen(false);
    setNewOperationsGoal({
      metricName: '',
      description: '',
      target: 0,
      actual: 0,
      unit: 'usd',
      department: 'Operations'
    });
  };

  const handleNewOperationsGoalChange = (field, value) => {
    setNewOperationsGoal(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddOperationsGoal = async (e) => {
    e.preventDefault();
    if (!newOperationsGoal.metricName.trim() || !newOperationsGoal.description.trim() || !selectedAccount?.contact?.email) return;

    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        const goalToAdd = {
          id: Date.now(), // Simple ID generation
          metricName: newOperationsGoal.metricName,
          description: newOperationsGoal.description,
          target: parseFloat(newOperationsGoal.target) || 0,
          actual: parseFloat(newOperationsGoal.actual) || 0,
          unit: newOperationsGoal.unit,
          department: newOperationsGoal.department
        };
        
        // Add to Firebase
        await addUserGoal(portalUserId, newOperationsGoal.department, goalToAdd);
        
        // Update local state
        setOperationsGoals(prev => [...prev, goalToAdd]);
        
        closeAddOperationsGoalModal();
      }
    } catch (error) {
      console.error("Error adding goal: ", error);
    }
  };

  const handleEditOperationsGoal = (goal) => {
    setEditingOperationsGoal({ ...goal });
    setIsEditOperationsGoalModalOpen(true);
    closeOperationsGoalsModal();
  };

  const closeEditOperationsGoalModal = () => {
    setIsEditOperationsGoalModalOpen(false);
    setEditingOperationsGoal(null);
  };

  const handleEditOperationsGoalChange = (field, value) => {
    setEditingOperationsGoal(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveOperationsGoalEdit = async (e) => {
    e.preventDefault();
    if (!editingOperationsGoal || !editingOperationsGoal.metricName.trim() || !editingOperationsGoal.description.trim() || !selectedAccount?.contact?.email) return;

    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        // Update in Firebase
        await updateUserGoal(portalUserId, editingOperationsGoal.department, editingOperationsGoal.id, {
          metricName: editingOperationsGoal.metricName,
          description: editingOperationsGoal.description,
          target: parseFloat(editingOperationsGoal.target) || 0,
          actual: parseFloat(editingOperationsGoal.actual) || 0,
          unit: editingOperationsGoal.unit
        });
        
        // Update local state
        setOperationsGoals(prev => prev.map(goal => 
          goal.id === editingOperationsGoal.id 
            ? { ...editingOperationsGoal, target: parseFloat(editingOperationsGoal.target) || 0, actual: parseFloat(editingOperationsGoal.actual) || 0 }
            : goal
        ));
        
        closeEditOperationsGoalModal();
      }
    } catch (error) {
      console.error("Error updating goal: ", error);
    }
  };

  const handleDeleteOperationsGoal = async (goalId) => {
    if (!selectedAccount?.contact?.email) return;
    
    if (window.confirm('Are you sure you want to delete this goal? This action cannot be undone.')) {
      try {
        // Find the portal user for this account
        const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount, { role: 'client' });
        
        if (!usersSnapshot.empty) {
          const portalUserDoc = usersSnapshot.docs[0];
          const portalUserId = portalUserDoc.id;
          
          // Delete from Firebase
          await deleteUserGoal(portalUserId, 'Operations', goalId);
          
          // Update local state
          setOperationsGoals(prev => prev.filter(goal => goal.id !== goalId));
          
          closeEditOperationsGoalModal();
        }
      } catch (error) {
        console.error("Error deleting goal: ", error);
      }
    }
  };

  // Load social media data from Firebase for the selected account's portal user
  const loadSocialMediaDataFromFirebase = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        // No portal user found - clear social media data
        setSocialData({
          facebook: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
          instagram: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
          tiktok: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
          youtube: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 }
        });
        setPlatformEntries({
          facebook: [],
          instagram: [],
          tiktok: [],
          youtube: []
        });
      } else {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        // Load from socialMediaData collection directly
        const socialMediaDocRef = doc(db, 'socialMediaData', portalUserId);
        const socialMediaDoc = await getDoc(socialMediaDocRef);
        
        if (socialMediaDoc.exists()) {
          const data = socialMediaDoc.data();
          
          // Load social media data
          if (data.socialData) {
            setSocialData(data.socialData);
          } else {
            setSocialData({
              facebook: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
              instagram: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
              tiktok: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
              youtube: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 }
            });
          }
          
          // Load platform entries
          if (data.platformEntries) {
            setPlatformEntries(data.platformEntries);
          } else {
            setPlatformEntries({
              facebook: [],
              instagram: [],
              tiktok: [],
              youtube: []
            });
          }
        } else {
          // No social media data found - set defaults
          setSocialData({
            facebook: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
            instagram: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
            tiktok: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
            youtube: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 }
          });
          setPlatformEntries({
            facebook: [],
            instagram: [],
            tiktok: [],
            youtube: []
          });
        }
      }
    } catch (error) {
      console.error('Error loading social media data:', error);
      setSocialData({
        facebook: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
        instagram: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
        tiktok: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
        youtube: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 }
      });
      setPlatformEntries({
        facebook: [],
        instagram: [],
        tiktok: [],
        youtube: []
      });
    }
  };

  // Save social media entry to Firebase for the selected account's portal user
  const saveSocialMediaEntry = async (platform, entryData) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        // Get current platform entries
        const currentEntries = platformEntries[platform] || [];
        
        // Add new entry with timestamp
        const newEntry = {
          ...entryData,
          id: Date.now().toString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        const updatedEntries = [...currentEntries, newEntry];
        
        // Update the platform entries in socialMediaData collection
        const socialMediaDocRef = doc(db, 'socialMediaData', portalUserId);
        const socialMediaDoc = await getDoc(socialMediaDocRef);
        
        if (socialMediaDoc.exists()) {
          // Update existing document
          await updateDoc(socialMediaDocRef, {
            [`platformEntries.${platform}`]: updatedEntries,
            updatedAt: serverTimestamp()
          });
        } else {
          // Create new document with default structure
          await setDoc(socialMediaDocRef, {
            socialData: {
              facebook: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
              instagram: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
              tiktok: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
              youtube: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 }
            },
            platformEntries: {
              facebook: platform === 'facebook' ? updatedEntries : [],
              instagram: platform === 'instagram' ? updatedEntries : [],
              tiktok: platform === 'tiktok' ? updatedEntries : [],
              youtube: platform === 'youtube' ? updatedEntries : []
            },
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
        
        // Update local state
        setPlatformEntries(prev => ({
          ...prev,
          [platform]: updatedEntries
        }));
        
        console.log('✅ Social media entry saved to socialMediaData collection:', portalUserId);
        return true;
      } else {
        console.log('❌ Portal user not found for email:', selectedAccount.contact.email);
        return false;
      }
    } catch (error) {
      console.error('Error saving social media entry:', error);
      return false;
    }
  };

  // Update social media entry in Firebase for the selected account's portal user
  const updateSocialMediaEntry = async (platform, entryId, updatedData) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        // Get current platform entries
        const currentEntries = platformEntries[platform] || [];
        
        // Update the specific entry
        const updatedEntries = currentEntries.map(entry => 
          entry.id === entryId 
            ? { ...entry, ...updatedData, updatedAt: new Date().toISOString() }
            : entry
        );
        
        // Update the platform entries in socialMediaData collection
        const socialMediaDocRef = doc(db, 'socialMediaData', portalUserId);
        await updateDoc(socialMediaDocRef, {
          [`platformEntries.${platform}`]: updatedEntries,
          updatedAt: serverTimestamp()
        });
        
        // Update local state
        setPlatformEntries(prev => ({
          ...prev,
          [platform]: updatedEntries
        }));
        
        console.log('✅ Social media entry updated in socialMediaData collection:', portalUserId);
        return true;
      } else {
        console.log('❌ Portal user not found for email:', selectedAccount.contact.email);
        return false;
      }
    } catch (error) {
      console.error('Error updating social media entry:', error);
      return false;
    }
  };

  // Delete social media entry from Firebase for the selected account's portal user
  const deleteSocialMediaEntry = async (platform, entryId) => {
    if (!currentUser || !selectedAccount) return false;
    
    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (!usersSnapshot.empty) {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        
        // Get current platform entries
        const currentEntries = platformEntries[platform] || [];
        
        // Remove the specific entry
        const updatedEntries = currentEntries.filter(entry => entry.id !== entryId);
        
        // Update the platform entries in socialMediaData collection
        const socialMediaDocRef = doc(db, 'socialMediaData', portalUserId);
        await updateDoc(socialMediaDocRef, {
          [`platformEntries.${platform}`]: updatedEntries,
          updatedAt: serverTimestamp()
        });
        
        // Update local state
        setPlatformEntries(prev => ({
          ...prev,
          [platform]: updatedEntries
        }));
        
        console.log('✅ Social media entry deleted from socialMediaData collection:', portalUserId);
        return true;
      } else {
        console.log('❌ Portal user not found for email:', selectedAccount.contact.email);
        return false;
      }
    } catch (error) {
      console.error('Error deleting social media entry:', error);
      return false;
    }
  };

  // Load paid media campaigns from Firebase for the selected account's portal user
  const loadPaidMediaCampaignsFromFirebase = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      console.log('Loading paid media campaigns for account:', selectedAccount.contact.email);
      
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        console.log('No portal user found for email:', selectedAccount.contact.email);
        setCampaigns([]);
      } else {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        console.log('Found portal user with ID:', portalUserId);
        
        // Query campaigns directly like the individual PaidMedia.js page does
        const campaignsRef = collection(db, 'campaigns');
        const campaignsQuery = query(
          campaignsRef,
          where('userId', '==', portalUserId)
        );
        const campaignsSnapshot = await getDocs(campaignsQuery);
        
        console.log('Found', campaignsSnapshot.docs.length, 'campaigns for user', portalUserId);
        
        const campaignsData = campaignsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('Processed campaigns:', campaignsData);
        setCampaigns(campaignsData);
      }
    } catch (error) {
      console.error('Error loading paid media campaigns:', error);
      setCampaigns([]);
    }
  };

  // Update campaign metrics
  const updateCampaignMetrics = async (campaignId, newMetrics) => {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        ...newMetrics,
        updatedAt: new Date()
      });
      console.log('Campaign updated successfully');
      
      // Reload campaigns to update the list
      if (selectedAccount) {
        await loadPaidMediaCampaignsFromFirebase(selectedAccount.id);
      }
    } catch (err) {
      console.error('Error updating campaign:', err);
    }
  };

  // Delete campaign
  const deleteCampaign = async (campaignId) => {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      await deleteDoc(campaignRef);
      console.log('Campaign deleted successfully');
      
      // Reload campaigns to update the list
      if (selectedAccount) {
        await loadPaidMediaCampaignsFromFirebase(selectedAccount.id);
      }
    } catch (err) {
      console.error('Error deleting campaign:', err);
    }
  };

  // Add new campaign
  const addCampaign = async (campaignData) => {
    if (!currentUser || !selectedAccount) {
      console.error('No user or account selected');
      return;
    }

    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        console.error('No portal user found for email:', selectedAccount.contact.email);
        return;
      }

      const portalUserDoc = usersSnapshot.docs[0];
      const portalUserId = portalUserDoc.id;

      const campaignRef = collection(db, 'campaigns');
      const newCampaign = {
        ...campaignData,
        userId: portalUserId,
        createdAt: new Date(),
        updatedAt: new Date(),
        spent: 0,
        metrics: {
          impressions: 0,
          clicks: 0,
          conversions: 0,
          ctr: 0,
          cpc: 0,
          cpm: 0,
          roas: 0,
          revenue: 0
        }
      };
      
      await addDoc(campaignRef, newCampaign);
      setShowAddCampaign(false);
      console.log('Campaign added successfully');
      
      // Reload campaigns to show the new campaign in the list
      await loadPaidMediaCampaignsFromFirebase(selectedAccount.id);
    } catch (err) {
      console.error('Error adding campaign:', err);
    }
  };

  // Load newsletter campaigns from Firebase for the selected account's portal user
  const loadNewsletterCampaignsFromFirebase = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      console.log('Loading newsletter campaigns for account:', selectedAccount.contact.email);
      
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        console.log('No portal user found for email:', selectedAccount.contact.email);
        setNewsletterCampaigns([]);
      } else {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        console.log('Found portal user with ID:', portalUserId);
        
        // Use the same service as the individual newsletter page
        const userData = await getUserNewsletterData(portalUserId);
        const campaigns = userData.campaigns || [];
        
        console.log('Found newsletter campaigns for user', portalUserId, ':', campaigns.length);
        setNewsletterCampaigns(campaigns);
      }
    } catch (error) {
      console.error('Error loading newsletter campaigns:', error);
      setNewsletterCampaigns([]);
    }
  };

  // Add newsletter campaign function
  const handleAddNewsletterCampaign = async (campaignData) => {
    if (!currentUser || !selectedAccount) {
      setError('You must be logged in and have a selected account to add campaigns');
      return;
    }

    try {
      setError(null);
      
      // Find the portal user for this account (same as paid media)
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        setError('No portal user found for this account');
        return;
      }

      const portalUserDoc = usersSnapshot.docs[0];
      const accountPortalUserId = portalUserDoc.id;
      
      console.log('handleAddNewsletterCampaign - Using accountPortalUserId:', accountPortalUserId);

      const newCampaign = await addNewsletterCampaign(accountPortalUserId, campaignData);
      setNewsletterCampaigns([...newsletterCampaigns, newCampaign]);
      setShowNewsletterAddPopup(false);
    } catch (error) {
      console.error('Error adding newsletter campaign:', error);
      setError('Failed to add newsletter campaign. Please try again.');
    }
  };

  // Update newsletter campaign function
  const handleUpdateNewsletterCampaign = async (campaignId, updateData) => {
    console.log('handleUpdateNewsletterCampaign called with campaignId:', campaignId);
    console.log('handleUpdateNewsletterCampaign updateData:', updateData);
    
    if (!currentUser || !selectedAccount) {
      setError('You must be logged in and have a selected account to update campaigns');
      return;
    }

    try {
      setError(null);
      
      // Find the portal user for this account (same as paid media)
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        setError('No portal user found for this account');
        return;
      }

      const portalUserDoc = usersSnapshot.docs[0];
      const accountPortalUserId = portalUserDoc.id;
      
      console.log('handleUpdateNewsletterCampaign - accountPortalUserId:', accountPortalUserId);

      await updateNewsletterCampaign(campaignId, updateData, accountPortalUserId);
      setNewsletterCampaigns(newsletterCampaigns.map(campaign => 
        campaign.id === campaignId 
          ? { ...campaign, ...updateData }
          : campaign
      ));
    } catch (error) {
      console.error('Error updating newsletter campaign:', error);
      setError('Failed to update newsletter campaign. Please try again.');
    }
  };

  // Delete newsletter campaign function
  const handleDeleteNewsletterCampaign = async (campaignId) => {
    if (!currentUser || !selectedAccount) {
      setError('You must be logged in and have a selected account to delete campaigns');
      return;
    }

    try {
      setError(null);
      
      // Find the portal user for this account (same as paid media)
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        setError('No portal user found for this account');
        return;
      }

      const portalUserDoc = usersSnapshot.docs[0];
      const accountPortalUserId = portalUserDoc.id;

      await deleteNewsletterCampaign(campaignId, accountPortalUserId);
      setNewsletterCampaigns(newsletterCampaigns.filter(campaign => campaign.id !== campaignId));
    } catch (error) {
      console.error('Error deleting newsletter campaign:', error);
      setError('Failed to delete newsletter campaign. Please try again.');
    }
  };

  const loadReviewsDataFromFirebase = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      console.log('Loading reviews data for account:', selectedAccount.contact.email);
      
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        console.log('No portal user found for email:', selectedAccount.contact.email);
        setReviewsData({
          categoryIds: [],
          additionalIds: [],
          totalPhotos: 0,
          totalReviews: 0,
          newThisMonth: 0,
          averageRating: 0,
          ratingDistribution: [],
          fiveStarReviews: 0,
          competitors: [],
          businessMetrics: {
            totalTopics: 0,
            topicReviews: 0,
            address: false,
            phone: false,
            website: false,
            logo: false,
            hours: false,
            claimed: false,
            attributes: 0
          }
        });
      } else {
        const portalUserDoc = usersSnapshot.docs[0];
        const portalUserId = portalUserDoc.id;
        console.log('Found portal user with ID:', portalUserId);
        
        // Use the same service as the individual reviews page
        const result = await getReviewsData(portalUserId);
        
        if (result.success && result.data) {
          console.log('Found reviews data for user', portalUserId, ':', result.data);
          setReviewsData(result.data);
        } else {
          console.log('No reviews data found for user', portalUserId);
          setReviewsData({
            categoryIds: [],
            additionalIds: [],
            totalPhotos: 0,
            totalReviews: 0,
            newThisMonth: 0,
            averageRating: 0,
            ratingDistribution: [],
            fiveStarReviews: 0,
            competitors: [],
            businessMetrics: {
              totalTopics: 0,
              topicReviews: 0,
              address: false,
              phone: false,
              website: false,
              logo: false,
              hours: false,
              claimed: false,
              attributes: 0
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading reviews data:', error);
      setReviewsData({
        categoryIds: [],
        additionalIds: [],
        totalPhotos: 0,
        totalReviews: 0,
        newThisMonth: 0,
        averageRating: 0,
        ratingDistribution: [],
        fiveStarReviews: 0,
        competitors: [],
        businessMetrics: {
          totalTopics: 0,
          topicReviews: 0,
          address: false,
          phone: false,
          website: false,
          logo: false,
          hours: false,
          claimed: false,
          attributes: 0
        }
      });
    }
  };


  // Load Job Listings data from Firebase
  const loadJobListingsFromFirebase = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      setJobListingsLoading(true);
      
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        // No portal user found - clear job listings data
        setJobListingsData({
          jobContent: {
            companyIntroduction: '',
            whyJoinUs: '',
            pleaseNote: 'This position is open to U.S. citizens, lawful permanent residents (Green Card holders), or individuals with valid Employment Authorization Documents (EADs). We are unable to sponsor employment visas at this time.',
            equalOpportunity: false,
            benefits: []
          },
          jobListings: []
        });
        setJobListingsLoading(false);
        return;
      }
      
      const portalUserDoc = usersSnapshot.docs[0];
      const portalUserId = portalUserDoc.id;
      
      // Load job content
      const userContentQuery = query(
        collection(db, 'userJobContent'),
        where('userId', '==', portalUserId)
      );
      const userContentSnapshot = await getDocs(userContentQuery);
      
      let jobContent = {
        companyIntroduction: '',
        whyJoinUs: '',
        pleaseNote: 'This position is open to U.S. citizens, lawful permanent residents (Green Card holders), or individuals with valid Employment Authorization Documents (EADs). We are unable to sponsor employment visas at this time.',
        equalOpportunity: false,
        benefits: []
      };
      
      if (!userContentSnapshot.empty) {
        const userContent = userContentSnapshot.docs[0].data();
        jobContent = {
          companyIntroduction: userContent.companyIntroduction || '',
          whyJoinUs: userContent.whyJoinUs || '',
          pleaseNote: userContent.pleaseNote || 'This position is open to U.S. citizens, lawful permanent residents (Green Card holders), or individuals with valid Employment Authorization Documents (EADs). We are unable to sponsor employment visas at this time.',
          equalOpportunity: userContent.equalOpportunity || false,
          benefits: userContent.benefits || []
        };
      }
      
      // Load job listings
      const jobListingsQuery = query(
        collection(db, 'jobListings'),
        where('userId', '==', portalUserId)
      );
      const jobListingsSnapshot = await getDocs(jobListingsQuery);
      const jobListings = jobListingsSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(job => 
          job.status !== 'deleted' && 
          job.title !== 'ss@gmail.com' &&
          job.description !== 'ss@gmail.com' &&
          job.location !== 'ss@gmail.com'
        );
      
      setJobListingsData({
        jobContent,
        jobListings
      });
      
    } catch (error) {
      console.error('Error loading job listings:', error);
      // Clear data on error
      setJobListingsData({
        jobContent: {
          companyIntroduction: '',
          whyJoinUs: '',
          pleaseNote: 'This position is open to U.S. citizens, lawful permanent residents (Green Card holders), or individuals with valid Employment Authorization Documents (EADs). We are unable to sponsor employment visas at this time.',
          equalOpportunity: false,
          benefits: []
        },
        jobListings: []
      });
    } finally {
      setJobListingsLoading(false);
    }
  };

  // Get the Firestore refs the shared tech stack lives on: the account doc is the
  // company-wide source of truth; the portal user doc is kept in sync for legacy readers

  // Save tech stack fields company-wide (account doc), mirroring to the portal user doc

  // Tech Stack popup handlers

  // Handle proposing software for tech stack

  // Handle removing software for tech stack (adds to proposed list as removed)

  // Handle updating proposed list (reorder, remove, edit descriptions)

  // Handle removing item from proposed list

  // Handle updating description for proposed item

  // Handle reordering proposed items

  // Helper function to get user's automation status

  // Filter automations by active tab and include user automation requests.
  // Exclude template automations that already have a request (same id) so we don't show duplicate cards.
  // Hidden automations show here (CRM PortalView) but not in client portal (Automation.js)

  // Handle plugin install

  // Handle plugin uninstall

  // Load Leads entries from Firebase
  const loadLeadsEntriesFromFirebase = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        // No portal user found - clear leads entries
        setLeadsEntries([]);
        return;
      }
      
      const portalUserDoc = usersSnapshot.docs[0];
      const userData = portalUserDoc.data();
      
      if (userData.marketingLeads) {
        setLeadsEntries(userData.marketingLeads);
      } else {
        setLeadsEntries([]);
      }
    } catch (error) {
      console.error('Error loading Leads entries:', error);
    }
  };

  // Load overhead data from Firebase
  const loadOverheadDataFromFirebase = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        // No portal user found - clear overhead data
        setOverheadData({
          expenses: {
            'Operational Overhead': [],
            'Operation Payroll': [],
            'Asset Payments': [],
            'Subscriptions': [],
            'Insurance': [],
            'Debt Service': [],
            'Soft Debt': []
          },
          timePeriod: 'Annual',
          summaryViewMode: 'box'
        });
        setOverheadTimePeriod('Annual');
        setOverheadSummary({
          fixedOverhead: 0,
          softOverhead: 0,
          totalOverhead: 0
        });
        return;
      }
      
      const portalUserDoc = usersSnapshot.docs[0];
      const portalUserId = portalUserDoc.id;
      
      // Load overhead data from user document
      const userRef = doc(db, 'users', portalUserId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.overhead) {
          console.log('Loading overhead data for portal user:', userData.overhead);
          setOverheadData(userData.overhead);
          setOverheadTimePeriod(userData.overhead.timePeriod || 'Annual');
          
          // Calculate summary values
          let fixedTotal = 0;
          let softTotal = 0;
          
          Object.entries(userData.overhead.expenses || {}).forEach(([category, categoryExpenses]) => {
            categoryExpenses.forEach(expense => {
              const adjustedAmount = calculateAdjustedAmount(expense.amount, expense.frequency);
              
              // Soft overhead: Debt Service and Soft Debt categories
              if (category === 'Soft Debt' || category === 'Debt Service') {
                softTotal += adjustedAmount;
              } else {
                // Fixed overhead: all other categories
                fixedTotal += adjustedAmount;
              }
            });
          });
          
          setOverheadSummary({
            fixedOverhead: fixedTotal,
            softOverhead: softTotal,
            totalOverhead: fixedTotal + softTotal
          });
        } else {
          console.log('No overhead data found for portal user');
          setOverheadData({
            expenses: {
              'Operational Overhead': [],
              'Operation Payroll': [],
              'Asset Payments': [],
              'Subscriptions': [],
              'Insurance': [],
              'Debt Service': [],
              'Soft Debt': []
            },
            timePeriod: 'Annual',
            summaryViewMode: 'box'
          });
          setOverheadTimePeriod('Annual');
          setOverheadSummary({
            fixedOverhead: 0,
            softOverhead: 0,
            totalOverhead: 0
          });
        }
      } else {
        console.log('Portal user document not found');
        setOverheadData({
          expenses: {
            'Operational Overhead': [],
            'Operation Payroll': [],
            'Asset Payments': [],
            'Subscriptions': [],
            'Insurance': [],
            'Debt Service': [],
            'Soft Debt': []
          },
          timePeriod: 'Annual',
          summaryViewMode: 'box'
        });
        setOverheadTimePeriod('Annual');
        setOverheadSummary({
          fixedOverhead: 0,
          softOverhead: 0,
          totalOverhead: 0
        });
      }
    } catch (error) {
      console.error('Error loading overhead data:', error);
    }
  };

  // Overhead data management functions
  const addOverheadExpense = useCallback((category) => {
    setOverheadData(prev => ({
      ...prev,
      expenses: {
        ...prev.expenses,
        [category]: [
          ...(prev.expenses[category] || []),
          { description: '', amount: 0, frequency: 'Monthly' }
        ]
      }
    }));
  }, []);

  const updateOverheadExpense = useCallback((category, index, field, value) => {
    setOverheadData(prev => {
      const updatedCategory = [...(prev.expenses[category] || [])];
      updatedCategory[index] = {
        ...updatedCategory[index],
        [field]: field === 'amount' ? parseFloat(value) || 0 : value
      };
      
      return {
        ...prev,
        expenses: {
          ...prev.expenses,
          [category]: updatedCategory
        }
      };
    });
  }, []);

  const removeOverheadExpense = useCallback((category, index) => {
    setOverheadData(prev => {
      const updatedCategory = [...(prev.expenses[category] || [])];
      updatedCategory.splice(index, 1);
      
      return {
        ...prev,
        expenses: {
          ...prev.expenses,
          [category]: updatedCategory
        }
      };
    });
  }, []);

  const saveOverheadDataToFirebase = async () => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      setOverheadSaving(true);
      
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        setOverheadMessage('Portal user not found');
        setTimeout(() => setOverheadMessage(''), 3000);
        return;
      }
      
      const portalUserDoc = usersSnapshot.docs[0];
      const portalUserId = portalUserDoc.id;
      
      // Save overhead data to user document
      const userRef = doc(db, 'users', portalUserId);
      await updateDoc(userRef, {
        overhead: {
          ...overheadData,
          timePeriod: overheadTimePeriod,
          lastUpdated: new Date().toISOString()
        }
      });
      
      setOverheadMessage('Overhead data saved successfully');
      setTimeout(() => setOverheadMessage(''), 3000);
      setOverheadEditing(false); // Exit edit mode after successful save
      
      // Recalculate summary after saving
      let fixedTotal = 0;
      let softTotal = 0;
      
      Object.entries(overheadData.expenses || {}).forEach(([category, categoryExpenses]) => {
        categoryExpenses.forEach(expense => {
          const adjustedAmount = calculateAdjustedAmount(expense.amount, expense.frequency);
          
          if (category === 'Soft Debt' || category === 'Debt Service') {
            softTotal += adjustedAmount;
          } else {
            fixedTotal += adjustedAmount;
          }
        });
      });
      
      setOverheadSummary({
        fixedOverhead: fixedTotal,
        softOverhead: softTotal,
        totalOverhead: fixedTotal + softTotal
      });
    } catch (error) {
      console.error('Error saving overhead data:', error);
      setOverheadMessage('Error saving overhead data');
      setTimeout(() => setOverheadMessage(''), 3000);
    } finally {
      setOverheadSaving(false);
    }
  };

  // Removed auto-save - only save when manually triggered

  // Load website data from Firebase
  const loadWebsiteDataFromFirebase = useCallback(async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        // No portal user found - clear website data
        setWebsiteData({
          lastScanned: 'No data available',
          seoScore: 0,
          pageHealth: 0,
          contentScore: 0,
          onPageScore: 0,
          offPageScore: 0,
          technicalScore: 0,
          issues: [],
          pages: [],
          audits: [],
          organicTraffic: {
            amount: 0,
            rankingKeywords: 0,
            estimatedTrafficValue: 0,
            keyInsight: 'No data available'
          },
          backlinkProfile: {
            backlinks: 0,
            spamScore: 0,
            nofollowLinks: 0,
            keyInsight: 'No data available'
          },
          historicalTraffic: {
            averageETV: 0,
            highestETV: 0,
            keywordsPerMonth: 0,
            keyInsight: 'No data available'
          },
          rankedKeywords: {
            total: 0,
            top10: 0,
            organicTrafficValue: 0,
            keyInsight: 'No data available'
          },
          pageAudit: {
            onpageScore: 0,
            technicalScore: 0,
            contentScore: 0,
            keyInsight: 'No data available'
          },
          errorsWarnings: {}
        });
        return;
      }
      
      const portalUserDoc = usersSnapshot.docs[0];
      const userData = portalUserDoc.data();
      const accountId = userData.accountId;
      
      console.log('Portal user found with accountId:', accountId);
      
      // Check for website audit data in the accounts/{accountId}/audits subcollection
      let websiteData = null;
      
      if (accountId) {
        try {
          // Query the account-specific audits collection for completed audits
          const auditsQuery = query(
            collection(db, 'accounts', accountId, 'audits'),
            where('status', '==', 'completed'),
            orderBy('createdAt', 'desc'),
            limit(1)
          );
          const auditsSnapshot = await getDocs(auditsQuery);
          
          if (!auditsSnapshot.empty) {
            const latestAudit = auditsSnapshot.docs[0].data();
            console.log('Found latest completed audit:', latestAudit);
            websiteData = latestAudit;
          } else {
            console.log('No completed audits found in accounts collection');
          }
        } catch (indexError) {
          // If the index is still building, fall back to a simpler query
          if (indexError.code === 'failed-precondition' || indexError.message?.includes('index')) {
            console.warn('Audits index still building, falling back to simple query');
            
            try {
              // Fallback: query by status only (no ordering)
              const fallbackQuery = query(
                collection(db, 'accounts', accountId, 'audits'),
                where('status', '==', 'completed')
              );
              const auditsSnapshot = await getDocs(fallbackQuery);
              
              if (!auditsSnapshot.empty) {
                // Sort by createdAt in memory and get the latest
                const audits = auditsSnapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                }));
                
                audits.sort((a, b) => {
                  const createdAtA = a.createdAt?.toMillis?.() || a.createdAt || 0;
                  const createdAtB = b.createdAt?.toMillis?.() || b.createdAt || 0;
                  return createdAtB - createdAtA; // Descending order
                });
                
                if (audits.length > 0) {
                  console.log('Found latest completed audit (fallback):', audits[0]);
                  websiteData = audits[0];
                }
              } else {
                console.log('No completed audits found in accounts collection');
              }
            } catch (fallbackError) {
              console.error('Error in fallback audits query:', fallbackError);
            }
          } else {
            console.error('Error querying audits collection:', indexError);
          }
        }
      }
      
      // Fallback: Check for website audit data in user document fields
      if (!websiteData) {
        // Check direct websiteAudit field
        if (userData.websiteAudit) {
          console.log('Loading website data from websiteAudit field:', userData.websiteAudit);
          websiteData = userData.websiteAudit;
        }
        // Check marketingData.website field
        else if (userData.marketingData?.website) {
          console.log('Loading website data from marketingData.website field:', userData.marketingData.website);
          websiteData = userData.marketingData.website;
        }
        // Check for completed audits in auditHistory
        else if (userData.auditHistory && userData.auditHistory.length > 0) {
          console.log('Loading website data from auditHistory:', userData.auditHistory);
          // Get the most recent completed audit
          const completedAudits = userData.auditHistory.filter(audit => audit.status === 'completed' && audit.results);
          if (completedAudits.length > 0) {
            const latestAudit = completedAudits[completedAudits.length - 1];
            websiteData = latestAudit.results;
          }
        }
        // Check for website data in other possible locations
        else if (userData.websiteData) {
          console.log('Loading website data from websiteData field:', userData.websiteData);
          websiteData = userData.websiteData;
        }
      }
      
      if (websiteData) {
        // Transform the data to match our expected structure
        const transformedData = {
          lastScanned: websiteData.lastScanned || websiteData.scanDate || new Date().toLocaleDateString(),
          seoScore: websiteData.seoScore || websiteData.seo_score || 0,
          pageHealth: websiteData.pageHealth || websiteData.page_health || 0,
          contentScore: websiteData.contentScore || websiteData.content_score || 0,
          onPageScore: websiteData.onPageScore || websiteData.onpage_score || 0,
          offPageScore: websiteData.offPageScore || websiteData.offpage_score || 0,
          technicalScore: websiteData.technicalScore || websiteData.technical_score || 0,
          issues: websiteData.issues || [],
          pages: websiteData.pages || [],
          audits: websiteData.audits || [],
          organicTraffic: websiteData.organicTraffic || {
            amount: websiteData.organic_traffic || 0,
            rankingKeywords: websiteData.ranking_keywords || 0,
            estimatedTrafficValue: websiteData.estimated_traffic_value || 0,
            keyInsight: websiteData.traffic_insight || 'No traffic data available'
          },
          backlinkProfile: websiteData.backlinkProfile || {
            backlinks: websiteData.backlinks || 0,
            spamScore: websiteData.spam_score || 0,
            nofollowLinks: websiteData.nofollow_links || 0,
            keyInsight: websiteData.backlink_insight || 'No backlink data available'
          },
          historicalTraffic: websiteData.historicalTraffic || {
            averageETV: websiteData.average_etv || 0,
            highestETV: websiteData.highest_etv || 0,
            keywordsPerMonth: websiteData.keywords_per_month || 0,
            keyInsight: websiteData.historical_insight || 'No historical data available'
          },
          rankedKeywords: websiteData.rankedKeywords || {
            total: websiteData.total_keywords || 0,
            top10: websiteData.top10_keywords || 0,
            organicTrafficValue: websiteData.organic_traffic_value || 0,
            keyInsight: websiteData.keyword_insight || 'No keyword data available'
          },
          pageAudit: websiteData.pageAudit || {
            onpageScore: websiteData.onpage_score || 0,
            technicalScore: websiteData.technical_score || 0,
            contentScore: websiteData.content_score || 0,
            keyInsight: websiteData.page_insight || 'No page audit data available'
          },
          errorsWarnings: websiteData.errorsWarnings || {}
        };
        
        console.log('Transformed website data:', transformedData);
        setWebsiteData(transformedData);
      } else {
        console.log('No website data found for portal user');
        setWebsiteData({
          lastScanned: 'No data available',
          seoScore: 0,
          pageHealth: 0,
          contentScore: 0,
          onPageScore: 0,
          offPageScore: 0,
          technicalScore: 0,
          issues: [],
          pages: [],
          audits: [],
          organicTraffic: {
            amount: 0,
            rankingKeywords: 0,
            estimatedTrafficValue: 0,
            keyInsight: 'No data available'
          },
          backlinkProfile: {
            backlinks: 0,
            spamScore: 0,
            nofollowLinks: 0,
            keyInsight: 'No data available'
          },
          historicalTraffic: {
            averageETV: 0,
            highestETV: 0,
            keywordsPerMonth: 0,
            keyInsight: 'No data available'
          },
          rankedKeywords: {
            total: 0,
            top10: 0,
            organicTrafficValue: 0,
            keyInsight: 'No data available'
          },
          pageAudit: {
            onpageScore: 0,
            technicalScore: 0,
            contentScore: 0,
            keyInsight: 'No data available'
          },
          errorsWarnings: {}
        });
      }
    } catch (error) {
      console.error('Error loading website data:', error);
    }
  }, [currentUser, selectedAccount]);

  // Save runway data to Firebase

  // Add new project functions

  // Edit and delete functions

  // Filter projects based on search term

  // Calculate GPM scenarios

  // Function to calculate a single GPM scenario

  // Load labor burden data from Firebase
  const loadLaborBurdenDataFromFirebase = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        // No portal user found - clear labor burden data
        setLaborBurdenEmployees([]);
        setLaborBurdenWorkDays(251);
        setLaborBurdenPto(10);
        setLaborBurdenHolidays(7);
        setLaborBurdenHourlyRate(30);
        setLaborBurdenHourlyExtraCosts(15);
        setLaborBurdenUnbillablePercentage(10);
        setLaborBurdenSalaryType('annual');
        setLaborBurdenSalaryRate(62400);
        setLaborBurdenSalaryExtraCosts(15);
        setLaborBurdenSummaryStats({
          hourly: 0,
          daily: 0,
          weekly: 0,
          monthly: 0,
          annual: 0
        });
        return;
      }
      
      const portalUserDoc = usersSnapshot.docs[0];
      const portalUserId = portalUserDoc.id;
      
      // Load labor burden data from portal user's document
      const userDocRef = doc(db, 'users', portalUserId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        
        // Load labor burden data from nested laborBurden object
        if (data.laborBurden) {
          const laborBurdenData = data.laborBurden;
          
          // Load employees data
          if (laborBurdenData.employees) {
            setLaborBurdenEmployees(laborBurdenData.employees);
          } else {
            setLaborBurdenEmployees([]);
          }
          
          // Load configuration data
          if (laborBurdenData.workDays) setLaborBurdenWorkDays(laborBurdenData.workDays);
          if (laborBurdenData.pto) setLaborBurdenPto(laborBurdenData.pto);
          if (laborBurdenData.holidays) setLaborBurdenHolidays(laborBurdenData.holidays);
          if (laborBurdenData.hourlyRate) setLaborBurdenHourlyRate(laborBurdenData.hourlyRate);
          if (laborBurdenData.hourlyExtraCosts) setLaborBurdenHourlyExtraCosts(laborBurdenData.hourlyExtraCosts);
          if (laborBurdenData.unbillablePercentage) setLaborBurdenUnbillablePercentage(laborBurdenData.unbillablePercentage);
          if (laborBurdenData.salaryType) setLaborBurdenSalaryType(laborBurdenData.salaryType);
          if (laborBurdenData.salaryRate) setLaborBurdenSalaryRate(laborBurdenData.salaryRate);
          if (laborBurdenData.salaryExtraCosts) setLaborBurdenSalaryExtraCosts(laborBurdenData.salaryExtraCosts);
          
          // Load summary stats if available
          if (laborBurdenData.summaryStats) {
            setLaborBurdenSummaryStats(laborBurdenData.summaryStats);
          } else {
            setLaborBurdenSummaryStats({
              hourly: 0,
              daily: 0,
              weekly: 0,
              monthly: 0,
              annual: 0
            });
          }
        } else {
          // No labor burden data found - set defaults
          setLaborBurdenEmployees([]);
          setLaborBurdenWorkDays(251);
          setLaborBurdenPto(10);
          setLaborBurdenHolidays(7);
          setLaborBurdenHourlyRate(30);
          setLaborBurdenHourlyExtraCosts(15);
          setLaborBurdenUnbillablePercentage(10);
          setLaborBurdenSalaryType('annual');
          setLaborBurdenSalaryRate(62400);
          setLaborBurdenSalaryExtraCosts(15);
          setLaborBurdenSummaryStats({
            hourly: 0,
            daily: 0,
            weekly: 0,
            monthly: 0,
            annual: 0
          });
        }
      } else {
        // Portal user document doesn't exist - set defaults
        setLaborBurdenEmployees([]);
        setLaborBurdenWorkDays(251);
        setLaborBurdenPto(10);
        setLaborBurdenHolidays(7);
        setLaborBurdenHourlyRate(30);
        setLaborBurdenHourlyExtraCosts(15);
        setLaborBurdenUnbillablePercentage(10);
        setLaborBurdenSalaryType('annual');
        setLaborBurdenSalaryRate(62400);
        setLaborBurdenSalaryExtraCosts(15);
        setLaborBurdenSummaryStats({
          hourly: 0,
          daily: 0,
          weekly: 0,
          monthly: 0,
          annual: 0
        });
      }
    } catch (error) {
      console.error('Error loading labor burden data:', error);
    }
  };

  // Load margin calculator data from Firebase
  const loadMarginCalculatorDataFromFirebase = async (accountId) => {
    if (!currentUser || !selectedAccount) return;
    
    setMarginCalculatorLoading(true);
    
    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        // No portal user found - use defaults
        const defaultInputs = {
          taxRate: 0.20,
          profitabilityDesire: 0.11,
          startingGPM: 0.30,
          overhead: 260000,
          trueGPM: 0.25,
        };
        setMarginCalculatorInputs(defaultInputs);
        return;
      }
      
      const portalUserDoc = usersSnapshot.docs[0];
      const portalUserId = portalUserDoc.id;
      
      // Load margin calculator data from portal user's document
      const userDocRef = doc(db, 'users', portalUserId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        
        // Try to load from various possible locations
        let marginInputs = null;
        
        // Check for marginCalculator (from standalone margin calculator)
        if (data.marginCalculator) {
          if (data.marginCalculator.inputs) {
            marginInputs = data.marginCalculator.inputs;
          } else {
            marginInputs = data.marginCalculator;
          }
        }
        // Check for marginCalculatorInputs (portal system)
        else if (data.marginCalculatorInputs) {
          marginInputs = data.marginCalculatorInputs;
        }
        // Check for operationsData.gpmData (from onboarding)
        else if (data.operationsData?.gpmData) {
          const gpmData = data.operationsData.gpmData;
          marginInputs = {
            taxRate: 0.20,
            profitabilityDesire: 0.11,
            startingGPM: gpmData.current ? gpmData.current / 100 : 0.30,
            overhead: 260000,
            trueGPM: gpmData.goal ? gpmData.goal / 100 : 0.25,
          };
        }
        // Check for businessAssessment.gpmData (from other forms)
        else if (data.businessAssessment?.gpmData) {
          const gpmData = data.businessAssessment.gpmData;
          marginInputs = {
            taxRate: 0.20,
            profitabilityDesire: 0.11,
            startingGPM: gpmData.oneYearAgo ? gpmData.oneYearAgo / 100 : 0.30,
            overhead: 260000,
            trueGPM: gpmData.currentYearProjection ? gpmData.currentYearProjection / 100 : 0.25,
          };
        }
        // Check for financeData (from financial health)
        else if (data.financeData) {
          marginInputs = {
            taxRate: 0.20,
            profitabilityDesire: 0.11,
            startingGPM: 0.30,
            overhead: 260000,
            trueGPM: 0.25,
          };
        }
        
        if (marginInputs) {
          setMarginCalculatorInputs(marginInputs);
        } else {
          // Try to extract any available financial data from the portal user
          let extractedInputs = {
            taxRate: 0.20,
            profitabilityDesire: 0.11,
            startingGPM: 0.30,
            overhead: 260000,
            trueGPM: 0.25,
          };
          
          // Try to extract overhead from various sources
          if (data.operationsData?.monthlyOverhead) {
            extractedInputs.overhead = data.operationsData.monthlyOverhead * 12; // Convert monthly to annual
          } else if (data.financeData?.monthlyOverhead) {
            extractedInputs.overhead = data.financeData.monthlyOverhead * 12;
          } else if (data.operationsData?.overhead) {
            extractedInputs.overhead = data.operationsData.overhead;
          }
          
          // Try to extract GPM data from various sources
          if (data.operationsData?.gpmData?.current) {
            extractedInputs.startingGPM = data.operationsData.gpmData.current / 100;
          }
          if (data.operationsData?.gpmData?.goal) {
            extractedInputs.trueGPM = data.operationsData.gpmData.goal / 100;
          }
          
          setMarginCalculatorInputs(extractedInputs);
        }
      } else {
        // Portal user document doesn't exist - use defaults
        const defaultInputs = {
          taxRate: 0.20,
          profitabilityDesire: 0.11,
          startingGPM: 0.30,
          overhead: 260000,
          trueGPM: 0.25,
        };
        setMarginCalculatorInputs(defaultInputs);
      }
    } catch (error) {
      console.error('Error loading margin calculator data:', error);
      // Fallback to defaults on error
      const defaultInputs = {
        taxRate: 0.20,
        profitabilityDesire: 0.11,
        startingGPM: 0.30,
        overhead: 260000,
        trueGPM: 0.25,
      };
      setMarginCalculatorInputs(defaultInputs);
    } finally {
      setMarginCalculatorLoading(false);
    }
  };

  // Helper function to generate chart data from user entries

  // Helper function to generate NPM chart data

  // Helper functions for NPM data management

  // Helper functions for Revenue data management

  // Helper functions for debt calculations

  // Calculate adjusted amount for overhead expenses
  const calculateAdjustedAmount = useCallback((amount, frequency) => {
    // First convert to annual amount
    let annualAmount = 0;
    if (frequency === 'Weekly') {
      annualAmount = amount * 52;
    } else if (frequency === 'Monthly') {
      annualAmount = amount * 12;
    } else if (frequency === 'Quarterly') {
      annualAmount = amount * 4;
    } else if (frequency === 'Annual') {
      annualAmount = amount;
    }
    
    // Then convert from annual to selected time period
    if (overheadTimePeriod === 'Weekly') {
      return annualAmount / 52;
    } else if (overheadTimePeriod === 'Monthly') {
      return annualAmount / 12;
    } else if (overheadTimePeriod === 'Quarterly') {
      return annualAmount / 4;
    } else {
      return annualAmount;
    }
  }, [overheadTimePeriod]);

  // Forecast calculation functions

  // GPM formatting functions

  // Calculate cash flow data

  // Helper function to get value from a timeline point

  // Calculate weighted interest rate for each point

  // Debt management functions

  // Helper functions for labor burden calculations
  const calculateLaborBurdenEmployeeCosts = (employee) => {
    if (employee.type === 'hourly') {
      const burden = employee.rate * (1 + (employee.extraCosts || 15) / 100);
      const unbillable = (burden / (1 - (employee.unbillablePercentage || 10) / 100)) - burden;
      const daysOff = (burden / (1 - (laborBurdenPto / laborBurdenWorkDays))) - burden;
      const holidayAmount = (burden / (1 - (laborBurdenHolidays / laborBurdenWorkDays))) - burden;
      const totalHourly = burden + unbillable + daysOff + holidayAmount;
      
      return {
        weekly: totalHourly * 40,
        monthly: (totalHourly * 40 * 52) / 12,
        annual: totalHourly * 40 * 52
      };
    } else {
      const annualRate = employee.rate * (1 + (employee.extraCosts || 15) / 100);
      return {
        weekly: annualRate / 52,
        monthly: annualRate / 12,
        annual: annualRate
      };
    }
  };

  const calculateLaborBurdenSummaryStats = () => {
    if (laborBurdenEmployees.length === 0) {
      setLaborBurdenSummaryStats({
        hourly: 0,
        daily: 0,
        weekly: 0,
        monthly: 0,
        annual: 0
      });
      return;
    }

    // Calculate total annual cost for all employees
    const totalAnnual = laborBurdenEmployees.reduce((sum, emp) => {
      const costs = calculateLaborBurdenEmployeeCosts(emp);
      return sum + costs.annual;
    }, 0);

    // Calculate hourly rate based on total annual cost
    const totalHourly = totalAnnual / (laborBurdenWorkDays * 8);

    setLaborBurdenSummaryStats({
      hourly: totalHourly,
      daily: totalHourly * 8,
      weekly: totalAnnual / 52,
      monthly: totalAnnual / 12,
      annual: totalAnnual
    });
  };

  const saveLaborBurdenData = async () => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        console.log('No portal user found for this account');
        return;
      }
      
      const portalUserDoc = usersSnapshot.docs[0];
      const portalUserId = portalUserDoc.id;
      
      const laborBurdenData = {
        employees: laborBurdenEmployees,
        workDays: laborBurdenWorkDays,
        pto: laborBurdenPto,
        holidays: laborBurdenHolidays,
        hourlyRate: laborBurdenHourlyRate,
        hourlyExtraCosts: laborBurdenHourlyExtraCosts,
        unbillablePercentage: laborBurdenUnbillablePercentage,
        salaryType: laborBurdenSalaryType,
        salaryRate: laborBurdenSalaryRate,
        salaryExtraCosts: laborBurdenSalaryExtraCosts,
        summaryStats: laborBurdenSummaryStats,
        lastUpdated: new Date().toISOString()
      };
      
      const userDocRef = doc(db, 'users', portalUserId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        await updateDoc(userDocRef, {
          laborBurden: laborBurdenData
        });
      } else {
        await setDoc(userDocRef, {
          laborBurden: laborBurdenData
        });
      }
    } catch (error) {
      console.error('Error saving labor burden data:', error);
    }
  };

  const handleAddLaborBurdenEmployee = async () => {
    if (!newLaborBurdenEmployee.name || !newLaborBurdenEmployee.jobTitle) {
      alert('Please fill in both name and job title');
      return;
    }
    
    try {
      const newEmp = {
        id: Date.now(),
        ...newLaborBurdenEmployee,
        extraCosts: 15,
        unbillablePercentage: newLaborBurdenEmployee.type === 'hourly' ? 10 : 0
      };
      
      // Update local state
      setLaborBurdenEmployees([...laborBurdenEmployees, newEmp]);
      
      // Save to Firebase
      await saveLaborBurdenData();
      
      setShowLaborBurdenAddModal(false);
      setNewLaborBurdenEmployee({
        type: 'hourly',
        name: '',
        jobTitle: '',
        rate: 0
      });
    } catch (error) {
      console.error('Error adding employee:', error);
    }
  };

  const handleUpdateLaborBurdenEmployee = async () => {
    if (!editingLaborBurdenEmployee.name || !editingLaborBurdenEmployee.jobTitle) {
      alert('Please fill in both name and job title');
      return;
    }
    
    try {
      // Update local state
      setLaborBurdenEmployees(laborBurdenEmployees.map(emp => 
        emp.id === editingLaborBurdenEmployee.id ? editingLaborBurdenEmployee : emp
      ));
      
      // Save to Firebase
      await saveLaborBurdenData();
      
      setShowLaborBurdenEditModal(false);
      setEditingLaborBurdenEmployee(null);
    } catch (error) {
      console.error('Error updating employee:', error);
    }
  };

  const handleRemoveLaborBurdenEmployee = async (id) => {
    try {
      // Update local state
      setLaborBurdenEmployees(laborBurdenEmployees.filter(emp => emp.id !== id));
      
      // Save to Firebase
      await saveLaborBurdenData();
    } catch (error) {
      console.error('Error removing employee:', error);
    }
  };

  const formatLaborBurdenCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatLaborBurdenCurrencyNoDecimals = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Calculate labor burden summary stats when employees change
  useEffect(() => {
    calculateLaborBurdenSummaryStats();
  }, [laborBurdenEmployees, laborBurdenWorkDays, laborBurdenPto, laborBurdenHolidays]);

  // Calculate hourly employee burden
  useEffect(() => {
    // Extra costs calculation
    const extraCosts = laborBurdenHourlyRate * (laborBurdenHourlyExtraCosts / 100);

    // Hourly burden
    const burden = laborBurdenHourlyRate + extraCosts;

    // Unbillable calculation
    const unbillable = (burden / (1 - laborBurdenUnbillablePercentage / 100)) - burden;

    // Days off calculation
    const daysOff = (burden / (1 - (laborBurdenPto / laborBurdenWorkDays))) - burden;

    // Holidays calculation
    const holidayAmount = (burden / (1 - (laborBurdenHolidays / laborBurdenWorkDays))) - burden;

    // Total hourly burden
    const totalBurden = burden + unbillable + daysOff + holidayAmount;
    setLaborBurdenTotalHourlyBurden(totalBurden);
    
    // Daily, weekly, monthly, annual rates
    setLaborBurdenHourlyDayRate(totalBurden * 8);
    setLaborBurdenHourlyWeekRate(totalBurden * 40);
    setLaborBurdenHourlyMonthRate((totalBurden * 40 * 52) / 12);
    setLaborBurdenHourlyAnnualRate(totalBurden * 40 * 52);
  }, [laborBurdenHourlyRate, laborBurdenHourlyExtraCosts, laborBurdenUnbillablePercentage, laborBurdenPto, laborBurdenHolidays, laborBurdenWorkDays]);

  // Calculate salaried employee burden
  useEffect(() => {
    let weekly, monthly, annual;
    
    // Convert salary to all timeframes based on input type
    if (laborBurdenSalaryType === 'weekly') {
      weekly = laborBurdenSalaryRate;
      monthly = weekly * 52 / 12;
      annual = weekly * 52;
    } else if (laborBurdenSalaryType === 'monthly') {
      monthly = laborBurdenSalaryRate;
      annual = monthly * 12;
      weekly = annual / 52;
    } else { // annual
      annual = laborBurdenSalaryRate;
      monthly = annual / 12;
      weekly = annual / 52;
    }
    
    // Calculate extra costs
    const extraCosts = {
      weekly: weekly * (laborBurdenSalaryExtraCosts / 100),
      monthly: monthly * (laborBurdenSalaryExtraCosts / 100),
      annual: annual * (laborBurdenSalaryExtraCosts / 100)
    };

    // Calculate total burden
    const totalBurden = {
      weekly: weekly + extraCosts.weekly,
      monthly: monthly + extraCosts.monthly,
      annual: annual + extraCosts.annual
    };
    setLaborBurdenTotalSalaryBurden(totalBurden);
    
    // Calculate hourly rate (based on 40-hour workweek)
    setLaborBurdenSalaryHourRate(totalBurden.annual / (laborBurdenWorkDays * 8));
    
    // Calculate daily rate
    setLaborBurdenSalaryDayRate(totalBurden.annual / laborBurdenWorkDays);
    
    // Weekly rate is already calculated
    setLaborBurdenSalaryWeekRate(totalBurden.weekly);
  }, [laborBurdenSalaryRate, laborBurdenSalaryType, laborBurdenSalaryExtraCosts, laborBurdenWorkDays]);

  // Auto-save labor burden data when it changes
  useEffect(() => {
    if (!loading && currentUser && selectedAccount) {
      const timeoutId = setTimeout(() => {
        saveLaborBurdenData();
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [laborBurdenEmployees, laborBurdenWorkDays, laborBurdenPto, laborBurdenHolidays, laborBurdenHourlyRate, laborBurdenHourlyExtraCosts, laborBurdenUnbillablePercentage, laborBurdenSalaryType, laborBurdenSalaryRate, laborBurdenSalaryExtraCosts, selectedAccount]);

  // Auto-save margin calculator data when it changes
  useEffect(() => {
    if (!loading && currentUser && selectedAccount) {
      const timeoutId = setTimeout(() => {
        saveMarginCalculatorData();
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [marginCalculatorInputs, selectedAccount]);

  // Helper functions for margin calculator
  const calculateMarginCalculatorScenario = (gpm, overhead, impliedEBT, taxRate) => {
    // Avoid division by zero or negative values
    if (gpm <= impliedEBT) {
      return {
        gpm,
        salesBreakeven: overhead / gpm,
        salesToProfitGoal: Infinity, // Cannot achieve profit goal
        annual: { 
          sales: 0, 
          gpm: 0, 
          grossProfit: 0, 
          overhead: overhead, 
          ebt: 0, 
          taxes: 0, 
          netProfit: 0, 
          profitMargin: 0 
        },
        quarterly: { sales: 0, grossProfit: 0, overhead: 0, ebt: 0, taxes: 0, netProfit: 0 },
        monthly: { sales: 0, grossProfit: 0, overhead: 0, ebt: 0, taxes: 0, netProfit: 0 },
      };
    }

    const salesBreakeven = overhead / gpm;
    const salesToProfitGoal = overhead / (gpm - impliedEBT);
    
    // Annual calculations
    const annualSales = salesToProfitGoal;
    const annualGrossProfit = annualSales * gpm;
    const annualEBT = annualGrossProfit - overhead;
    const annualTaxes = annualEBT * taxRate;
    const annualNetProfit = annualEBT - annualTaxes;
    const profitMargin = annualNetProfit / annualSales;
    
    // Quarterly and monthly are just divisions of annual
    const quarterly = {
      sales: annualSales / 4,
      grossProfit: annualGrossProfit / 4,
      overhead: overhead / 4,
      ebt: annualEBT / 4,
      taxes: annualTaxes / 4,
      netProfit: annualNetProfit / 4,
    };
    
    const monthly = {
      sales: annualSales / 12,
      grossProfit: annualGrossProfit / 12,
      overhead: overhead / 12,
      ebt: annualEBT / 12,
      taxes: annualTaxes / 12,
      netProfit: annualNetProfit / 12,
    };
    
    return {
      gpm,
      salesBreakeven,
      salesToProfitGoal,
      annual: {
        sales: annualSales,
        gpm,
        grossProfit: annualGrossProfit,
        overhead,
        ebt: annualEBT,
        taxes: annualTaxes,
        netProfit: annualNetProfit,
        profitMargin,
      },
      quarterly,
      monthly,
    };
  };

  const saveMarginCalculatorData = async () => {
    if (!currentUser || !selectedAccount) return;
    
    try {
      // Find the portal user for this account
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        console.log('No portal user found for this account');
        return;
      }
      
      const portalUserDoc = usersSnapshot.docs[0];
      const portalUserId = portalUserDoc.id;
      
      const dataToSave = {
        marginCalculator: {
          inputs: marginCalculatorInputs,
          lastCalculated: new Date().toISOString(),
          version: '1.0'
        }
      };
      
      const userDocRef = doc(db, 'users', portalUserId);
      await updateDoc(userDocRef, dataToSave);
      
      console.log('Margin calculator data saved for account:', selectedAccount.name);
    } catch (error) {
      console.error('Error saving margin calculator data:', error);
    }
  };

  const handleMarginCalculatorInputChange = (e) => {
    const { name, value } = e.target;
    let parsedValue = parseFloat(value);
    
    // Convert percentage inputs to decimal
    if (['taxRate', 'profitabilityDesire', 'startingGPM', 'trueGPM'].includes(name)) {
      parsedValue = parsedValue / 100;
    }
    
    setMarginCalculatorInputs(prev => ({ ...prev, [name]: parsedValue }));
  };

  const formatMarginCalculatorCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };
  
  const formatMarginCalculatorPercent = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  };

  // Calculate margin calculator scenarios when inputs change
  useEffect(() => {
    console.log('Margin calculator inputs changed:', marginCalculatorInputs);
    const impliedEBT = marginCalculatorInputs.profitabilityDesire / (1 - marginCalculatorInputs.taxRate);

    // Generate scenarios
    const newScenarios = [];
    const baseGPM = marginCalculatorInputs.startingGPM;
    const increment = 0.005; // Fixed at 0.5%

    // Generate 5 scenarios below and 5 above the starting GPM
    for (let i = -5; i <= 5; i++) {
      const gpm = baseGPM + (i * increment);
      if (gpm <= 0) continue; // Skip negative GPM
      
      const scenario = calculateMarginCalculatorScenario(gpm, marginCalculatorInputs.overhead, impliedEBT, marginCalculatorInputs.taxRate);
      newScenarios.push(scenario);
    }

    setMarginCalculatorScenarios(newScenarios);
  }, [marginCalculatorInputs]);



  // Generate chart data from user entries

  // Handle adding new GPM entry

  // Handle editing GPM entry

  // Handle updating GPM entry

  // Handle deleting GPM entry

  // Job Descriptions helper functions

  // SOPs helper functions

  // KPIs helper functions

  // KPI job functions

  // Technology department helper functions


  // Marketing leads handler functions
  const handleAddLeadsEntry = async (e) => {
    e.preventDefault();
    
    if (!leadsFormData.leadsCount || !leadsFormData.month || !leadsFormData.year) {
      alert('Please fill in all fields');
      return;
    }
    
    if (!currentUser) {
      alert('Please log in to add entries');
      return;
    }
    
    try {
      const newEntry = {
        id: Date.now(),
        count: parseInt(leadsFormData.leadsCount),
        month: leadsFormData.month,
        year: parseInt(leadsFormData.year),
        createdAt: new Date().toISOString()
      };
      
      // Update local state
      setLeadsEntries(prev => [...prev, newEntry]);
      
      // Save to Firestore
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        alert('Portal user not found for this account');
        return;
      }
      
      const portalUserDoc = usersSnapshot.docs[0];
      const portalUserRef = doc(db, 'users', portalUserDoc.id);
      
      const userDoc = await getDoc(portalUserRef);
      
      if (userDoc.exists()) {
        const currentLeads = userDoc.data().leadsEntries || [];
        const currentMarketingLeads = userDoc.data().marketingLeads || [];
        const currentSalesDataLeads = userDoc.data().salesData?.leads || [];
        
        await updateDoc(portalUserRef, {
          leadsEntries: [...currentLeads, newEntry],
          marketingLeads: [...currentMarketingLeads, newEntry],
          'salesData.leads': [...currentSalesDataLeads, newEntry]
        });
      } else {
        await setDoc(portalUserRef, {
          leadsEntries: [newEntry],
          marketingLeads: [newEntry],
          salesData: {
            leads: [newEntry]
          }
        });
      }
      
      // Close popup and reset form
      setShowLeadsAddPopup(false);
      setLeadsFormData({
        leadsCount: '',
        month: new Date().toLocaleString('default', { month: 'long' }),
        year: new Date().getFullYear().toString()
      });
    } catch (error) {
      console.error('Error adding entry:', error);
      alert('Error adding entry. Please try again.');
    }
  };

  const handleEditLeadsEntry = (entry) => {
    setEditingLeadsEntry(entry);
    setLeadsFormData({
      leadsCount: entry.count.toString(),
      month: entry.month,
      year: entry.year.toString()
    });
  };

  const handleUpdateLeadsEntry = async (e) => {
    e.preventDefault();
    
    if (!leadsFormData.leadsCount || !leadsFormData.month || !leadsFormData.year) {
      alert('Please fill in all fields');
      return;
    }
    
    if (!currentUser) {
      alert('Please log in to update entries');
      return;
    }
    
    try {
      const updatedEntry = {
        ...editingLeadsEntry,
        count: parseInt(leadsFormData.leadsCount),
        month: leadsFormData.month,
        year: parseInt(leadsFormData.year),
        updatedAt: new Date().toISOString()
      };
      
      // Update local state
      setLeadsEntries(prev => prev.map(entry => 
        entry.id === editingLeadsEntry.id ? updatedEntry : entry
      ));
      
      // Save to Firestore
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        alert('Portal user not found for this account');
        return;
      }
      
      const portalUserDoc = usersSnapshot.docs[0];
      const portalUserRef = doc(db, 'users', portalUserDoc.id);
      
      const userDoc = await getDoc(portalUserRef);
      
      if (userDoc.exists()) {
        const currentLeads = userDoc.data().leadsEntries || [];
        const currentMarketingLeads = userDoc.data().marketingLeads || [];
        const currentSalesDataLeads = userDoc.data().salesData?.leads || [];
        const updatedLeads = currentLeads.map(entry => 
          entry.id === editingLeadsEntry.id ? updatedEntry : entry
        );
        const updatedMarketingLeads = currentMarketingLeads.map(entry => 
          entry.id === editingLeadsEntry.id ? updatedEntry : entry
        );
        const updatedSalesDataLeads = currentSalesDataLeads.map(entry => 
          entry.id === editingLeadsEntry.id ? updatedEntry : entry
        );
        
        await updateDoc(portalUserRef, {
          leadsEntries: updatedLeads,
          marketingLeads: updatedMarketingLeads,
          'salesData.leads': updatedSalesDataLeads
        });
      }
      
      setEditingLeadsEntry(null);
      setLeadsFormData({
        leadsCount: '',
        month: new Date().toLocaleString('default', { month: 'long' }),
        year: new Date().getFullYear().toString()
      });
    } catch (error) {
      console.error('Error updating entry:', error);
      alert('Error updating entry. Please try again.');
    }
  };

  const handleDeleteLeadsEntry = async (entryId) => {
    if (!currentUser) {
      alert('Please log in to delete entries');
      return;
    }
    
    try {
      // Update local state
      setLeadsEntries(prev => prev.filter(entry => entry.id !== entryId));
      
      // Save to Firestore
      const usersSnapshot = await getAccountPortalUserSnapshot(selectedAccount);
      
      if (usersSnapshot.empty) {
        alert('Portal user not found for this account');
        return;
      }
      
      const portalUserDoc = usersSnapshot.docs[0];
      const portalUserRef = doc(db, 'users', portalUserDoc.id);
      
      const userDoc = await getDoc(portalUserRef);
      
      if (userDoc.exists()) {
        const currentLeads = userDoc.data().leadsEntries || [];
        const currentMarketingLeads = userDoc.data().marketingLeads || [];
        const currentSalesDataLeads = userDoc.data().salesData?.leads || [];
        const updatedLeads = currentLeads.filter(entry => entry.id !== entryId);
        const updatedMarketingLeads = currentMarketingLeads.filter(entry => entry.id !== entryId);
        const updatedSalesDataLeads = currentSalesDataLeads.filter(entry => entry.id !== entryId);
        
        await updateDoc(portalUserRef, {
          leadsEntries: updatedLeads,
          marketingLeads: updatedMarketingLeads,
          'salesData.leads': updatedSalesDataLeads
        });
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Error deleting entry. Please try again.');
    }
  };

  const filteredAccounts = accounts.filter((account) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    const contact = account.contact || {};
    const contactName =
      contact.name ||
      (contact.firstName && contact.lastName
        ? `${contact.firstName} ${contact.lastName}`
        : contact.firstName || contact.lastName || '');
    const company = String(account.company || '').toLowerCase();
    const name = String(contactName).toLowerCase();
    const team = (accountTeamNames[account.id] || []).join(' ').toLowerCase();
    return company.includes(q) || name.includes(q) || team.includes(q);
  });

  // Get pinned accounts
  const pinnedAccounts = accounts.filter(account => 
    pinnedAccountIds.includes(account.id)
  ).slice(0, 6); // Limit to 6

  // Pin/unpin account functions
  const handlePinAccount = async (accountId) => {
    if (!currentUser || !isInternal) return;
    
    if (pinnedAccountIds.length >= 6) {
      alert('You can only pin up to 6 accounts');
      return;
    }
    
    try {
      const newPinnedIds = [...pinnedAccountIds, accountId];
      setPinnedAccountIds(newPinnedIds);
      
      // Save to Firestore
      await updateDoc(doc(db, 'internal_users', currentUser.uid), {
        pinnedAccountIds: newPinnedIds
      });
    } catch (error) {
      console.error('Error pinning account:', error);
      // Revert on error
      setPinnedAccountIds(pinnedAccountIds);
      alert('Failed to pin account. Please try again.');
    }
  };

  const handleUnpinAccount = async (accountId, e) => {
    if (!currentUser || !isInternal) return;
    
    e?.stopPropagation(); // Prevent account selection when clicking unpin
    
    try {
      const newPinnedIds = pinnedAccountIds.filter(id => id !== accountId);
      setPinnedAccountIds(newPinnedIds);
      
      // Save to Firestore
      await updateDoc(doc(db, 'internal_users', currentUser.uid), {
        pinnedAccountIds: newPinnedIds
      });
    } catch (error) {
      console.error('Error unpinning account:', error);
      // Revert on error
      setPinnedAccountIds(pinnedAccountIds);
      alert('Failed to unpin account. Please try again.');
    }
  };

  // General — status pill colors shared by Questions and Fires.
  const generalStatusStyle = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'resolved' || s === 'completed' || s === 'closed') return 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400';
    if (s === 'in progress' || s === 'active') return 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
    if (s === 'pending' || s === 'open') return 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
    return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
  };

  const generalEmptyState = (Icon, title, subtitle) => (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col items-center justify-center py-14 px-6 text-center">
      <Icon className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
      <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{title}</p>
      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-sm">{subtitle}</p>
    </div>
  );

  // General — Roadmap overview grid (all steps as status-colored cards, no
  // current-step focus). Mirrors the portal Roadmap "Overview" tab, scoped to
  // the selected client's company roadmap.
  const renderRoadmapView = () => {
    if (generalExtrasLoading) return <div className="flex justify-center py-16"><BBALoader size={96} /></div>;
    if (!roadmapItems.length) {
      return generalEmptyState(MapIcon, `No roadmap for ${selectedAccount?.company || 'this account'} yet.`, "This client's roadmap steps will appear here once it's set up.");
    }
    const deptName = (dept) => {
      const d = String(dept || '').toLowerCase();
      const names = { sales: 'Sales', operations: 'Operations', finance: 'Finance', technology: 'Technology', marketing: 'Marketing', hr: 'HR', onboarding: 'Onboarding', general: 'General' };
      return names[d] || (dept ? dept.charAt(0).toUpperCase() + dept.slice(1) : 'General');
    };
    const deptPill = (dept) => {
      const map = {
        sales: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300',
        operations: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
        finance: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
        technology: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
        marketing: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
        hr: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
      };
      return map[String(dept || '').toLowerCase()] || 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300';
    };
    const cardTone = (status) => {
      const s = String(status || '').toLowerCase();
      if (s === 'in-progress' || s === 'in_progress' || s === 'active') return 'border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20';
      if (s === 'completed') return 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20';
      return 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/50';
    };
    const statusPill = (status) => {
      const s = String(status || '').toLowerCase();
      if (s === 'in-progress' || s === 'in_progress' || s === 'active') {
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"><PlayCircle className="w-3 h-3" />In Progress</span>;
      }
      if (s === 'completed') {
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"><CheckCircle2 className="w-3 h-3" />Complete</span>;
      }
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"><Lock className="w-3 h-3" />Upcoming</span>;
    };
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {roadmapItems.map((item, index) => (
          <div key={item.id || index} className={`rounded-xl border shadow-sm p-4 ${cardTone(item.status)}`}>
            <div className="text-center flex flex-col h-full">
              <div className="mb-3 mt-1">
                <span className="text-4xl">{item.emoji || getPortalEmoji(item.department)}</span>
              </div>
              <h3 className="font-semibold text-base text-gray-900 dark:text-white mb-4">{item.title}</h3>
              <div className="mt-auto flex items-center justify-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${deptPill(item.department)}`}>
                  {deptName(item.department)}
                </span>
                {statusPill(item.status)}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // CRM-style status dashboard for Questions/Fires: clickable status count cards
  // (toggle as a filter) + search box + status dropdown, then the filtered list.
  const renderStatusDashboard = ({
    items, statuses, search, setSearch, statusFilter, setStatusFilter,
    searchFields, renderCard, emptyTitle, emptySubtitle, noun,
  }) => {
    if (generalExtrasLoading) return <div className="flex justify-center py-16"><BBALoader size={96} /></div>;
    if (!items.length) return generalEmptyState(FaBook, emptyTitle, emptySubtitle);
    const norm = (s) => String(s || '').toLowerCase();
    const q = search.trim().toLowerCase();
    const filtered = items.filter((item) => {
      const matchesStatus = statusFilter === 'all' || norm(item.status) === norm(statusFilter);
      const matchesSearch = !q || searchFields(item).some((v) => String(v || '').toLowerCase().includes(q));
      return matchesStatus && matchesSearch;
    });
    const inputBase = 'rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white placeholder-gray-400';
    return (
      <div>
        {/* Status count cards — click to filter (click again to clear) */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
          {statuses.map((s) => {
            const count = items.filter((it) => norm(it.status) === norm(s.value)).length;
            const active = norm(statusFilter) === norm(s.value);
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => setStatusFilter(active ? 'all' : s.value)}
                className={`p-4 rounded-lg border text-center transition-all ${s.tile} ${active ? s.ring : ''}`}
              >
                <p className={`text-xs sm:text-sm font-semibold ${s.text}`}>{s.label}</p>
                <p className={`text-2xl sm:text-3xl font-bold ${s.count}`}>{count}</p>
              </button>
            );
          })}
        </div>
        {/* Search + status filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${noun}...`}
              className={`w-full pl-9 pr-3 py-2 ${inputBase}`}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`sm:w-48 px-3 py-2 ${inputBase}`}
          >
            <option value="all">All statuses</option>
            {statuses.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        {filtered.length === 0 ? (
          <p className="text-sm text-center py-10 text-gray-500 dark:text-gray-400">No {noun} match your search.</p>
        ) : (
          <div className="space-y-3">{filtered.map(renderCard)}</div>
        )}
      </div>
    );
  };

  const renderQuestionsView = () => renderStatusDashboard({
    items: generalQuestions,
    statuses: [
      { value: 'Open', label: 'Open', tile: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800', text: 'text-yellow-800 dark:text-yellow-300', count: 'text-yellow-600 dark:text-yellow-400', ring: 'ring-2 ring-yellow-400 dark:ring-yellow-500' },
      { value: 'In Progress', label: 'In Progress', tile: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800', text: 'text-orange-800 dark:text-orange-300', count: 'text-orange-600 dark:text-orange-400', ring: 'ring-2 ring-orange-400 dark:ring-orange-500' },
      { value: 'Resolved', label: 'Resolved', tile: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800', text: 'text-green-800 dark:text-green-300', count: 'text-green-600 dark:text-green-400', ring: 'ring-2 ring-green-400 dark:ring-green-500' },
    ],
    search: questionSearch, setSearch: setQuestionSearch,
    statusFilter: questionStatusFilter, setStatusFilter: setQuestionStatusFilter,
    searchFields: (q) => [q.questionText, q.createdByName, q.assigneeName],
    noun: 'questions',
    emptyTitle: `No questions from ${selectedAccount?.company || 'this account'}.`,
    emptySubtitle: 'Questions this client submits will appear here.',
    renderCard: (q) => (
      <div key={q.id} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-medium text-gray-900 dark:text-white">{q.questionText || 'Untitled question'}</p>
          <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${generalStatusStyle(q.status)}`}>{q.status || 'Open'}</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
          {q.createdByName && <span>Asked by {q.createdByName}</span>}
          {q.assigneeName && <span>Assigned to {q.assigneeName}</span>}
          {q.createdAt && <span>{new Date(q.createdAt).toLocaleDateString()}</span>}
        </div>
      </div>
    ),
  });

  const renderFiresView = () => renderStatusDashboard({
    items: generalFires,
    statuses: [
      { value: 'Active', label: 'Active', tile: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800', text: 'text-red-800 dark:text-red-300', count: 'text-red-600 dark:text-red-400', ring: 'ring-2 ring-red-400 dark:ring-red-500' },
      { value: 'Pending', label: 'Pending', tile: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800', text: 'text-yellow-800 dark:text-yellow-300', count: 'text-yellow-600 dark:text-yellow-400', ring: 'ring-2 ring-yellow-400 dark:ring-yellow-500' },
      { value: 'Resolved', label: 'Resolved', tile: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800', text: 'text-green-800 dark:text-green-300', count: 'text-green-600 dark:text-green-400', ring: 'ring-2 ring-green-400 dark:ring-green-500' },
    ],
    search: fireSearch, setSearch: setFireSearch,
    statusFilter: fireStatusFilter, setStatusFilter: setFireStatusFilter,
    searchFields: (f) => [f.name, f.description, f.createdByName],
    noun: 'fires',
    emptyTitle: `No fires for ${selectedAccount?.company || 'this account'}.`,
    emptySubtitle: 'Urgent issues (fires) raised for this client will appear here.',
    renderCard: (f) => (
      <div key={f.id} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
            <span aria-hidden="true">🔥</span>{f.name || 'Untitled fire'}
          </p>
          <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${generalStatusStyle(f.status)}`}>{f.status || 'Active'}</span>
        </div>
        {f.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{f.description}</p>}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
          {f.createdByName && <span>Reported by {f.createdByName}</span>}
          {f.dateReported && <span>{new Date(f.dateReported).toLocaleDateString()}</span>}
          {f.deadline && <span>Due {new Date(f.deadline).toLocaleDateString()}</span>}
        </div>
      </div>
    ),
  });

  const renderCollectionView = () => {
    if (generalExtrasLoading) return <div className="flex justify-center py-16"><BBALoader size={96} /></div>;
    if (!collectionDocs.length && !collectionCalls.length) {
      return generalEmptyState(FaBook, `Nothing in ${selectedAccount?.company || 'this account'}'s collection yet.`, 'Client documents and strategy-call recordings will appear here.');
    }
    const companyName = selectedAccount?.company || 'this client';
    const companyInitial = companyName.trim().charAt(0).toUpperCase() || '?';
    return (
      <div className="space-y-8">
        {collectionDocs.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Documents</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {collectionDocs.map((d) => (
                <button
                  key={d.id || d.fileName || d.url}
                  type="button"
                  onClick={() => d.url && setCollectionViewerDoc({ url: d.url, title: d.title || d.name || d.fileName || 'Document' })}
                  className="text-left bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 flex items-start gap-3 hover:border-red-300 dark:hover:border-red-700 transition-colors"
                >
                  <FileText className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{d.title || d.name || d.fileName || 'Document'}</p>
                    {d.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{d.description}</p>}
                    {d.department && <span className="inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{d.department}</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        {collectionCalls.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Call Recordings</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {collectionCalls.map((c) => {
                const hasSummary = !!(c.summary && String(c.summary).trim());
                return (
                  <div key={c.id} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 flex flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2 min-w-0">
                        <Camera className="w-4 h-4 text-red-500 shrink-0" />
                        <span className="truncate">{c.title || 'Strategy Call'}</span>
                      </p>
                      <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">{c.date ? new Date(c.date).toLocaleDateString() : ''}</span>
                    </div>
                    {/* Who it was with */}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="w-8 h-8 rounded-full bg-emerald-500 dark:bg-emerald-600 flex items-center justify-center text-white text-xs font-medium shrink-0">
                        {companyInitial}
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs text-gray-500 dark:text-gray-400">With</div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{companyName}</div>
                      </div>
                    </div>
                    {/* Meeting summary button */}
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      {hasSummary ? (
                        <button
                          type="button"
                          onClick={() => setCallSummary(c)}
                          className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700"
                        >
                          Meeting Summary
                          <FileText className="w-4 h-4 shrink-0" />
                        </button>
                      ) : (
                        <div aria-disabled="true" className="flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-gray-200 dark:bg-gray-700 px-4 py-2.5 text-sm font-semibold text-gray-500 dark:text-gray-400">
                          No Meeting Summary
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* In-page document viewer — same overlay the portal Sales/HR materials use */}
        <DocumentViewerModal
          isOpen={!!collectionViewerDoc}
          onClose={() => setCollectionViewerDoc(null)}
          url={collectionViewerDoc?.url}
          title={collectionViewerDoc?.title}
        />

        {/* Meeting summary modal */}
        {callSummary && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setCallSummary(null)}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
                <button type="button" onClick={() => setCallSummary(null)} className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                  <X className="w-5 h-5" />
                </button>
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mb-2">
                    <Camera className="w-5 h-5" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{callSummary.title || 'Strategy Call'}</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    With {companyName}{callSummary.date ? ` · ${new Date(callSummary.date).toLocaleDateString()}` : ''}
                  </p>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <MeetingSummaryContent markdown={callSummary.summary} />
                {Array.isArray(callSummary.actionItems) && callSummary.actionItems.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Action Items</h3>
                    <ul className="list-disc list-outside ml-5 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      {callSummary.actionItems.map((it, i) => (
                        <li key={i}>{typeof it === 'string' ? it : (it.text || it.title || it.description || '')}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {callSummary.recordingUrl && (
                  <a href={callSummary.recordingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-red-600 dark:text-red-400 hover:underline">
                    View recording
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCoursesView = () => {
    if (!courses.length) {
      return (
        <div className="text-center py-12">
          <FaBook className="text-6xl text-gray-400 mx-auto mb-6" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Courses Available</h3>
          <p className="text-gray-500 dark:text-gray-400">
            No courses are currently available for this account's program.
          </p>
        </div>
      );
    }

    // Calculate overall progress statistics
    const totalCourses = courses.length;
    const completedCourses = courses.filter(course => {
      const progress = courseProgress.find(p => p.courseId === course.id);
      return progress && progress.progressPercentage === 100;
    }).length;
    const inProgressCourses = courses.filter(course => {
      const progress = courseProgress.find(p => p.courseId === course.id);
      return progress && progress.progressPercentage > 0 && progress.progressPercentage < 100;
    }).length;
    const notStartedCourses = totalCourses - completedCourses - inProgressCourses;

    return (
      <div className="space-y-6">
        {/* Progress Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
            <div className="flex flex-col items-center text-center">
              <FaBook className="h-8 w-8 text-green-600 dark:text-green-400 mb-2" />
              <p className="text-sm font-medium text-green-800 dark:text-green-200">Completed</p>
              <p className="text-2xl font-bold text-green-900 dark:text-green-100">{completedCourses}</p>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <div className="flex flex-col items-center text-center">
              <FaClock className="h-8 w-8 text-blue-600 dark:text-blue-400 mb-2" />
              <p className="text-sm font-medium text-blue-800 dark:text-blue-200">In Progress</p>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{inProgressCourses}</p>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-center text-center">
              <FaBook className="h-8 w-8 text-gray-600 dark:text-gray-400 mb-2" />
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Not Started</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{notStartedCourses}</p>
            </div>
          </div>
        </div>

        {/* All courses with per-course progress — completed (green) and
            in-progress (blue) sorted to the top, not-started last. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses
            .map((course) => ({
              course,
              pct: courseProgress.find((p) => p.courseId === course.id)?.progressPercentage ?? 0,
            }))
            .sort((a, b) => {
              // rank: completed 0, in-progress 1, not-started 2
              const rank = (pct) => (pct === 100 ? 0 : pct > 0 ? 1 : 2);
              return rank(a.pct) - rank(b.pct);
            })
            .map(({ course, pct }) => {
            const cardTone =
              pct === 100
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : pct > 0
                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700';
            return (
              <div
                key={course.id}
                className={`${cardTone} rounded-lg border shadow-sm overflow-hidden flex flex-col`}
              >
                {course.thumbnailUrl ? (
                  <img src={course.thumbnailUrl} alt={course.title} className="w-full h-32 object-cover" />
                ) : (
                  <div className="w-full h-32 flex items-center justify-center bg-gray-100 dark:bg-gray-700/50">
                    <FaBook className="h-8 w-8 text-gray-400" />
                  </div>
                )}
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2">{course.title}</h3>
                  {course.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{course.description}</p>
                  )}
                  <div className="mt-auto pt-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Progress</span>
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">{pct}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTasksView = () => {
    const filteredTasks = tasks.filter(task => {
      if (taskFilter === 'todo') return !task.completed;
      if (taskFilter === 'done') return task.completed;
      return false;
    });

    // Calculate pagination (accumulating "Load More" style, like the portal tasks page)
    const totalTasks = filteredTasks.length;
    const endIndex = currentPage * tasksPerPage;
    const paginatedTasks = filteredTasks.slice(0, endIndex);
    const hasNextPage = endIndex < totalTasks;

    const taskTabs = [
      {
        id: 'todo',
        label: 'To Do',
        icon: ListTodo,
        count: tasks.filter(t => !t.completed).length,
        tile: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
        countText: 'text-amber-600 dark:text-amber-400',
        ring: 'ring-2 ring-amber-400 dark:ring-amber-500'
      },
      {
        id: 'done',
        label: 'Done',
        icon: CheckCircle2,
        count: tasks.filter(t => t.completed).length,
        tile: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
        countText: 'text-emerald-600 dark:text-emerald-400',
        ring: 'ring-2 ring-emerald-400 dark:ring-emerald-500'
      }
    ];


    const formatDate = (date) => {
      if (!date) return 'No date';
      try {
        // Handle Firestore Timestamp
        if (date && typeof date === 'object' && date.toDate) {
          return date.toDate().toLocaleDateString();
        }
        // Handle Firestore Timestamp with seconds/nanoseconds
        if (date && typeof date === 'object' && date.seconds) {
          return new Date(date.seconds * 1000).toLocaleDateString();
        }
        // Handle regular Date objects
        if (date instanceof Date) {
          return date.toLocaleDateString();
        }
        // Handle ISO strings
        const taskDate = new Date(date);
        if (isNaN(taskDate.getTime())) {
          return 'No date';
        }
        return taskDate.toLocaleDateString();
      } catch (error) {
        console.error('Error formatting date:', error, date);
        return 'No date';
      }
    };

    const isOverdue = (dueDate, isCompleted) => {
      if (!dueDate || isCompleted) return false;
      try {
        let taskDate;
        // Handle Firestore Timestamp
        if (dueDate && typeof dueDate === 'object' && dueDate.toDate) {
          taskDate = dueDate.toDate();
        } else if (dueDate && typeof dueDate === 'object' && dueDate.seconds) {
          taskDate = new Date(dueDate.seconds * 1000);
        } else if (dueDate instanceof Date) {
          taskDate = dueDate;
        } else {
          taskDate = new Date(dueDate);
        }
        if (isNaN(taskDate.getTime())) return false;
        return taskDate < new Date();
      } catch (error) {
        return false;
      }
    };

    const renderTaskCreatorName = (createdBy) => {
      if (!createdBy) return 'User';
      if (createdBy === 'Internal') return 'Internal';

      const internalUser = internalUsers.find(u => u.id === createdBy);
      if (internalUser) {
        if (internalUser.firstName && internalUser.lastName) {
          return `${internalUser.firstName} ${internalUser.lastName}`;
        }
        if (internalUser.firstName) return internalUser.firstName;
        if (internalUser.email) return internalUser.email.split('@')[0];
      }

      if (portalUser && portalUser.id === createdBy) {
        if (portalUser.firstName && portalUser.lastName) {
          return `${portalUser.firstName} ${portalUser.lastName}`;
        }
        if (portalUser.firstName) return portalUser.firstName;
      }

      if (selectedAccount?.contact?.name) return selectedAccount.contact.name;
      if (selectedAccount?.contact?.firstName && selectedAccount?.contact?.lastName) {
        return `${selectedAccount.contact.firstName} ${selectedAccount.contact.lastName}`;
      }
      if (selectedAccount?.company) return selectedAccount.company;

      return 'User';
    };

    return (
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header — create a task on the client's behalf */}
        <div className="relative flex items-center justify-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Tasks</h2>
          <AddButton
            onClick={openNewTaskModal}
            className="absolute right-0"
            title="Create a task for this client"
          />
        </div>

        {/* To Do / Done stat-card tabs */}
        <div className="grid grid-cols-2 gap-4">
          {taskTabs.map(tab => {
            const Icon = tab.icon;
            const active = taskFilter === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTaskFilterChange(tab.id)}
                className={`group rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm text-left transition-all hover:shadow-md hover:-translate-y-0.5 p-4 ${active ? tab.ring : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${tab.tile}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-2xl font-bold leading-none ${
                      tab.count === 0 ? 'text-gray-300 dark:text-gray-600' : tab.countText
                    }`}>
                      {tab.count}
                    </p>
                    <p className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400 truncate">{tab.label}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Tasks List */}
        {paginatedTasks.length > 0 ? (
          <div className="space-y-2">
            {paginatedTasks.map(task => {
              const isTaskOverdue = isOverdue(task.dueDate, task.completed);

              return (
                <div
                  key={task.id}
                  className="p-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => handleToggleTaskCompleted(task)}
                      disabled={togglingTaskIds.includes(task.id)}
                      title={task.completed ? 'Mark as not done' : 'Mark as done'}
                      aria-label={task.completed ? 'Mark as not done' : 'Mark as done'}
                      className={`w-5 h-5 mt-0.5 rounded-full border flex items-center justify-center shrink-0 transition-colors disabled:opacity-50 ${
                        task.completed
                          ? 'bg-emerald-500 border-emerald-500 hover:bg-emerald-600'
                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/30'
                      }`}
                    >
                      {task.completed && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </button>

                    <div className="flex-1 min-w-0">
                      <h3 className={`text-sm font-medium ${task.completed ? 'line-through opacity-60' : ''} text-gray-900 dark:text-gray-100`}>
                        {task.title}
                      </h3>
                      {task.description && (
                        <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                          {task.description}
                        </p>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                        <span className="inline-flex items-center gap-1 min-w-0">
                          <span className="truncate">Created by {renderTaskCreatorName(task.createdBy)}</span>
                        </span>
                      </div>
                    </div>

                    {task.dueDate && (
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                          isTaskOverdue
                            ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-300'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                        }`}>
                          <CalendarIcon className="w-3 h-3" />
                          {formatDate(task.dueDate)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Load More */}
            {hasNextPage && (
              <div className="flex justify-center pt-4">
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Load More ({totalTasks - paginatedTasks.length} remaining)
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col items-center justify-center py-14 px-6 text-center">
            <ClipboardList className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
              {tasks.length === 0
                ? `No tasks are currently assigned to ${selectedAccount?.company || 'this account'}.`
                : `No ${taskFilter === 'todo' ? 'pending' : 'completed'} tasks`}
            </p>
            {taskFilter === 'todo' && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-sm">
                All caught up. Use the + button above to create a task for this client.
              </p>
            )}
          </div>
        )}

        {/* New Task modal */}
        <Modal isOpen={isNewTaskModalOpen} onClose={closeNewTaskModal}>
          <div className="flex items-center justify-center mb-4">
            <FaPlus className="text-red-600 mr-3 text-2xl" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">New Task</h2>
          </div>
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            Assigned to everyone on {selectedAccount?.company || 'this account'}'s portal.
          </p>

          <form onSubmit={handleCreateAccountTask}>
            <div className="mb-4">
              <label htmlFor="portalViewTaskTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Title *
              </label>
              <input
                type="text"
                id="portalViewTaskTitle"
                value={newTaskForm.title}
                onChange={(e) => setNewTaskForm(prev => ({ ...prev, title: e.target.value }))}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                placeholder="e.g., Upload last month's P&L"
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="portalViewTaskDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                id="portalViewTaskDescription"
                rows="3"
                value={newTaskForm.description}
                onChange={(e) => setNewTaskForm(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                placeholder="Add any detail the client needs to complete this"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="portalViewTaskDueDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Due Date
              </label>
              <input
                type="date"
                id="portalViewTaskDueDate"
                value={newTaskForm.dueDate}
                onChange={(e) => setNewTaskForm(prev => ({ ...prev, dueDate: e.target.value }))}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeNewTaskModal}
                disabled={creatingTask}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creatingTask || !newTaskForm.title.trim()}
                className="px-4 py-2 text-sm font-medium rounded-md bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                {creatingTask ? 'Creating…' : 'Create Task'}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    );
  };

  const renderOperationsGoalsView = () => {
    if (operationsGoalsLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <BBALoader size={48} className="mx-auto mb-4" />
            <p className="text-gray-500">Loading operations goals...</p>
          </div>
        </div>
      );
    }

    if (!operationsGoals || operationsGoals.length === 0) {
      return (
        <div className="text-center py-12">
          <FaBullseye className="text-4xl text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Operations Goals Set</h3>
          <p className="text-gray-500 dark:text-gray-400">
            This account hasn't set any operations goals yet. Goals can be managed from the portal.
          </p>
        </div>
      );
    }

    const formatValue = (value, unit) => {
      switch (unit) {
        case 'usd':
          return `$${Number(value).toLocaleString()}`;
        case 'percent':
          return `${value}%`;
        case 'seconds':
          return `${value}s`;
        case 'days':
          return `${value} days`;
        case 'months':
          return `${value} months`;
        default:
          return Number(value).toLocaleString();
      }
    };

    const calculateProgress = (actual, target) => {
      if (target === 0) return 0;
      return Math.min(100, Math.round((actual / target) * 100));
    };

    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-center relative mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Operations Goals
            </h3>
            <button
              onClick={openAddOperationsGoalModal}
              className="absolute right-0 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow-lg transition-all duration-200 transform hover:scale-105"
              title="Add New Goal"
            >
              <FaPlus className="w-4 h-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {operationsGoals.map(goal => {
              const progress = calculateProgress(goal.actual, goal.target);
              
              return (
                <div 
                  key={goal.id} 
                  className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4 border border-gray-200 dark:border-gray-600 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => openOperationsGoalsModal(goal)}
                >
                  <div className="flex justify-center items-start mb-4">
                    <div className="text-center">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{goal.metricName}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{goal.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {/* Target */}
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900 dark:text-white">
                        {formatValue(goal.target, goal.unit)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Target</div>
                    </div>
                    
                    {/* Current */}
                    <div className="text-center">
                      <div className="text-xl font-bold text-red-600 dark:text-red-400">
                        {formatValue(goal.actual, goal.unit)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Current</div>
                    </div>
                    
                    {/* Progress */}
                    <div className="text-center">
                      <div className={`text-xl font-bold ${
                        progress >= 100 ? 'text-green-600 dark:text-green-400' : 
                        progress >= 70 ? 'text-yellow-600 dark:text-yellow-400' : 
                        'text-red-600 dark:text-red-400'
                      }`}>
                        {progress}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Complete</div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-300 ${
                        progress >= 90 ? 'bg-green-500 dark:bg-green-400' :
                        progress >= 70 ? 'bg-yellow-500 dark:bg-yellow-400' :
                        'bg-red-500 dark:bg-red-400'
                      }`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Operations Goals Modal */}
        {selectedGoal && (
          <Modal isOpen={isOperationsGoalsModalOpen} onClose={closeOperationsGoalsModal}>
            <div className="flex items-center justify-center mb-4">
              <FaBullseye className="text-red-600 mr-3 text-2xl" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedGoal.metricName}</h2>
            </div>
            
            {/* Toggle buttons */}
            <div className="flex mb-4">
              <button
                type="button"
                onClick={() => setIsUpdatingCurrent(true)}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-l-md border ${
                  isUpdatingCurrent
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Update Current
              </button>
              <button
                type="button"
                onClick={() => setIsUpdatingCurrent(false)}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-r-md border-t border-r border-b ${
                  !isUpdatingCurrent
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Update Target
              </button>
            </div>

            {!isUpdatingCurrent ? (
              <form onSubmit={handleOperationsTargetSubmit}>
                <div className="mb-4">
                  <label htmlFor="newTarget" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    New Target ({selectedGoal.unit})
                  </label>
                  <input
                    type="text"
                    id="newTarget"
                    value={newTarget}
                    onChange={handleTargetChange}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex justify-between">
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleEditOperationsGoal(selectedGoal)}
                      className="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded-md shadow-sm hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    >
                      <FaEdit className="w-4 h-4 inline-block mr-2" />
                      Edit
                    </button>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={closeOperationsGoalsModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700"
                    >
                      Set Target
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <form onSubmit={handleOperationsCurrentSubmit}>
                <div className="mb-4">
                  <label htmlFor="newCurrent" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    New Current Value ({selectedGoal.unit})
                  </label>
                  <input
                    type="text"
                    id="newCurrent"
                    value={newCurrent}
                    onChange={handleCurrentChange}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={closeOperationsGoalsModal}
                    className="mr-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700"
                  >
                    Set Current
                  </button>
                </div>
              </form>
            )}
          </Modal>
        )}

        {/* Add Operations Goal Modal */}
        <Modal isOpen={isAddOperationsGoalModalOpen} onClose={closeAddOperationsGoalModal}>
          <div className="flex items-center justify-center mb-4">
            <FaPlus className="text-green-600 mr-3 text-2xl" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Add New Goal</h2>
          </div>
          
          <form onSubmit={handleAddOperationsGoal}>
            <div className="mb-4">
              <label htmlFor="department" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Department *
              </label>
              <select
                id="department"
                value={newOperationsGoal.department}
                onChange={(e) => handleNewOperationsGoalChange('department', e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                required
              >
                <option value="Sales">Sales</option>
                <option value="Operations">Operations</option>
                <option value="Technology">Technology</option>
                <option value="Finance">Finance</option>
                <option value="Marketing">Marketing</option>
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="metricName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Goal Name *
              </label>
              <input
                type="text"
                id="metricName"
                value={newOperationsGoal.metricName}
                onChange={(e) => handleNewOperationsGoalChange('metricName', e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                placeholder="e.g., Customer Satisfaction Score"
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description *
              </label>
              <textarea
                id="description"
                value={newOperationsGoal.description}
                onChange={(e) => handleNewOperationsGoalChange('description', e.target.value)}
                rows="3"
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                placeholder="Describe what this goal measures and why it's important"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="target" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Target Value
                </label>
                <input
                  type="number"
                  id="target"
                  value={newOperationsGoal.target}
                  onChange={(e) => handleNewOperationsGoalChange('target', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label htmlFor="actual" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Current Value
                </label>
                <input
                  type="number"
                  id="actual"
                  value={newOperationsGoal.actual}
                  onChange={(e) => handleNewOperationsGoalChange('actual', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="unit" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Unit
              </label>
              <select
                id="unit"
                value={newOperationsGoal.unit}
                onChange={(e) => handleNewOperationsGoalChange('unit', e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
              >
                <option value="usd">USD ($)</option>
                <option value="percent">Percentage (%)</option>
                <option value="number">Number</option>
                <option value="days">Days</option>
                <option value="months">Months</option>
              </select>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={closeAddOperationsGoalModal}
                className="mr-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700"
              >
                Add Goal
              </button>
            </div>
          </form>
        </Modal>

        {/* Edit Operations Goal Modal */}
        {editingOperationsGoal && (
          <Modal isOpen={isEditOperationsGoalModalOpen} onClose={closeEditOperationsGoalModal}>
            <div className="flex items-center justify-center mb-4">
              <FaEdit className="text-blue-600 mr-3 text-2xl" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Goal</h2>
            </div>
            
            <form onSubmit={handleSaveOperationsGoalEdit}>
              <div className="mb-4">
                <label htmlFor="editDepartment" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Department *
                </label>
                <select
                  id="editDepartment"
                  value={editingOperationsGoal.department}
                  onChange={(e) => handleEditOperationsGoalChange('department', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  required
                >
                  <option value="Sales">Sales</option>
                  <option value="Operations">Operations</option>
                  <option value="Technology">Technology</option>
                  <option value="Finance">Finance</option>
                  <option value="Marketing">Marketing</option>
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor="editMetricName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Goal Name *
                </label>
                <input
                  type="text"
                  id="editMetricName"
                  value={editingOperationsGoal.metricName}
                  onChange={(e) => handleEditOperationsGoalChange('metricName', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="e.g., Customer Satisfaction Score"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="editDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description *
                </label>
                <textarea
                  id="editDescription"
                  value={editingOperationsGoal.description}
                  onChange={(e) => handleEditOperationsGoalChange('description', e.target.value)}
                  rows="3"
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="Describe what this goal measures and why it's important"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="editTarget" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Target Value
                  </label>
                  <input
                    type="number"
                    id="editTarget"
                    value={editingOperationsGoal.target}
                    onChange={(e) => handleEditOperationsGoalChange('target', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label htmlFor="editActual" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Current Value
                  </label>
                  <input
                    type="number"
                    id="editActual"
                    value={editingOperationsGoal.actual}
                    onChange={(e) => handleEditOperationsGoalChange('actual', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="editUnit" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Unit
                </label>
                <select
                  id="editUnit"
                  value={editingOperationsGoal.unit}
                  onChange={(e) => handleEditOperationsGoalChange('unit', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                >
                  <option value="usd">USD ($)</option>
                  <option value="percent">Percentage (%)</option>
                  <option value="number">Number</option>
                  <option value="days">Days</option>
                  <option value="months">Months</option>
                </select>
              </div>

              <div className="flex justify-between">
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => handleDeleteOperationsGoal(editingOperationsGoal.id)}
                    className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 border border-red-300 dark:border-red-600 rounded-md shadow-sm hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <FaTrash className="w-4 h-4 inline-block mr-2" />
                    Delete
                  </button>
                </div>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={closeEditOperationsGoalModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </form>
          </Modal>
        )}
      </div>
    );
  };

  const renderFinanceGoalsView = () => {
    if (financeGoalsLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <BBALoader size={48} className="mx-auto mb-4" />
            <p className="text-gray-500">Loading finance goals...</p>
          </div>
        </div>
      );
    }

    if (!financeGoals || financeGoals.length === 0) {
      return (
        <div className="text-center py-12">
          <FaBullseye className="text-4xl text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Finance Goals Set</h3>
          <p className="text-gray-500 dark:text-gray-400">
            This account hasn't set any finance goals yet. Goals can be managed from the portal.
          </p>
        </div>
      );
    }

    const formatValue = (value, unit) => {
      switch (unit) {
        case 'usd':
          return `$${Number(value).toLocaleString()}`;
        case 'percent':
          return `${value}%`;
        case 'seconds':
          return `${value}s`;
        case 'days':
          return `${value} days`;
        case 'months':
          return `${value} months`;
        default:
          return Number(value).toLocaleString();
      }
    };

    const calculateProgress = (actual, target) => {
      if (target === 0) return 0;
      return Math.min(100, Math.round((actual / target) * 100));
    };

    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-center relative mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Finance Goals
            </h3>
            <button
              onClick={openAddFinanceGoalModal}
              className="absolute right-0 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow-lg transition-all duration-200 transform hover:scale-105"
              title="Add New Goal"
            >
              <FaPlus className="w-4 h-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {financeGoals.map(goal => {
              const progress = calculateProgress(goal.actual, goal.target);
              
              return (
                <div 
                  key={goal.id} 
                  className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4 border border-gray-200 dark:border-gray-600 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => openFinanceGoalsModal(goal)}
                >
                  <div className="flex justify-center items-start mb-4">
                    <div className="text-center">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{goal.metricName}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{goal.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {/* Target */}
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900 dark:text-white">
                        {formatValue(goal.target, goal.unit)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Target</div>
                    </div>
                    
                    {/* Current */}
                    <div className="text-center">
                      <div className="text-xl font-bold text-red-600 dark:text-red-400">
                        {formatValue(goal.actual, goal.unit)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Current</div>
                    </div>
                    
                    {/* Progress */}
                    <div className="text-center">
                      <div className={`text-xl font-bold ${
                        progress >= 100 ? 'text-green-600 dark:text-green-400' : 
                        progress >= 70 ? 'text-yellow-600 dark:text-yellow-400' : 
                        'text-red-600 dark:text-red-400'
                      }`}>
                        {progress}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Complete</div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-300 ${
                        progress >= 90 ? 'bg-green-500 dark:bg-green-400' :
                        progress >= 70 ? 'bg-yellow-500 dark:bg-yellow-400' :
                        'bg-red-500 dark:bg-red-400'
                      }`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Finance Goals Modal */}
        {selectedGoal && (
          <Modal isOpen={isFinanceGoalsModalOpen} onClose={closeFinanceGoalsModal}>
            <div className="flex items-center justify-center mb-4">
              <FaBullseye className="text-red-600 mr-3 text-2xl" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedGoal.metricName}</h2>
            </div>
            
            {/* Toggle buttons */}
            <div className="flex mb-4">
              <button
                type="button"
                onClick={() => setIsUpdatingCurrent(true)}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-l-md border ${
                  isUpdatingCurrent
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Update Current
              </button>
              <button
                type="button"
                onClick={() => setIsUpdatingCurrent(false)}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-r-md border-t border-r border-b ${
                  !isUpdatingCurrent
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Update Target
              </button>
            </div>

            {!isUpdatingCurrent ? (
              <form onSubmit={handleFinanceTargetSubmit}>
                <div className="mb-4">
                  <label htmlFor="newTarget" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    New Target ({selectedGoal.unit})
                  </label>
                  <input
                    type="text"
                    id="newTarget"
                    value={newTarget}
                    onChange={handleTargetChange}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex justify-between">
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleEditFinanceGoal(selectedGoal)}
                      className="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded-md shadow-sm hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    >
                      <FaEdit className="w-4 h-4 inline-block mr-2" />
                      Edit
                    </button>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={closeFinanceGoalsModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700"
                    >
                      Set Target
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <form onSubmit={handleFinanceCurrentSubmit}>
                <div className="mb-4">
                  <label htmlFor="newCurrent" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    New Current Value ({selectedGoal.unit})
                  </label>
                  <input
                    type="text"
                    id="newCurrent"
                    value={newCurrent}
                    onChange={handleCurrentChange}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={closeFinanceGoalsModal}
                    className="mr-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700"
                  >
                    Set Current
                  </button>
                </div>
              </form>
            )}
          </Modal>
        )}

        {/* Add Finance Goal Modal */}
        <Modal isOpen={isAddFinanceGoalModalOpen} onClose={closeAddFinanceGoalModal}>
          <div className="flex items-center justify-center mb-4">
            <FaPlus className="text-green-600 mr-3 text-2xl" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Add New Goal</h2>
          </div>
          
          <form onSubmit={handleAddFinanceGoal}>
            <div className="mb-4">
              <label htmlFor="department" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Department *
              </label>
              <select
                id="department"
                value={newFinanceGoal.department}
                onChange={(e) => handleNewFinanceGoalChange('department', e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                required
              >
                <option value="Sales">Sales</option>
                <option value="Operations">Operations</option>
                <option value="Technology">Technology</option>
                <option value="Finance">Finance</option>
                <option value="Marketing">Marketing</option>
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="metricName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Goal Name *
              </label>
              <input
                type="text"
                id="metricName"
                value={newFinanceGoal.metricName}
                onChange={(e) => handleNewFinanceGoalChange('metricName', e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                placeholder="e.g., Revenue Target, Profit Margin, Cash Flow"
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description *
              </label>
              <textarea
                id="description"
                value={newFinanceGoal.description}
                onChange={(e) => handleNewFinanceGoalChange('description', e.target.value)}
                rows="3"
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                placeholder="Describe what this goal measures and why it's important"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="target" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Target Value
                </label>
                <input
                  type="number"
                  id="target"
                  value={newFinanceGoal.target}
                  onChange={(e) => handleNewFinanceGoalChange('target', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label htmlFor="actual" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Current Value
                </label>
                <input
                  type="number"
                  id="actual"
                  value={newFinanceGoal.actual}
                  onChange={(e) => handleNewFinanceGoalChange('actual', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="unit" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Unit
              </label>
              <select
                id="unit"
                value={newFinanceGoal.unit}
                onChange={(e) => handleNewFinanceGoalChange('unit', e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
              >
                <option value="usd">USD ($)</option>
                <option value="percent">Percentage (%)</option>
                <option value="number">Number</option>
                <option value="days">Days</option>
                <option value="months">Months</option>
              </select>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={closeAddFinanceGoalModal}
                className="mr-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700"
              >
                Add Goal
              </button>
            </div>
          </form>
        </Modal>

        {/* Edit Finance Goal Modal */}
        {editingFinanceGoal && (
          <Modal isOpen={isEditFinanceGoalModalOpen} onClose={closeEditFinanceGoalModal}>
            <div className="flex items-center justify-center mb-4">
              <FaEdit className="text-blue-600 mr-3 text-2xl" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Goal</h2>
            </div>
            
            <form onSubmit={handleSaveFinanceGoalEdit}>
              <div className="mb-4">
                <label htmlFor="editDepartment" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Department *
                </label>
                <select
                  id="editDepartment"
                  value={editingFinanceGoal.department}
                  onChange={(e) => handleEditFinanceGoalChange('department', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  required
                >
                  <option value="Sales">Sales</option>
                  <option value="Operations">Operations</option>
                  <option value="Technology">Technology</option>
                  <option value="Finance">Finance</option>
                  <option value="Marketing">Marketing</option>
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor="editMetricName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Goal Name *
                </label>
                <input
                  type="text"
                  id="editMetricName"
                  value={editingFinanceGoal.metricName}
                  onChange={(e) => handleEditFinanceGoalChange('metricName', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="e.g., Revenue Target, Profit Margin, Cash Flow"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="editDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description *
                </label>
                <textarea
                  id="editDescription"
                  value={editingFinanceGoal.description}
                  onChange={(e) => handleEditFinanceGoalChange('description', e.target.value)}
                  rows="3"
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="Describe what this goal measures and why it's important"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="editTarget" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Target Value
                  </label>
                  <input
                    type="number"
                    id="editTarget"
                    value={editingFinanceGoal.target}
                    onChange={(e) => handleEditFinanceGoalChange('target', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label htmlFor="editActual" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Current Value
                  </label>
                  <input
                    type="number"
                    id="editActual"
                    value={editingFinanceGoal.actual}
                    onChange={(e) => handleEditFinanceGoalChange('actual', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="editUnit" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Unit
                </label>
                <select
                  id="editUnit"
                  value={editingFinanceGoal.unit}
                  onChange={(e) => handleEditFinanceGoalChange('unit', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                >
                  <option value="usd">USD ($)</option>
                  <option value="percent">Percentage (%)</option>
                  <option value="number">Number</option>
                  <option value="days">Days</option>
                  <option value="months">Months</option>
                </select>
              </div>

              <div className="flex justify-between">
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => handleDeleteFinanceGoal(editingFinanceGoal.id)}
                    className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 border border-red-300 dark:border-red-600 rounded-md shadow-sm hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <FaTrash className="w-4 h-4 inline-block mr-2" />
                    Delete
                  </button>
                </div>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={closeEditFinanceGoalModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </form>
          </Modal>
        )}
      </div>
    );
  };

  const renderMarketingGoalsView = () => {
    if (marketingGoalsLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <BBALoader size={48} className="mx-auto mb-4" />
            <p className="text-gray-500">Loading marketing goals...</p>
          </div>
        </div>
      );
    }

    if (!marketingGoals || marketingGoals.length === 0) {
      return (
        <div className="text-center py-12">
          <FaBullseye className="text-4xl text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Marketing Goals Set</h3>
          <p className="text-gray-500 dark:text-gray-400">
            This account hasn't set any marketing goals yet. Goals can be managed from the portal.
          </p>
        </div>
      );
    }

    const formatValue = (value, unit) => {
      switch (unit) {
        case 'usd':
          return `$${Number(value).toLocaleString()}`;
        case 'percent':
          return `${value}%`;
        case 'number':
          return Number(value).toLocaleString();
        case 'days':
          return `${value} days`;
        case 'months':
          return `${value} months`;
        default:
          return Number(value).toLocaleString();
      }
    };

    const calculateProgress = (actual, target) => {
      if (target === 0) return 0;
      return Math.min(100, Math.round((actual / target) * 100));
    };

    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-center relative mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Marketing Goals
            </h3>
            <button
              onClick={openAddMarketingGoalModal}
              className="absolute right-0 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow-lg transition-all duration-200 transform hover:scale-105"
              title="Add New Goal"
            >
              <FaPlus className="w-4 h-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {marketingGoals.map(goal => {
              const progress = calculateProgress(goal.actual, goal.target);
              
              return (
                <div 
                  key={goal.id} 
                  className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4 border border-gray-200 dark:border-gray-600 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => openMarketingGoalsModal(goal)}
                >
                  <div className="flex justify-center items-start mb-4">
                    <div className="text-center">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{goal.metricName}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{goal.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {/* Target */}
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900 dark:text-white">
                        {formatValue(goal.target, goal.unit)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Target</div>
                    </div>
                    
                    {/* Current */}
                    <div className="text-center">
                      <div className="text-xl font-bold text-red-600 dark:text-red-400">
                        {formatValue(goal.actual, goal.unit)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Current</div>
                    </div>
                    
                    {/* Progress */}
                    <div className="text-center">
                      <div className={`text-xl font-bold ${
                        progress >= 100 ? 'text-green-600 dark:text-green-400' : 
                        progress >= 70 ? 'text-yellow-600 dark:text-yellow-400' : 
                        'text-red-600 dark:text-red-400'
                      }`}>
                        {progress}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Complete</div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-300 ${
                        progress >= 90 ? 'bg-green-500 dark:bg-green-400' :
                        progress >= 70 ? 'bg-yellow-500 dark:bg-yellow-400' :
                        'bg-red-500 dark:bg-red-400'
                      }`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Marketing Goals Modal */}
        {selectedGoal && (
          <Modal isOpen={isMarketingGoalsModalOpen} onClose={closeMarketingGoalsModal}>
            <div className="flex items-center justify-center mb-4">
              <FaBullseye className="text-red-600 mr-3 text-2xl" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedGoal.metricName}</h2>
            </div>
            
            {/* Toggle buttons */}
            <div className="flex mb-4">
              <button
                type="button"
                onClick={() => setIsUpdatingCurrent(true)}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-l-md border ${
                  isUpdatingCurrent
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Update Current
              </button>
              <button
                type="button"
                onClick={() => setIsUpdatingCurrent(false)}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-r-md border-t border-r border-b ${
                  !isUpdatingCurrent
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Update Target
              </button>
            </div>

            {!isUpdatingCurrent ? (
              <form onSubmit={handleMarketingTargetSubmit}>
                <div className="mb-4">
                  <label htmlFor="newTarget" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    New Target ({selectedGoal.unit})
                  </label>
                  <input
                    type="text"
                    id="newTarget"
                    value={newTarget}
                    onChange={handleTargetChange}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex justify-between">
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleEditMarketingGoal(selectedGoal)}
                      className="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded-md shadow-sm hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    >
                      <FaEdit className="w-4 h-4 inline-block mr-2" />
                      Edit
                    </button>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={closeMarketingGoalsModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700"
                    >
                      Set Target
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <form onSubmit={handleMarketingCurrentSubmit}>
                <div className="mb-4">
                  <label htmlFor="newCurrent" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    New Current Value ({selectedGoal.unit})
                  </label>
                  <input
                    type="text"
                    id="newCurrent"
                    value={newCurrent}
                    onChange={handleCurrentChange}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={closeMarketingGoalsModal}
                    className="mr-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700"
                  >
                    Set Current
                  </button>
                </div>
              </form>
            )}
          </Modal>
        )}

        {/* Add Marketing Goal Modal */}
        <Modal isOpen={isAddMarketingGoalModalOpen} onClose={closeAddMarketingGoalModal}>
          <div className="flex items-center justify-center mb-4">
            <FaPlus className="text-green-600 mr-3 text-2xl" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Add New Goal</h2>
          </div>
          
          <form onSubmit={handleAddMarketingGoal}>
            <div className="mb-4">
              <label htmlFor="department" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Department *
              </label>
              <select
                id="department"
                value={newMarketingGoal.department}
                onChange={(e) => handleNewMarketingGoalChange('department', e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                required
              >
                <option value="Sales">Sales</option>
                <option value="Operations">Operations</option>
                <option value="Technology">Technology</option>
                <option value="Finance">Finance</option>
                <option value="Marketing">Marketing</option>
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="metricName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Goal Name *
              </label>
              <input
                type="text"
                id="metricName"
                value={newMarketingGoal.metricName}
                onChange={(e) => handleNewMarketingGoalChange('metricName', e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                placeholder="e.g., Lead Generation, Website Traffic, Social Media Engagement"
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description *
              </label>
              <textarea
                id="description"
                value={newMarketingGoal.description}
                onChange={(e) => handleNewMarketingGoalChange('description', e.target.value)}
                rows="3"
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                placeholder="Describe what this goal measures and why it's important"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="target" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Target Value
                </label>
                <input
                  type="number"
                  id="target"
                  value={newMarketingGoal.target}
                  onChange={(e) => handleNewMarketingGoalChange('target', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label htmlFor="actual" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Current Value
                </label>
                <input
                  type="number"
                  id="actual"
                  value={newMarketingGoal.actual}
                  onChange={(e) => handleNewMarketingGoalChange('actual', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="unit" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Unit
              </label>
              <select
                id="unit"
                value={newMarketingGoal.unit}
                onChange={(e) => handleNewMarketingGoalChange('unit', e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
              >
                <option value="number">Number</option>
                <option value="usd">USD ($)</option>
                <option value="percent">Percentage (%)</option>
                <option value="days">Days</option>
                <option value="months">Months</option>
              </select>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={closeAddMarketingGoalModal}
                className="mr-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700"
              >
                Add Goal
              </button>
            </div>
          </form>
        </Modal>

        {/* Edit Marketing Goal Modal */}
        {editingMarketingGoal && (
          <Modal isOpen={isEditMarketingGoalModalOpen} onClose={closeEditMarketingGoalModal}>
            <div className="flex items-center justify-center mb-4">
              <FaEdit className="text-blue-600 mr-3 text-2xl" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Goal</h2>
            </div>
            
            <form onSubmit={handleSaveMarketingGoalEdit}>
              <div className="mb-4">
                <label htmlFor="editDepartment" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Department *
                </label>
                <select
                  id="editDepartment"
                  value={editingMarketingGoal.department}
                  onChange={(e) => handleEditMarketingGoalChange('department', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  required
                >
                  <option value="Sales">Sales</option>
                  <option value="Operations">Operations</option>
                  <option value="Technology">Technology</option>
                  <option value="Finance">Finance</option>
                  <option value="Marketing">Marketing</option>
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor="editMetricName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Goal Name *
                </label>
                <input
                  type="text"
                  id="editMetricName"
                  value={editingMarketingGoal.metricName}
                  onChange={(e) => handleEditMarketingGoalChange('metricName', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="e.g., Lead Generation, Website Traffic, Social Media Engagement"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="editDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description *
                </label>
                <textarea
                  id="editDescription"
                  value={editingMarketingGoal.description}
                  onChange={(e) => handleEditMarketingGoalChange('description', e.target.value)}
                  rows="3"
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="Describe what this goal measures and why it's important"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="editTarget" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Target Value
                  </label>
                  <input
                    type="number"
                    id="editTarget"
                    value={editingMarketingGoal.target}
                    onChange={(e) => handleEditMarketingGoalChange('target', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label htmlFor="editActual" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Current Value
                  </label>
                  <input
                    type="number"
                    id="editActual"
                    value={editingMarketingGoal.actual}
                    onChange={(e) => handleEditMarketingGoalChange('actual', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="editUnit" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Unit
                </label>
                <select
                  id="editUnit"
                  value={editingMarketingGoal.unit}
                  onChange={(e) => handleEditMarketingGoalChange('unit', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
                >
                  <option value="number">Number</option>
                  <option value="usd">USD ($)</option>
                  <option value="percent">Percentage (%)</option>
                  <option value="days">Days</option>
                  <option value="months">Months</option>
                </select>
              </div>

              <div className="flex justify-between">
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => handleDeleteMarketingGoal(editingMarketingGoal.id)}
                    className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 border border-red-300 dark:border-red-600 rounded-md shadow-sm hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <FaTrash className="w-4 h-4 inline-block mr-2" />
                    Delete
                  </button>
                </div>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={closeEditMarketingGoalModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </form>
          </Modal>
        )}
      </div>
    );
  };

  const renderOverheadView = () => {
    return (
      <div className="space-y-6">
        {/* Header with Edit Controls */}
        <div className="flex justify-center items-center mb-6 relative">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Overhead Management</h2>
          <div className="absolute right-0 flex items-center gap-4">
            {overheadMessage && (
              <div className={`px-4 py-2 rounded ${overheadMessage.includes('Error') ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'}`}>
                {overheadMessage}
              </div>
            )}
            {!overheadEditing && (
              <button
                onClick={() => setOverheadEditing(true)}
                className="w-10 h-10 bg-gray-500 hover:bg-gray-600 text-white rounded-full flex items-center justify-center transition-colors"
                title="Edit Overhead"
              >
                <Edit2 className="h-4 w-4" />
              </button>
            )}
            {overheadEditing && (
              <button
                onClick={saveOverheadDataToFirebase}
                disabled={overheadSaving}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
              >
                {overheadSaving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="mb-8">
          {/* Time Period Tabs */}
          <div className="flex items-center justify-center mb-4">
            <div className="border-b-2 border-gray-200 dark:border-gray-700 flex">
              {['Annual', 'Quarterly', 'Monthly', 'Weekly'].map(period => (
                <button
                  key={period}
                  className={`px-8 py-2 font-medium ${overheadTimePeriod === period ? 'text-red-500 border-b-2 border-red-500 -mb-0.5' : (darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700')} transition-colors`}
                  onClick={() => setOverheadTimePeriod(period)}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 flex flex-col items-center justify-center">
              <div className="text-gray-600 dark:text-gray-400 mb-1">Fixed Overhead</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                ${overheadSummary.fixedOverhead.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 flex flex-col items-center justify-center">
              <div className="text-gray-600 dark:text-gray-400 mb-1">Soft Overhead</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                ${overheadSummary.softOverhead.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 flex flex-col items-center justify-center">
              <div className="text-gray-600 dark:text-gray-400 mb-1">Total Overhead</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                ${overheadSummary.totalOverhead.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Fixed Overhead Section Title */}
        <div className="text-center mb-6 mt-10">
          <h2 className="text-xl font-bold text-gray-700 dark:text-white inline-block px-6 py-2 border-b-2 border-gray-300 dark:border-gray-600">Fixed Overhead</h2>
        </div>
        
        {/* Fixed Overhead Categories */}
        {['Operational Overhead', 'Operation Payroll', 'Asset Payments', 'Subscriptions', 'Insurance'].map(category => (
          <OverheadCategorySection
            key={category}
            title={category}
            expenses={overheadData.expenses?.[category] || []}
            calculateAdjustedAmount={calculateAdjustedAmount}
            timePeriod={overheadTimePeriod}
            isFixedOverhead={true}
            summary={overheadSummary}
            darkMode={darkMode}
            editing={overheadEditing}
            addExpense={() => addOverheadExpense(category)}
            updateExpense={(index, field, value) => updateOverheadExpense(category, index, field, value)}
            removeExpense={(index) => removeOverheadExpense(category, index)}
          />
        ))}
        
        {/* Soft Overhead Section Title */}
        <div className="text-center mb-6 mt-10">
          <h2 className="text-xl font-bold text-gray-700 dark:text-white inline-block px-6 py-2 border-b-2 border-gray-300 dark:border-gray-600">Soft Overhead</h2>
        </div>
        
        {/* Soft Overhead Categories */}
        {['Debt Service', 'Soft Debt'].map(category => (
          <OverheadCategorySection
            key={category}
            title={category}
            expenses={overheadData.expenses?.[category] || []}
            calculateAdjustedAmount={calculateAdjustedAmount}
            timePeriod={overheadTimePeriod}
            isFixedOverhead={false}
            summary={overheadSummary}
            darkMode={darkMode}
            editing={overheadEditing}
            addExpense={() => addOverheadExpense(category)}
            updateExpense={(index, field, value) => updateOverheadExpense(category, index, field, value)}
            removeExpense={(index) => removeOverheadExpense(category, index)}
          />
        ))}

        {/* No Data Message */}
        {Object.values(overheadData.expenses || {}).every(expenses => !expenses || expenses.length === 0) && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-sm border border-gray-200 dark:border-gray-800 text-center">
            <FaCalculator className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Overhead Data</h3>
            <p className="text-gray-500 dark:text-gray-400">
              This account doesn't have any overhead data configured yet.
            </p>
          </div>
        )}
      </div>
    );
  };


  const renderGpmView = () => {
    // For Finance department, show Margin Calculator content instead of GPM chart
    return renderMarginCalculatorView();
  };















  const renderLaborBurdenView = () => {
    const filteredLaborBurdenEmployees = laborBurdenEmployees.filter(employee =>
      employee.name.toLowerCase().includes(laborBurdenSearchQuery.toLowerCase())
    );

    return (
      <div className="space-y-6">
        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setLaborBurdenActiveTab('myCompany')}
                className={`${
                  laborBurdenActiveTab === 'myCompany'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                My Company
              </button>
              <button
                onClick={() => setLaborBurdenActiveTab('comparison')}
                className={`${
                  laborBurdenActiveTab === 'comparison'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Comparison
              </button>
            </nav>
          </div>
        </div>

        {/* My Company Tab Content */}
        {laborBurdenActiveTab === 'myCompany' && (
          <div>
            {/* Summary Statistics */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-800 mb-8">
              <h2 className="text-xl font-semibold mb-4 border-b pb-2 text-center text-gray-900 dark:text-white border-gray-200 dark:border-gray-600">Company Summary</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <div className="bg-gradient-to-b from-red-50 to-white border border-red-100 dark:bg-gradient-to-b dark:from-red-900/30 dark:to-gray-700 dark:border-red-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                  <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Per Hour</div>
                  <div className="font-bold text-lg text-red-700 dark:text-red-300">{formatLaborBurdenCurrency(laborBurdenSummaryStats.hourly)}</div>
                </div>
                <div className="bg-gradient-to-b from-red-50 to-white border border-red-100 dark:bg-gradient-to-b dark:from-red-900/30 dark:to-gray-700 dark:border-red-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                  <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Per Day</div>
                  <div className="font-bold text-lg text-red-700 dark:text-red-300">{formatLaborBurdenCurrency(laborBurdenSummaryStats.daily)}</div>
                </div>
                <div className="bg-gradient-to-b from-red-50 to-white border border-red-100 dark:bg-gradient-to-b dark:from-red-900/30 dark:to-gray-700 dark:border-red-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                  <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Weekly</div>
                  <div className="font-bold text-lg text-red-700 dark:text-red-300">{formatLaborBurdenCurrencyNoDecimals(laborBurdenSummaryStats.weekly)}</div>
                </div>
                <div className="bg-gradient-to-b from-red-50 to-white border border-red-100 dark:bg-gradient-to-b dark:from-red-900/30 dark:to-gray-700 dark:border-red-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                  <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Monthly</div>
                  <div className="font-bold text-lg text-red-700 dark:text-red-300">{formatLaborBurdenCurrencyNoDecimals(laborBurdenSummaryStats.monthly)}</div>
                </div>
                <div className="bg-gradient-to-b from-red-50 to-white border border-red-100 dark:bg-gradient-to-b dark:from-red-900/30 dark:to-gray-700 dark:border-red-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                  <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Annual</div>
                  <div className="font-bold text-lg text-red-700 dark:text-red-300">{formatLaborBurdenCurrencyNoDecimals(laborBurdenSummaryStats.annual)}</div>
                </div>
              </div>
            </div>

            {/* Add Employee Button and Search Bar */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex-1 mr-4">
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={laborBurdenSearchQuery}
                  onChange={(e) => setLaborBurdenSearchQuery(e.target.value)}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-red-200 focus:border-red-400 transition-colors bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
              <button
                onClick={() => setShowLaborBurdenAddModal(true)}
                className="p-2 rounded-full bg-red-600 hover:bg-red-700 text-white"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>

            {/* Employee Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredLaborBurdenEmployees.map((employee) => {
                const costs = calculateLaborBurdenEmployeeCosts(employee);
                return (
                  <div 
                    key={employee.id} 
                    className="bg-white dark:bg-gray-700 p-4 rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow flex flex-col items-center text-center"
                    onClick={() => {
                      setEditingLaborBurdenEmployee({ ...employee });
                      setShowLaborBurdenEditModal(true);
                    }}
                  >
                    <h3 className="text-lg font-semibold mb-2 text-gray-700 dark:text-white">{employee.name}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full mb-3 ${
                      employee.type === 'hourly' 
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }`}>
                      {employee.type === 'hourly' ? 'Hourly' : 'Salaried'}
                    </span>
                    <div className="text-sm mb-1 text-gray-500 dark:text-gray-400">Annual Cost</div>
                    <div className="font-medium text-gray-700 dark:text-white">{formatLaborBurdenCurrencyNoDecimals(costs.annual)}</div>
                  </div>
                );
              })}
            </div>

            {/* Add Employee Modal */}
            {showLaborBurdenAddModal && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-md">
                  <h3 className="text-lg font-semibold mb-4 text-center text-gray-900 dark:text-white">Add New Employee</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Name</label>
                      <input
                        type="text"
                        value={newLaborBurdenEmployee.name}
                        onChange={(e) => setNewLaborBurdenEmployee({ ...newLaborBurdenEmployee, name: e.target.value })}
                        className="w-full p-2 border rounded-xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                        placeholder="Employee Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Job Title</label>
                      <input
                        type="text"
                        value={newLaborBurdenEmployee.jobTitle}
                        onChange={(e) => setNewLaborBurdenEmployee({ ...newLaborBurdenEmployee, jobTitle: e.target.value })}
                        className="w-full p-2 border rounded-xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                        placeholder="Job Title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Employee Type</label>
                      <select
                        value={newLaborBurdenEmployee.type}
                        onChange={(e) => setNewLaborBurdenEmployee({ ...newLaborBurdenEmployee, type: e.target.value })}
                        className="w-full p-2 border rounded-xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                      >
                        <option value="hourly">Hourly</option>
                        <option value="salaried">Salaried</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">
                        Rate ({newLaborBurdenEmployee.type === 'hourly' ? 'Hourly' : 'Annual'})
                      </label>
                      <input
                        type="number"
                        value={newLaborBurdenEmployee.rate}
                        onChange={(e) => setNewLaborBurdenEmployee({ ...newLaborBurdenEmployee, rate: Number(e.target.value) })}
                        className="w-full p-2 border rounded-xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      onClick={() => setShowLaborBurdenAddModal(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddLaborBurdenEmployee}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Add Employee
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Employee Modal */}
            {showLaborBurdenEditModal && editingLaborBurdenEmployee && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-md">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Employee</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Name</label>
                      <input
                        type="text"
                        value={editingLaborBurdenEmployee.name}
                        onChange={(e) => setEditingLaborBurdenEmployee({ ...editingLaborBurdenEmployee, name: e.target.value })}
                        className="w-full p-2 border rounded-xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Job Title</label>
                      <input
                        type="text"
                        value={editingLaborBurdenEmployee.jobTitle}
                        onChange={(e) => setEditingLaborBurdenEmployee({ ...editingLaborBurdenEmployee, jobTitle: e.target.value })}
                        className="w-full p-2 border rounded-xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Employee Type</label>
                      <select
                        value={editingLaborBurdenEmployee.type}
                        onChange={(e) => setEditingLaborBurdenEmployee({ ...editingLaborBurdenEmployee, type: e.target.value })}
                        className="w-full p-2 border rounded-xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                      >
                        <option value="hourly">Hourly</option>
                        <option value="salaried">Salaried</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">
                        Rate ({editingLaborBurdenEmployee.type === 'hourly' ? 'Hourly' : 'Annual'})
                      </label>
                      <input
                        type="number"
                        value={editingLaborBurdenEmployee.rate}
                        onChange={(e) => setEditingLaborBurdenEmployee({ ...editingLaborBurdenEmployee, rate: Number(e.target.value) })}
                        className="w-full p-2 border rounded-xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Extra Costs (%)</label>
                      <input
                        type="number"
                        value={editingLaborBurdenEmployee.extraCosts || 15}
                        onChange={(e) => setEditingLaborBurdenEmployee({ ...editingLaborBurdenEmployee, extraCosts: Number(e.target.value) })}
                        className="w-full p-2 border rounded-xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                      />
                    </div>
                    {editingLaborBurdenEmployee.type === 'hourly' && (
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Unbillable (%)</label>
                        <input
                          type="number"
                          value={editingLaborBurdenEmployee.unbillablePercentage || 10}
                          onChange={(e) => setEditingLaborBurdenEmployee({ ...editingLaborBurdenEmployee, unbillablePercentage: Number(e.target.value) })}
                          className="w-full p-2 border rounded-xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                        />
                      </div>
                    )}
                  </div>
                  <div className="mt-6 flex justify-between">
                    <button
                      onClick={() => handleRemoveLaborBurdenEmployee(editingLaborBurdenEmployee.id)}
                      className="px-4 py-2 text-red-600 hover:text-red-800"
                    >
                      Delete Employee
                    </button>
                    <div className="space-x-3">
                      <button
                        onClick={() => {
                          setShowLaborBurdenEditModal(false);
                          setEditingLaborBurdenEmployee(null);
                        }}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateLaborBurdenEmployee}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Comparison Tab Content */}
        {laborBurdenActiveTab === 'comparison' && (
          <div>
            {/* Global Inputs */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-800 mb-8">
              <h2 className="text-xl font-semibold mb-4 border-b pb-2 text-center text-gray-700 dark:text-white border-gray-200 dark:border-gray-600">Time Off & Workdays</h2>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Work Days Per Year</label>
                  <input
                    type="number"
                    value={laborBurdenWorkDays}
                    onChange={(e) => setLaborBurdenWorkDays(Number(e.target.value))}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-colors bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">PTO Days</label>
                  <input
                    type="number"
                    value={laborBurdenPto}
                    onChange={(e) => setLaborBurdenPto(Number(e.target.value))}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-colors bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Holidays</label>
                  <input
                    type="number"
                    value={laborBurdenHolidays}
                    onChange={(e) => setLaborBurdenHolidays(Number(e.target.value))}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-colors bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Hourly Employee Section */}
              <div>
                {/* Hourly Employee Inputs */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-800 mb-6">
                  <h2 className="text-xl font-semibold mb-4 border-b pb-2 text-center text-indigo-700 dark:text-indigo-300 border-gray-200 dark:border-gray-600">Hourly Employee</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Hourly Rate ($)</label>
                      <input
                        type="number"
                        value={laborBurdenHourlyRate}
                        onChange={(e) => setLaborBurdenHourlyRate(Number(e.target.value))}
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-colors bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Extra Costs (%)</label>
                      <input
                        type="number"
                        value={laborBurdenHourlyExtraCosts}
                        onChange={(e) => setLaborBurdenHourlyExtraCosts(Number(e.target.value))}
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-colors bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Marked Unbillable (%)</label>
                      <input
                        type="number"
                        value={laborBurdenUnbillablePercentage}
                        onChange={(e) => setLaborBurdenUnbillablePercentage(Number(e.target.value))}
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-colors bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Hourly Total Labor Load */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-800">
                  <h3 className="text-lg font-semibold mb-4 border-b pb-2 text-center text-indigo-700 dark:text-indigo-300 border-gray-200 dark:border-gray-600">Total Labor Load</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    <div className="bg-gradient-to-b from-indigo-50 to-white border border-indigo-100 dark:bg-gradient-to-b dark:from-indigo-900/30 dark:to-gray-700 dark:border-indigo-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                      <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Per Hour</div>
                      <div className="font-bold text-lg text-indigo-700 dark:text-indigo-300">{formatLaborBurdenCurrency(laborBurdenTotalHourlyBurden)}</div>
                    </div>
                    <div className="bg-gradient-to-b from-indigo-50 to-white border border-indigo-100 dark:bg-gradient-to-b dark:from-indigo-900/30 dark:to-gray-700 dark:border-indigo-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                      <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Per Day</div>
                      <div className="font-bold text-lg text-indigo-700 dark:text-indigo-300">{formatLaborBurdenCurrency(laborBurdenHourlyDayRate)}</div>
                    </div>
                    <div className="bg-gradient-to-b from-indigo-50 to-white border border-indigo-100 dark:bg-gradient-to-b dark:from-indigo-900/30 dark:to-gray-700 dark:border-indigo-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                      <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Weekly</div>
                      <div className="font-bold text-lg text-indigo-700 dark:text-indigo-300">{formatLaborBurdenCurrencyNoDecimals(laborBurdenHourlyWeekRate)}</div>
                    </div>
                    <div className="bg-gradient-to-b from-indigo-50 to-white border border-indigo-100 dark:bg-gradient-to-b dark:from-indigo-900/30 dark:to-gray-700 dark:border-indigo-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                      <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Monthly</div>
                      <div className="font-bold text-lg text-indigo-700 dark:text-indigo-300">{formatLaborBurdenCurrencyNoDecimals(laborBurdenHourlyMonthRate)}</div>
                    </div>
                    <div className="bg-gradient-to-b from-indigo-50 to-white border border-indigo-100 dark:bg-gradient-to-b dark:from-indigo-900/30 dark:to-gray-700 dark:border-indigo-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                      <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Annual</div>
                      <div className="font-bold text-lg text-indigo-700 dark:text-indigo-300">{formatLaborBurdenCurrencyNoDecimals(laborBurdenHourlyAnnualRate)}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Salaried Employee Section */}
              <div>
                {/* Salaried Employee Inputs */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-800 mb-6">
                  <h2 className="text-xl font-semibold mb-4 border-b pb-2 text-center text-teal-700 dark:text-teal-300 border-gray-200 dark:border-gray-600">Salaried Employee</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Salary Input Type</label>
                      <select
                        value={laborBurdenSalaryType}
                        onChange={(e) => setLaborBurdenSalaryType(e.target.value)}
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-teal-200 focus:border-teal-400 transition-colors bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                      >
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="annual">Annual</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">
                        Salary Rate ({laborBurdenSalaryType === 'weekly' ? 'Weekly' : laborBurdenSalaryType === 'monthly' ? 'Monthly' : 'Annual'})
                      </label>
                      <input
                        type="number"
                        value={laborBurdenSalaryRate}
                        onChange={(e) => setLaborBurdenSalaryRate(Number(e.target.value))}
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-teal-200 focus:border-teal-400 transition-colors bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">Extra Costs (%)</label>
                      <input
                        type="number"
                        value={laborBurdenSalaryExtraCosts}
                        onChange={(e) => setLaborBurdenSalaryExtraCosts(Number(e.target.value))}
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-teal-200 focus:border-teal-400 transition-colors bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Salary Total Labor Load */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-800">
                  <h3 className="text-lg font-semibold mb-4 border-b pb-2 text-center text-teal-700 dark:text-teal-300 border-gray-200 dark:border-gray-600">Total Labor Load</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    <div className="bg-gradient-to-b from-teal-50 to-white border border-teal-100 dark:bg-gradient-to-b dark:from-teal-900/30 dark:to-gray-700 dark:border-teal-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                      <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Per Hour</div>
                      <div className="font-bold text-lg text-teal-700 dark:text-teal-300">{formatLaborBurdenCurrency(laborBurdenSalaryHourRate)}</div>
                    </div>
                    <div className="bg-gradient-to-b from-teal-50 to-white border border-teal-100 dark:bg-gradient-to-b dark:from-teal-900/30 dark:to-gray-700 dark:border-teal-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                      <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Per Day</div>
                      <div className="font-bold text-lg text-teal-700 dark:text-teal-300">{formatLaborBurdenCurrency(laborBurdenSalaryDayRate)}</div>
                    </div>
                    <div className="bg-gradient-to-b from-teal-50 to-white border border-teal-100 dark:bg-gradient-to-b dark:from-teal-900/30 dark:to-gray-700 dark:border-teal-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                      <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Weekly</div>
                      <div className="font-bold text-lg text-teal-700 dark:text-teal-300">{formatLaborBurdenCurrencyNoDecimals(laborBurdenSalaryWeekRate)}</div>
                    </div>
                    <div className="bg-gradient-to-b from-teal-50 to-white border border-teal-100 dark:bg-gradient-to-b dark:from-teal-900/30 dark:to-gray-700 dark:border-teal-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                      <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Monthly</div>
                      <div className="font-bold text-lg text-teal-700 dark:text-teal-300">{formatLaborBurdenCurrencyNoDecimals(laborBurdenTotalSalaryBurden.monthly)}</div>
                    </div>
                    <div className="bg-gradient-to-b from-teal-50 to-white border border-teal-100 dark:bg-gradient-to-b dark:from-teal-900/30 dark:to-gray-700 dark:border-teal-700 rounded-lg p-3 flex flex-col items-center shadow-sm">
                      <div className="text-xs mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Annual</div>
                      <div className="font-bold text-lg text-teal-700 dark:text-teal-300">{formatLaborBurdenCurrencyNoDecimals(laborBurdenTotalSalaryBurden.annual)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLeadsView = () => {

    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  // Generate chart data from user entries
  const generateChartData = () => {
    if (!leadsEntries.length) {
      // Return empty data if no user entries - show empty graph
      return [];
    }

      if (leadsTimeFilter === 'currentYear') {
        // Show monthly data for current year
        const currentYear = new Date().getFullYear();
        const currentYearEntries = leadsEntries.filter(entry => entry.year === currentYear);
        
        if (currentYearEntries.length === 0) {
          return [
            { month: 'Jan', value: 0, goal: 200 },
            { month: 'Feb', value: 0, goal: 200 },
            { month: 'Mar', value: 0, goal: 200 },
            { month: 'Apr', value: 0, goal: 200 },
            { month: 'May', value: 0, goal: 200 },
            { month: 'Jun', value: 0, goal: 200 },
            { month: 'Jul', value: 0, goal: 200 },
            { month: 'Aug', value: 0, goal: 200 },
            { month: 'Sep', value: 0, goal: 200 },
            { month: 'Oct', value: 0, goal: 200 },
            { month: 'Nov', value: 0, goal: 200 },
            { month: 'Dec', value: 0, goal: 200 }
          ];
        }

        // Group entries by month for current year
        const monthlyData = {};
        currentYearEntries.forEach(entry => {
          if (!monthlyData[entry.month]) {
            monthlyData[entry.month] = 0;
          }
          monthlyData[entry.month] += entry.count || 0;
        });

        // Convert to chart format with all months
        const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June',
                           'July', 'August', 'September', 'October', 'November', 'December'];
        
        const chartData = monthOrder.map(month => ({
          month: month.substring(0, 3), // Short month name for chart
          value: monthlyData[month] || 0,
          goal: 200 // Default goal, could be made user-configurable
        }));

        return chartData;
      } else {
        // Show yearly data for all time
        const yearlyData = {};
        leadsEntries.forEach(entry => {
          if (!yearlyData[entry.year]) {
            yearlyData[entry.year] = 0;
          }
          yearlyData[entry.year] += entry.count;
        });

        // Convert to chart format
        const chartData = Object.entries(yearlyData).map(([year, value]) => ({
          month: year.toString(), // Use year as x-axis label
          value: value,
          goal: 200 // Default goal, could be made user-configurable
        }));

        // Sort by year
        chartData.sort((a, b) => parseInt(a.month) - parseInt(b.month));

        return chartData;
      }
    };



    return (
      <div className={`p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
        {/* Header with Edit, Title, Time Filters, and Add buttons in one row */}
        <div className="flex items-center justify-between mb-6">
          {/* Edit Button - Far Left */}
          <button
            onClick={() => setShowLeadsEditPopup(true)}
            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>

          {/* Title and Time Filter Buttons - Center */}
          <div className="flex items-center">
            <div className="flex items-center">
              <FaBullhorn className="w-6 h-6 text-red-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Leads</h3>
            </div>
            <div className="flex space-x-2 ml-4">
              <button 
                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                  leadsTimeFilter === 'currentYear' 
                    ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                onClick={() => setLeadsTimeFilter('currentYear')}
              >
                Current Year
              </button>
              <button 
                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                  leadsTimeFilter === 'allTime' 
                    ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                onClick={() => setLeadsTimeFilter('allTime')}
              >
                All Time
              </button>
            </div>
          </div>

          {/* Add Button - Far Right */}
          <button
            onClick={() => setShowLeadsAddPopup(true)}
            className="p-2 rounded-full bg-red-600 hover:bg-red-700 text-white"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
        
        {/* Chart */}
        <div className="h-80 mb-6">
          {generateChartData().length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p className={`text-lg font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No leads data available
                </p>
                <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Add your first entry using the + button above
                </p>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={generateChartData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                <XAxis 
                  dataKey="month" 
                  stroke={darkMode ? '#9ca3af' : '#6b7280'} 
                  tickFormatter={(value) => leadsTimeFilter === 'allTime' ? value : value}
                />
                <YAxis stroke={darkMode ? '#9ca3af' : '#6b7280'} />
                <Tooltip 
                  formatter={(value) => [value, 'Leads']}
                  labelFormatter={(label) => leadsTimeFilter === 'allTime' ? `Year: ${label}` : `Month: ${label}`}
                  contentStyle={{
                    backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                    border: darkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                    color: darkMode ? '#f9fafb' : '#1f2937'
                  }}
                />
                <Line type="monotone" dataKey="value" name="Leads" stroke="#DC2626" dot={false} activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Edit Entries Popup */}
        {showLeadsEditPopup && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className={`p-6 rounded-lg shadow-lg max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex items-center mb-4 relative">
                <h3 className={`text-lg font-semibold flex-1 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Edit Leads Entries
                </h3>
                <button
                  onClick={() => {
                    setShowLeadsEditPopup(false);
                    setEditingLeadsEntry(null);
                  }}
                  className={`p-1 rounded-full absolute right-0 ${
                    darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Edit Form */}
              {editingLeadsEntry && (
                <div className={`p-4 rounded-lg mb-4 ${
                  darkMode ? 'bg-gray-700' : 'bg-gray-50'
                }`}>
                  <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Edit Entry
                  </h4>
                  <form onSubmit={handleUpdateLeadsEntry} className="grid grid-cols-4 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Leads Count
                      </label>
                      <input
                        type="number"
                        value={leadsFormData.leadsCount}
                        onChange={(e) => setLeadsFormData({...leadsFormData, leadsCount: e.target.value})}
                        className={`w-full p-2 border rounded ${
                          darkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        min="0"
                      />
                    </div>
                   
                   <div>
                     <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                       Month
                     </label>
                                           <select
                        value={leadsFormData.month}
                        onChange={(e) => setLeadsFormData({...leadsFormData, month: e.target.value})}
                        className={`w-full p-2 border rounded ${
                          darkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                       {months.map(month => (
                         <option key={month} value={month}>{month}</option>
                       ))}
                     </select>
                   </div>
                   
                   <div>
                     <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                       Year
                     </label>
                                           <select
                        value={leadsFormData.year}
                        onChange={(e) => setLeadsFormData({...leadsFormData, year: e.target.value})}
                        className={`w-full p-2 border rounded ${
                          darkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                       {years.map(year => (
                         <option key={year} value={year}>{year}</option>
                       ))}
                     </select>
                   </div>
                   
                   <div className="flex items-end space-x-2">
                     <button
                       type="submit"
                       className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                     >
                       Update
                     </button>
                                             <button
                          type="button"
                          onClick={() => setEditingLeadsEntry(null)}
                          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                   </div>
                 </form>
               </div>
             )}

             {/* Entries List */}
             <div className="space-y-2">
               <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                 All Entries
               </h4>
               {leadsEntries.length === 0 ? (
                 <p className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                   No entries found. Add your first entry using the + button.
                 </p>
               ) : (
                 leadsEntries
                   .sort((a, b) => {
                     const yearDiff = b.year - a.year;
                     if (yearDiff !== 0) return yearDiff;
                     return months.indexOf(b.month) - months.indexOf(a.month);
                   })
                   .map(entry => (
                     <div key={entry.id} className={`flex items-center justify-between p-3 rounded-lg ${
                       darkMode ? 'bg-gray-700' : 'bg-gray-50'
                     }`}>
                       <div className="flex-1">
                         <div className="flex items-center space-x-4">
                           <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                             {entry.count}
                           </span>
                           <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                             {entry.month} {entry.year}
                           </span>
                         </div>
                       </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditLeadsEntry(entry)}
                          className={`p-1 rounded ${
                            darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                          }`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteLeadsEntry(entry.id)}
                          className={`p-1 rounded text-red-500 hover:bg-red-100 ${
                            darkMode ? 'hover:bg-red-900' : 'hover:bg-red-100'
                          }`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
               )}
             </div>
           </div>
         </div>
       )}

               {/* Add Entry Popup */}
        {showLeadsAddPopup && (
         <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
           <div className={`p-6 rounded-lg shadow-lg max-w-md w-full mx-4 ${
             darkMode ? 'bg-gray-800' : 'bg-white'
           }`}>
             <div className="flex items-center mb-4 relative">
               <h3 className={`text-lg font-semibold flex-1 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                 Add Leads Entry
               </h3>
               <button
                 onClick={() => setShowLeadsAddPopup(false)}
                 className={`p-1 rounded-full absolute right-0 ${
                   darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                 }`}
               >
                 <X className="w-5 h-5" />
               </button>
             </div>
             
             <form onSubmit={handleAddLeadsEntry} className="space-y-4">
               <div>
                 <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                   Leads Count
                 </label>
                 <input
                   type="number"
                   value={leadsFormData.leadsCount}
                   onChange={(e) => setLeadsFormData({...leadsFormData, leadsCount: e.target.value})}
                   className={`w-full p-3 border rounded-lg ${
                     darkMode 
                       ? 'bg-gray-700 border-gray-600 text-white' 
                       : 'bg-white border-gray-300 text-gray-900'
                   }`}
                   placeholder="Enter leads count"
                   min="0"
                 />
               </div>
               
               <div>
                 <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                   Month
                 </label>
                 <select
                   value={leadsFormData.month}
                   onChange={(e) => setLeadsFormData({...leadsFormData, month: e.target.value})}
                   className={`w-full p-3 border rounded-lg ${
                     darkMode 
                       ? 'bg-gray-700 border-gray-600 text-white' 
                       : 'bg-white border-gray-300 text-gray-900'
                   }`}
                 >
                   {months.map(month => (
                     <option key={month} value={month}>{month}</option>
                   ))}
                 </select>
               </div>
               
               <div>
                 <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                   Year
                 </label>
                 <select
                   value={leadsFormData.year}
                   onChange={(e) => setLeadsFormData({...leadsFormData, year: e.target.value})}
                   className={`w-full p-3 border rounded-lg ${
                     darkMode 
                       ? 'bg-gray-700 border-gray-600 text-white' 
                       : 'bg-white border-gray-300 text-gray-900'
                   }`}
                 >
                   {years.map(year => (
                     <option key={year} value={year}>{year}</option>
                   ))}
                 </select>
               </div>
               
               <div className="flex space-x-3 pt-4">
                 <button
                   type="button"
                   onClick={() => setShowLeadsAddPopup(false)}
                   className={`flex-1 py-2 px-4 rounded-lg border ${
                     darkMode 
                       ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                       : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                   }`}
                 >
                   Cancel
                 </button>
                 <button
                   type="submit"
                   className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700"
                 >
                   Add Entry
                 </button>
               </div>
             </form>
           </div>
         </div>
       )}
     </div>
   );
 };

  const renderWebsiteView = () => {
    // Helper function for score color classes

    // Summary Card Component

    return (
      <div className="space-y-6">


        {/* Portal User Website Data Display - Always show */}
          <div className={`mb-6 p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Account Website Performance
            </h2>
            {/* First Row - 3 columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className={`p-4 text-center bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                <div className="flex items-center justify-center mb-2">
                  <div className="text-2xl mr-2">🔍</div>
                  <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    SEO Score
                  </span>
                </div>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  {websiteData.seoScore || 0}
                </p>
                <div className="mt-2">
                  <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <div 
                      className={`h-2 rounded-full ${
                        websiteData.seoScore >= 80 ? 'bg-green-600' : 
                        websiteData.seoScore >= 60 ? 'bg-blue-600' :
                        websiteData.seoScore >= 40 ? 'bg-yellow-500' : 'bg-red-600'
                      }`}
                      style={{ width: `${websiteData.seoScore || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              
              <div className={`p-4 text-center bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                <div className="flex items-center justify-center mb-2">
                  <div className="text-2xl mr-2">🏥</div>
                  <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Technical SEO
                  </span>
                </div>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  {websiteData.pageHealth || 0}
                </p>
                <div className="mt-2">
                  <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <div 
                      className={`h-2 rounded-full ${
                        websiteData.pageHealth >= 80 ? 'bg-green-600' : 
                        websiteData.pageHealth >= 60 ? 'bg-blue-600' :
                        websiteData.pageHealth >= 40 ? 'bg-yellow-500' : 'bg-red-600'
                      }`}
                      style={{ width: `${websiteData.pageHealth || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              
              <div className={`p-4 text-center bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                <div className="flex items-center justify-center mb-2">
                  <div className="text-2xl mr-2">📝</div>
                  <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Organic Visibility
                  </span>
                </div>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  {websiteData.contentScore || 0}
                </p>
                <div className="mt-2">
                  <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <div 
                      className={`h-2 rounded-full ${
                        websiteData.contentScore >= 80 ? 'bg-green-600' : 
                        websiteData.contentScore >= 60 ? 'bg-blue-600' :
                        websiteData.contentScore >= 40 ? 'bg-yellow-500' : 'bg-red-600'
                      }`}
                      style={{ width: `${websiteData.contentScore || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Second Row - 3 columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`p-4 text-center bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                <div className="flex items-center justify-center mb-2">
                  <div className="text-2xl mr-2">📅</div>
                  <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Last Scanned
                  </span>
                </div>
                <p className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  {websiteData.lastScanned || 'Never'}
                </p>
              </div>
              
              <div className={`p-4 text-center bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                <div className="flex items-center justify-center mb-2">
                  <div className="text-2xl mr-2">📄</div>
                  <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    On-Page SEO
                  </span>
                </div>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  {websiteData.onPageScore || 0}
                </p>
                <div className="mt-2">
                  <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <div 
                      className={`h-2 rounded-full ${
                        websiteData.onPageScore >= 80 ? 'bg-green-600' : 
                        websiteData.onPageScore >= 60 ? 'bg-blue-600' :
                        websiteData.onPageScore >= 40 ? 'bg-yellow-500' : 'bg-red-600'
                      }`}
                      style={{ width: `${websiteData.onPageScore || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              
              <div className={`p-4 text-center bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                <div className="flex items-center justify-center mb-2">
                  <div className="text-2xl mr-2">🔗</div>
                  <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Off-Page SEO
                  </span>
                </div>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  {websiteData.offPageScore || 0}
                </p>
                <div className="mt-2">
                  <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <div 
                      className={`h-2 rounded-full ${
                        websiteData.offPageScore >= 80 ? 'bg-green-600' : 
                        websiteData.offPageScore >= 60 ? 'bg-blue-600' :
                        websiteData.offPageScore >= 40 ? 'bg-yellow-500' : 'bg-red-600'
                      }`}
                      style={{ width: `${websiteData.offPageScore || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            
            {/* Issues Summary */}
            {websiteData.issues && websiteData.issues.length > 0 && (
              <div className="mt-4">
                <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Issues Found ({websiteData.issues.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {websiteData.issues.slice(0, 6).map((issue, index) => (
                    <div key={index} className={`p-2 rounded text-sm ${
                      issue.type === 'Critical' ? 'bg-red-100 text-red-800' :
                      issue.type === 'SEO' ? 'bg-blue-100 text-blue-800' :
                      issue.type === 'Performance' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {issue.title || issue.message || `Issue ${index + 1}`}
                    </div>
                  ))}
                </div>
                {websiteData.issues.length > 6 && (
                  <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    And {websiteData.issues.length - 6} more issues...
                  </p>
                )}
              </div>
            )}

            {/* Organic Traffic and Backlink Profile - Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {/* Organic Traffic Section */}
              {websiteData.organicTraffic && Object.keys(websiteData.organicTraffic).length > 0 && (
                <div>
                  <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Organic Traffic
                  </h3>
                  <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Traffic Amount
                        </div>
                        <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {websiteData.organicTraffic.amount?.toLocaleString() || 'N/A'}
                        </div>
                      </div>
                      <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Ranking Keywords
                        </div>
                        <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {websiteData.organicTraffic.rankingKeywords || 'N/A'}
                        </div>
                      </div>
                      <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Estimated Value
                        </div>
                        <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          ${websiteData.organicTraffic.estimatedTrafficValue?.toLocaleString() || 'N/A'}
                        </div>
                      </div>
                    </div>
                    {websiteData.organicTraffic.keyInsight && (
                      <div className={`mt-3 text-sm text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        💡 {websiteData.organicTraffic.keyInsight}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Backlink Profile Section */}
              {websiteData.backlinkProfile && Object.keys(websiteData.backlinkProfile).length > 0 && (
                <div>
                  <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Backlink Profile
                  </h3>
                  <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Total Backlinks
                        </div>
                        <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {websiteData.backlinkProfile.backlinks?.toLocaleString() || 'N/A'}
                        </div>
                      </div>
                      <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Spam Score
                        </div>
                        <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {websiteData.backlinkProfile.spamScore || 'N/A'}%
                        </div>
                      </div>
                      <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Nofollow Links
                        </div>
                        <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {websiteData.backlinkProfile.nofollowLinks?.toLocaleString() || 'N/A'}
                        </div>
                      </div>
                    </div>
                    {websiteData.backlinkProfile.keyInsight && (
                      <div className={`mt-3 text-sm text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        💡 {websiteData.backlinkProfile.keyInsight}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Historical Traffic and Ranked Keywords - Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {/* Historical Traffic Section */}
              {websiteData.historicalTraffic && Object.keys(websiteData.historicalTraffic).length > 0 && (
                <div>
                  <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Historical Traffic
                  </h3>
                  <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Average ETV
                        </div>
                        <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          ${websiteData.historicalTraffic.averageETV?.toLocaleString() || 'N/A'}
                        </div>
                      </div>
                      <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Highest ETV
                        </div>
                        <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          ${websiteData.historicalTraffic.highestETV?.toLocaleString() || 'N/A'}
                        </div>
                      </div>
                      <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Keywords per Month
                        </div>
                        <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {websiteData.historicalTraffic.keywordsPerMonth || 'N/A'}
                        </div>
                      </div>
                    </div>
                    {websiteData.historicalTraffic.keyInsight && (
                      <div className={`mt-3 text-sm text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        💡 {websiteData.historicalTraffic.keyInsight}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Ranked Keywords Section */}
              {websiteData.rankedKeywords && Object.keys(websiteData.rankedKeywords).length > 0 && (
                <div>
                  <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Ranked Keywords
                  </h3>
                  <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Total Keywords
                        </div>
                        <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {websiteData.rankedKeywords.total?.toLocaleString() || 'N/A'}
                        </div>
                      </div>
                      <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Top 10 Keywords
                        </div>
                        <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {websiteData.rankedKeywords.top10?.toLocaleString() || 'N/A'}
                        </div>
                      </div>
                      <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Traffic Value
                        </div>
                        <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          ${websiteData.rankedKeywords.organicTrafficValue?.toLocaleString() || 'N/A'}
                        </div>
                      </div>
                    </div>
                    {websiteData.rankedKeywords.keyInsight && (
                      <div className={`mt-3 text-sm text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        💡 {websiteData.rankedKeywords.keyInsight}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Page Audit Section */}
            {websiteData.pageAudit && Object.keys(websiteData.pageAudit).length > 0 && (
              <div className="mt-6">
                <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Page Audit
                </h3>
                <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                      <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        On-Page Score
                      </div>
                      <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        {websiteData.pageAudit.onpageScore || 'N/A'}
                      </div>
                    </div>
                    <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                      <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Load Speed
                      </div>
                      <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        {websiteData.pageAudit.loadSpeed ? `${websiteData.pageAudit.loadSpeed}s` : 'N/A'}
                      </div>
                    </div>
                    <div className={`p-3 rounded-xl text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                      <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Error/Warnings
                      </div>
                      <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        {websiteData.pageAudit.errorWarnings || 'N/A'}
                      </div>
                    </div>
                  </div>
                  {websiteData.pageAudit.keyInsight && (
                    <div className={`mt-3 text-sm text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      💡 {websiteData.pageAudit.keyInsight}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Errors & Warnings Section */}
            {websiteData.errorsWarnings && Object.keys(websiteData.errorsWarnings).length > 0 && (
              <div className="mt-6">
                <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Errors & Warnings
                </h3>
                <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <div className={`p-3 rounded text-center ${darkMode ? 'bg-red-900' : 'bg-red-100'}`}>
                      <div className={`text-sm font-medium ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                        Critical Errors
                      </div>
                      <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-red-800'}`}>
                        {websiteData.errorsWarnings.criticalErrors || 0}
                      </div>
                    </div>
                    <div className={`p-3 rounded text-center ${darkMode ? 'bg-yellow-900' : 'bg-yellow-100'}`}>
                      <div className={`text-sm font-medium ${darkMode ? 'text-yellow-300' : 'text-yellow-600'}`}>
                        Warnings
                      </div>
                      <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-yellow-800'}`}>
                        {websiteData.errorsWarnings.warnings || 0}
                      </div>
                    </div>
                    <div className={`p-3 rounded text-center ${darkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
                      <div className={`text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                        Notices
                      </div>
                      <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-blue-800'}`}>
                        {websiteData.errorsWarnings.notices || 0}
                      </div>
                    </div>
                    <div className={`p-3 rounded text-center ${darkMode ? 'bg-orange-900' : 'bg-orange-100'}`}>
                      <div className={`text-sm font-medium ${darkMode ? 'text-orange-300' : 'text-orange-600'}`}>
                        Accessibility
                      </div>
                      <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-orange-800'}`}>
                        {websiteData.errorsWarnings.accessibilityIssues || 0}
                      </div>
                    </div>
                    <div className={`p-3 rounded text-center ${darkMode ? 'bg-purple-900' : 'bg-purple-100'}`}>
                      <div className={`text-sm font-medium ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                        Performance
                      </div>
                      <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-purple-800'}`}>
                        {websiteData.errorsWarnings.performanceIssues || 0}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>







      </div>
    );
  };

  const renderSocialMediaView = () => {
    // Always show the social media tab content, even when there's no data

    return (
      <div className="space-y-6">
        {/* Platform Card Component */}
        {(() => {
          const PlatformCard = ({ platform, data, color, iconPath }) => {
            // Get latest entry for this platform
            const latestEntry = platformEntries[platform.toLowerCase()]?.sort((a, b) => {
              const yearDiff = b.year - a.year;
              if (yearDiff !== 0) return yearDiff;
              const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
              return months.indexOf(b.month) - months.indexOf(a.month);
            })[0];

            const displayFollowers = latestEntry ? latestEntry.followers : data.followers;
            
            return (
              <div className={`p-4 rounded-lg shadow-md border-l-4 ${color} relative ${
                darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
              }`}>
                {/* Edit Button */}
                <button
                  onClick={() => {
                    setEditingSocialPlatform(platform.toLowerCase());
                    // Load the latest entry data into the form
                    const latestEntry = platformEntries[platform.toLowerCase()]?.sort((a, b) => {
                      const yearDiff = b.year - a.year;
                      if (yearDiff !== 0) return yearDiff;
                      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                      return months.indexOf(b.month) - months.indexOf(a.month);
                    })[0];
                    
                    if (latestEntry) {
                      setSocialFormData({
                        followers: latestEntry.followers?.toString() || '',
                        views: latestEntry.views?.toString() || '',
                        likes: latestEntry.likes?.toString() || '',
                        comments: latestEntry.comments?.toString() || '',
                        shares: latestEntry.shares?.toString() || '',
                        month: latestEntry.month || '',
                        year: latestEntry.year?.toString() || new Date().getFullYear().toString()
                      });
                      setEditingSocialEntry(latestEntry);
                    } else {
                      setSocialFormData({
                        followers: '',
                        views: '',
                        likes: '',
                        comments: '',
                        shares: '',
                        month: '',
                        year: new Date().getFullYear().toString()
                      });
                      setEditingSocialEntry(null);
                    }
                    setShowSocialEditPopup(true);
                  }}
            className={`absolute top-2 left-2 p-1 rounded-full transition-colors ${
              darkMode 
                ? 'bg-gray-600 hover:bg-gray-500 text-white' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>

          {/* Add Button */}
          <button
            onClick={() => {
              setEditingSocialPlatform(platform.toLowerCase());
              setSocialFormData({
                followers: '',
                views: '',
                likes: '',
                comments: '',
                shares: '',
                month: '',
                year: new Date().getFullYear().toString()
              });
              setShowSocialAddPopup(true);
            }}
            className={`absolute top-2 right-2 p-1 rounded-full transition-colors ${
              darkMode 
                ? 'bg-red-600 hover:bg-red-700 text-white' 
                : 'bg-red-100 hover:bg-red-200 text-red-600'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>

          <div className="flex flex-col items-center mb-4">
            <div className="w-16 h-16 mb-3 flex items-center justify-center">
              <img 
                src={iconPath} 
                alt={`${platform} icon`}
                className="w-14 h-14"
              />
            </div>
            <h3 className={`text-sm font-bold text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {platform}
            </h3>
          </div>
          
          <div className="text-center">
            <div className={`w-full p-2 text-2xl font-bold text-center ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {displayFollowers.toLocaleString()}
            </div>
            <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              followers
            </div>
          </div>
        </div>
      );
    };

    // Generate chart data from platform entries
    const generateChartData = () => {
      if (socialTimeFilter === 'currentYear') {
        // Show monthly data for current year
        const currentYear = new Date().getFullYear();
        const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June',
                           'July', 'August', 'September', 'October', 'November', 'December'];
        
        return monthOrder.map(month => {
          let totalFollowers = 0;
          Object.values(platformEntries).forEach(platformEntries => {
            const monthEntry = platformEntries.find(entry => 
              entry.month === month && entry.year === currentYear
            );
            if (monthEntry) {
              totalFollowers += monthEntry.followers;
            }
          });
          
          return {
            month: month.substring(0, 3),
            value: totalFollowers
          };
        });
      } else {
        // Show yearly data for all time
        const yearlyData = {};
        
        Object.values(platformEntries).forEach(platformEntries => {
          platformEntries.forEach(entry => {
            if (!yearlyData[entry.year]) {
              yearlyData[entry.year] = 0;
            }
            yearlyData[entry.year] += entry.followers;
          });
        });
        
        return Object.entries(yearlyData)
          .sort(([a], [b]) => parseInt(a) - parseInt(b))
          .map(([year, value]) => ({
            month: year,
            value: value
          }));
      }
    };

    // Total Followers Line Graph Component
    const TotalFollowersLineGraph = () => {
      const chartData = generateChartData();

      return (
        <div className={`p-6 rounded-lg shadow-md ${
          darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
        } border`}>
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center">
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'} mr-4`}>
                Total Followers
              </h3>
              <div className="flex space-x-2">
                <button 
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    socialTimeFilter === 'currentYear' 
                      ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  onClick={() => setSocialTimeFilter('currentYear')}
                >
                  Current Year
                </button>
                <button 
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                    socialTimeFilter === 'allTime' 
                      ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  onClick={() => setSocialTimeFilter('allTime')}
                >
                  All Time
                </button>
              </div>
            </div>
          </div>
          
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [value.toLocaleString(), 'Followers']}
                  labelFormatter={(label) => socialTimeFilter === 'allTime' ? `Year: ${label}` : `Month: ${label}`}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  name="Total Followers" 
                  stroke="#DC2626" 
                  strokeWidth={2}
                  dot={{ r: 6 }}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    };

    // Top Platforms Card Component
    const TopPlatformsCard = () => {
      const platformIcons = {
        facebook: '/facebook.svg',
        instagram: '/instagram.svg',
        tiktok: '/tiktok.svg',
        youtube: '/Youtube.svg'
      };

      const platforms = Object.entries(socialData)
        .map(([platform, data]) => ({
          platform: platform.charAt(0).toUpperCase() + platform.slice(1),
          platformKey: platform,
          followers: data.followers,
          engagement: data.engagement
        }))
        .sort((a, b) => b.followers - a.followers)
        .slice(0, 4);

      return (
        <div className={`p-6 rounded-lg shadow-md ${
          darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
        } border`}>
          <h3 className={`text-xl font-bold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Top Platforms
          </h3>
          
          <div className="space-y-3">
            {platforms.map((platform, index) => (
              <div key={platform.platform} className={`p-3 rounded-md ${
                darkMode ? 'bg-gray-600' : 'bg-gray-50'
              }`}>
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-4 ${
                    index === 0 ? 'bg-yellow-500 text-white' :
                    index === 1 ? 'bg-gray-400 text-white' :
                    index === 2 ? 'bg-orange-500 text-white' :
                    'bg-blue-500 text-white'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="w-8 h-8 mr-4 flex items-center justify-center">
                    <img 
                      src={platformIcons[platform.platformKey]} 
                      alt={`${platform.platform} icon`}
                      className="w-6 h-6"
                    />
                  </div>
                  <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {platform.platform}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };


    // Marketing Insights Component
    const MarketingInsights = () => {
      // Calculate metrics from actual platform entries data
      const allEntries = Object.values(platformEntries).flat();
      
      const totalViews = allEntries.reduce((sum, entry) => sum + (entry.views || 0), 0);
      const totalLikes = allEntries.reduce((sum, entry) => sum + (entry.likes || 0), 0);
      const totalComments = allEntries.reduce((sum, entry) => sum + (entry.comments || 0), 0);
      const totalShares = allEntries.reduce((sum, entry) => sum + (entry.shares || 0), 0);
      const totalInteractions = totalLikes + totalComments + totalShares;
      
      // Calculate total followers by summing the latest followers from each platform
      const totalFollowers = Object.keys(platformEntries).reduce((sum, platform) => {
        const platformEntriesList = platformEntries[platform];
        if (platformEntriesList && platformEntriesList.length > 0) {
          // Sort entries by year and month to get the latest one
          const latestEntry = platformEntriesList.sort((a, b) => {
            const yearDiff = b.year - a.year;
            if (yearDiff !== 0) return yearDiff;
            const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            return months.indexOf(b.month) - months.indexOf(a.month);
          })[0];
          return sum + (latestEntry.followers || 0);
        }
        return sum;
      }, 0);
      
      // Count platforms that have entries
      
      // Calculate averages based on platforms with data

      return (
        <div className={`p-6 rounded-lg shadow-md ${
          darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
        } border`}>
          <h3 className={`text-xl font-bold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Marketing Insights
          </h3>
          
          <div className="grid grid-cols-6 gap-4">
            <div className={`p-3 rounded-xl text-center ${darkMode ? 'bg-gray-800' : 'bg-blue-50'}`}>
              <div className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-blue-600'}`}>
                Total Followers
              </div>
              <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-blue-900'}`}>
                {totalFollowers.toLocaleString()}
              </div>
            </div>
                
            <div className={`p-3 rounded-xl text-center ${darkMode ? 'bg-gray-800' : 'bg-green-50'}`}>
              <div className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-green-600'}`}>
                Total Views
              </div>
              <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-green-900'}`}>
                {totalViews.toLocaleString()}
              </div>
            </div>
            
            <div className={`p-3 rounded-xl text-center ${darkMode ? 'bg-gray-800' : 'bg-purple-50'}`}>
              <div className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-purple-600'}`}>
                Total Interactions
              </div>
              <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-purple-900'}`}>
                {totalInteractions.toLocaleString()}
              </div>
            </div>
        
            <div className={`p-3 rounded-xl text-center ${darkMode ? 'bg-gray-800' : 'bg-orange-50'}`}>
              <div className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-orange-600'}`}>
                Total Likes
              </div>
              <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-orange-900'}`}>
                {totalLikes.toLocaleString()}
              </div>
            </div>
            
            <div className={`p-3 rounded-xl text-center ${darkMode ? 'bg-gray-800' : 'bg-red-50'}`}>
              <div className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-red-600'}`}>
                Total Comments
              </div>
              <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-red-900'}`}>
                {totalComments.toLocaleString()}
              </div>
            </div>
            
            <div className={`p-3 rounded-xl text-center ${darkMode ? 'bg-gray-800' : 'bg-indigo-50'}`}>
              <div className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-indigo-600'}`}>
                Total Shares
              </div>
              <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-indigo-900'}`}>
                {totalShares.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-6">

        {/* Total Followers Line Graph and Top Platforms */}
        <div className="grid grid-cols-4 gap-6">
          <div className="col-span-3">
            <TotalFollowersLineGraph />
          </div>
          <div className="col-span-1 space-y-6">
            <TopPlatformsCard />
          </div>
        </div>

        {/* Platform Cards */}
        <div className="grid grid-cols-4 gap-4">
          <PlatformCard 
            platform="Facebook" 
            data={socialData.facebook} 
            color="border-blue-500" 
            iconPath="/facebook.svg" 
          />
          <PlatformCard 
            platform="Instagram" 
            data={socialData.instagram} 
            color="border-pink-500" 
            iconPath="/instagram.svg" 
          />
          <PlatformCard 
            platform="TikTok" 
            data={socialData.tiktok} 
            color="border-black" 
            iconPath="/tiktok.svg" 
          />
          <PlatformCard 
            platform="YouTube" 
            data={socialData.youtube} 
            color="border-red-500" 
            iconPath="/Youtube.svg" 
          />
        </div>

        {/* Marketing Insights */}
        <div>
          <MarketingInsights />
        </div>

        {/* Social Media Data Popup */}
        {(showSocialAddPopup || showSocialEditPopup) && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className={`p-6 rounded-lg shadow-lg max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex items-center mb-4 relative">
                <h3 className={`text-lg font-semibold flex-1 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  {showSocialEditPopup ? 'Edit' : 'Add'} {editingSocialPlatform && editingSocialPlatform.charAt(0).toUpperCase() + editingSocialPlatform.slice(1)} Data
                </h3>
                <button
                  onClick={() => {
                    setShowSocialAddPopup(false);
                    setShowSocialEditPopup(false);
                    setEditingSocialPlatform(null);
                    setEditingSocialEntry(null);
                    setSocialFormData({
                      followers: '',
                      views: '',
                      likes: '',
                      comments: '',
                      shares: '',
                      month: '',
                      year: new Date().getFullYear().toString()
                    });
                  }}
                  className={`p-1 rounded-full absolute right-0 ${
                    darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Edit Form */}
              {editingSocialEntry && (
                <div className={`p-4 rounded-lg mb-4 ${
                  darkMode ? 'bg-gray-700' : 'bg-gray-50'
                }`}>
                  <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Edit Entry
                  </h4>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!socialFormData.followers || !socialFormData.month || !socialFormData.year) {
                      alert('Please fill in all required fields (Followers, Month, Year)');
                      return;
                    }
                    
                    if (!editingSocialEntry) {
                      alert('No entry selected for editing');
                      return;
                    }
                    
                    const success = await updateSocialMediaEntry(editingSocialPlatform, editingSocialEntry.id, {
                      followers: parseInt(socialFormData.followers) || 0,
                      views: parseInt(socialFormData.views) || 0,
                      likes: parseInt(socialFormData.likes) || 0,
                      comments: parseInt(socialFormData.comments) || 0,
                      shares: parseInt(socialFormData.shares) || 0,
                      month: socialFormData.month,
                      year: parseInt(socialFormData.year)
                    });
                    
                    if (success) {
                      setEditingSocialEntry(null);
                      setSocialFormData({
                        followers: '',
                        views: '',
                        likes: '',
                        comments: '',
                        shares: '',
                        month: '',
                        year: new Date().getFullYear().toString()
                      });
                    } else {
                      alert('Error updating entry. Please try again.');
                    }
                  }} className="grid grid-cols-3 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Followers
                      </label>
                      <input
                        type="number"
                        value={socialFormData.followers}
                        onChange={(e) => setSocialFormData({...socialFormData, followers: e.target.value})}
                        className={`w-full p-2 border rounded ${
                          darkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        min="0"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Views
                      </label>
                      <input
                        type="number"
                        value={socialFormData.views}
                        onChange={(e) => setSocialFormData({...socialFormData, views: e.target.value})}
                        className={`w-full p-2 border rounded ${
                          darkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        min="0"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Likes
                      </label>
                      <input
                        type="number"
                        value={socialFormData.likes}
                        onChange={(e) => setSocialFormData({...socialFormData, likes: e.target.value})}
                        className={`w-full p-2 border rounded ${
                          darkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        min="0"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Comments
                      </label>
                      <input
                        type="number"
                        value={socialFormData.comments}
                        onChange={(e) => setSocialFormData({...socialFormData, comments: e.target.value})}
                        className={`w-full p-2 border rounded ${
                          darkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        min="0"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Shares
                      </label>
                      <input
                        type="number"
                        value={socialFormData.shares}
                        onChange={(e) => setSocialFormData({...socialFormData, shares: e.target.value})}
                        className={`w-full p-2 border rounded ${
                          darkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        min="0"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Month
                      </label>
                      <select
                        value={socialFormData.month}
                        onChange={(e) => setSocialFormData({...socialFormData, month: e.target.value})}
                        className={`w-full p-2 border rounded ${
                          darkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        <option value="">Select Month</option>
                        <option value="January">January</option>
                        <option value="February">February</option>
                        <option value="March">March</option>
                        <option value="April">April</option>
                        <option value="May">May</option>
                        <option value="June">June</option>
                        <option value="July">July</option>
                        <option value="August">August</option>
                        <option value="September">September</option>
                        <option value="October">October</option>
                        <option value="November">November</option>
                        <option value="December">December</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Year
                      </label>
                      <select
                        value={socialFormData.year}
                        onChange={(e) => setSocialFormData({...socialFormData, year: e.target.value})}
                        className={`w-full p-2 border rounded ${
                          darkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end space-x-2">
                      <button
                        type="submit"
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Update
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingSocialEntry(null)}
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Entries List */}
              <div className="space-y-2">
                <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  All Entries
                </h4>
                {platformEntries[editingSocialPlatform]?.length === 0 ? (
                  <p className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No entries found. Add your first entry using the + button.
                  </p>
                ) : (
                  platformEntries[editingSocialPlatform]?.map(entry => {
                    const platformIcons = {
                      facebook: '/facebook.svg',
                      instagram: '/instagram.svg',
                      tiktok: '/tiktok.svg',
                      youtube: '/Youtube.svg'
                    };
                    
                    return (
                    <div key={entry.id} className={`flex items-center justify-between p-4 rounded-lg ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="w-12 h-12 flex items-center justify-center">
                          <img 
                            src={platformIcons[editingSocialPlatform]} 
                            alt={`${editingSocialPlatform} icon`}
                            className="w-10 h-10"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-4">
                            <span className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                              {entry.followers.toLocaleString()}
                            </span>
                            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {entry.month} {entry.year}
                            </span>
                          </div>
                          <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Views: {entry.views.toLocaleString()} | Likes: {entry.likes.toLocaleString()} | Comments: {entry.comments.toLocaleString()} | Shares: {entry.shares.toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setEditingSocialEntry(entry);
                            setSocialFormData({
                              followers: entry.followers.toString(),
                              views: entry.views.toString(),
                              likes: entry.likes.toString(),
                              comments: entry.comments.toString(),
                              shares: entry.shares.toString(),
                              month: entry.month,
                              year: entry.year.toString()
                            });
                          }}
                          className={`p-1 rounded ${
                            darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                          }`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to delete this entry?')) {
                              const success = await deleteSocialMediaEntry(editingSocialPlatform, entry.id);
                              if (!success) {
                                alert('Error deleting entry. Please try again.');
                              }
                            }
                          }}
                          className={`p-1 rounded text-red-500 hover:bg-red-100 ${
                            darkMode ? 'hover:bg-red-900' : 'hover:bg-red-100'
                          }`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    );
                  })
                )}
              </div>

              {/* Add New Entry Form */}
              {showSocialAddPopup && (
                <div className={`p-4 rounded-lg mt-4 ${
                  darkMode ? 'bg-gray-700' : 'bg-gray-50'
                }`}>
                  <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Add New Entry
                  </h4>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!socialFormData.followers || !socialFormData.month || !socialFormData.year) {
                      alert('Please fill in all required fields (Followers, Month, Year)');
                      return;
                    }
                    
                    const success = await saveSocialMediaEntry(editingSocialPlatform, {
                      followers: parseInt(socialFormData.followers) || 0,
                      views: parseInt(socialFormData.views) || 0,
                      likes: parseInt(socialFormData.likes) || 0,
                      comments: parseInt(socialFormData.comments) || 0,
                      shares: parseInt(socialFormData.shares) || 0,
                      month: socialFormData.month,
                      year: parseInt(socialFormData.year)
                    });
                    
                    if (success) {
                      setShowSocialAddPopup(false);
                      setSocialFormData({
                        followers: '',
                        views: '',
                        likes: '',
                        comments: '',
                        shares: '',
                        month: '',
                        year: new Date().getFullYear().toString()
                      });
                    } else {
                      alert('Error saving entry. Please try again.');
                    }
                  }} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Followers
                        </label>
                        <input
                          type="number"
                          value={socialFormData.followers}
                          onChange={(e) => setSocialFormData({...socialFormData, followers: e.target.value})}
                          className={`w-full p-2 border rounded ${
                            darkMode 
                              ? 'bg-gray-600 border-gray-500 text-white' 
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Views
                        </label>
                        <input
                          type="number"
                          value={socialFormData.views}
                          onChange={(e) => setSocialFormData({...socialFormData, views: e.target.value})}
                          className={`w-full p-2 border rounded ${
                            darkMode 
                              ? 'bg-gray-600 border-gray-500 text-white' 
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Likes
                        </label>
                        <input
                          type="number"
                          value={socialFormData.likes}
                          onChange={(e) => setSocialFormData({...socialFormData, likes: e.target.value})}
                          className={`w-full p-2 border rounded ${
                            darkMode 
                              ? 'bg-gray-600 border-gray-500 text-white' 
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Comments
                        </label>
                        <input
                          type="number"
                          value={socialFormData.comments}
                          onChange={(e) => setSocialFormData({...socialFormData, comments: e.target.value})}
                          className={`w-full p-2 border rounded ${
                            darkMode 
                              ? 'bg-gray-600 border-gray-500 text-white' 
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Shares
                        </label>
                        <input
                          type="number"
                          value={socialFormData.shares}
                          onChange={(e) => setSocialFormData({...socialFormData, shares: e.target.value})}
                          className={`w-full p-2 border rounded ${
                            darkMode 
                              ? 'bg-gray-600 border-gray-500 text-white' 
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Month
                        </label>
                        <select
                          value={socialFormData.month}
                          onChange={(e) => setSocialFormData({...socialFormData, month: e.target.value})}
                          className={`w-full p-2 border rounded ${
                            darkMode 
                              ? 'bg-gray-600 border-gray-500 text-white' 
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        >
                          <option value="">Select Month</option>
                          <option value="January">January</option>
                          <option value="February">February</option>
                          <option value="March">March</option>
                          <option value="April">April</option>
                          <option value="May">May</option>
                          <option value="June">June</option>
                          <option value="July">July</option>
                          <option value="August">August</option>
                          <option value="September">September</option>
                          <option value="October">October</option>
                          <option value="November">November</option>
                          <option value="December">December</option>
                        </select>
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Year
                        </label>
                        <select
                          value={socialFormData.year}
                          onChange={(e) => setSocialFormData({...socialFormData, year: e.target.value})}
                          className={`w-full p-2 border rounded ${
                            darkMode 
                              ? 'bg-gray-600 border-gray-500 text-white' 
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        >
                          {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex space-x-2 pt-2">
                      <button
                        type="submit"
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Add Entry
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowSocialAddPopup(false);
                          setSocialFormData({
                            followers: '',
                            views: '',
                            likes: '',
                            comments: '',
                            shares: '',
                            month: '',
                            year: new Date().getFullYear().toString()
                          });
                        }}
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
        })()}
      </div>
    );
  };

  const renderLeadsCalculatorView = () => {
    return <LeadsCalculatorContent 
      selectedAccount={selectedAccount} 
      leadsCalculatorData={leadsCalculatorData}
      leadsCalculatorLoading={leadsCalculatorLoading}
    />;
  };

  const renderLeadsROIView = () => {
    return <LeadsROIContent 
      selectedAccount={selectedAccount}
      leadsROIData={leadsROIData}
      leadsROILoading={leadsROILoading}
      onDataSaved={() => loadLeadsROIData(selectedAccount.id)}
    />;
  };

  const renderMarginCalculatorView = () => {
    if (marginCalculatorLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <FaSpinner className="animate-spin text-2xl text-blue-500" />
        </div>
      );
    }


    // Find the scenario closest to the starting GPM
    const currentScenario = marginCalculatorScenarios.find(s => Math.abs(s.gpm - marginCalculatorInputs.startingGPM) < 0.0001) || 
      (marginCalculatorScenarios.length > 0 ? marginCalculatorScenarios[Math.floor(marginCalculatorScenarios.length / 2)] : null);

    return (
      <div className="space-y-6">

        {/* GPM Inputs Section */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-800">
          <div className="mb-8 flex justify-center space-x-4">
            <div className="border rounded-lg p-4 shadow-sm w-64 text-center border-gray-200 dark:border-gray-600">
              <label className="block text-xl font-semibold mb-2 text-gray-700 dark:text-white">Target GPM</label>
              <div className="relative rounded-md shadow-sm">
                <input
                  type="number"
                  name="startingGPM"
                  value={marginCalculatorInputs.startingGPM * 100}
                  onChange={handleMarginCalculatorInputChange}
                  className="block w-full rounded-md pl-3 pr-12 focus:border-blue-500 focus:ring-blue-500 text-lg text-center bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                  placeholder="30"
                  min="0"
                  max="100"
                  step="0.5"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="sm:text-sm text-gray-500 dark:text-gray-400">%</span>
                </div>
              </div>
            </div>
            
            <div className="border rounded-lg p-4 shadow-sm w-64 text-center border-gray-200 dark:border-gray-600">
              <label className="block text-xl font-semibold mb-2 text-gray-700 dark:text-white">True GPM</label>
              <div className="relative rounded-md shadow-sm">
                <input
                  type="number"
                  name="trueGPM"
                  value={marginCalculatorInputs.trueGPM * 100}
                  onChange={handleMarginCalculatorInputChange}
                  className="block w-full rounded-md pl-3 pr-12 focus:border-blue-500 focus:ring-blue-500 text-lg text-center bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                  placeholder="25"
                  min="0"
                  max="100"
                  step="0.5"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="sm:text-sm text-gray-500 dark:text-gray-400">%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Summarized Outputs Section with Tabs */}
        {currentScenario && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-800 mb-8">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex justify-center space-x-8" aria-label="Tabs">
                {['Annual', 'Quarterly', 'Monthly'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setMarginCalculatorActiveTab(tab.toLowerCase())}
                    className={`${
                      marginCalculatorActiveTab === tab.toLowerCase()
                        ? 'border-red-500 text-red-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600'
                    } whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>
            
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="border rounded-lg p-4 shadow-sm flex flex-col justify-center items-center border-gray-200 dark:border-gray-600">
                <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Break-Even Sales</div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {marginCalculatorActiveTab === 'annual' 
                    ? formatMarginCalculatorCurrency(currentScenario.salesBreakeven)
                    : marginCalculatorActiveTab === 'quarterly'
                      ? formatMarginCalculatorCurrency(currentScenario.salesBreakeven / 4)
                      : formatMarginCalculatorCurrency(currentScenario.salesBreakeven / 12)
                  }
                </div>
              </div>
              
              <div className="border rounded-lg p-4 shadow-sm flex flex-col justify-center items-center border-gray-200 dark:border-gray-600">
                <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Sales to Profit</div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {marginCalculatorActiveTab === 'annual' 
                    ? formatMarginCalculatorCurrency(currentScenario.annual.sales)
                    : marginCalculatorActiveTab === 'quarterly'
                      ? formatMarginCalculatorCurrency(currentScenario.quarterly.sales)
                      : formatMarginCalculatorCurrency(currentScenario.monthly.sales)
                  }
                </div>
              </div>
              
              <div className="border rounded-lg p-4 shadow-sm flex flex-col justify-center items-center border-gray-200 dark:border-gray-600">
                <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Gross Profit</div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {marginCalculatorActiveTab === 'annual' 
                    ? formatMarginCalculatorCurrency(currentScenario.annual.grossProfit)
                    : marginCalculatorActiveTab === 'quarterly'
                      ? formatMarginCalculatorCurrency(currentScenario.quarterly.grossProfit)
                      : formatMarginCalculatorCurrency(currentScenario.monthly.grossProfit)
                  }
                </div>
              </div>
              
              <div className="border rounded-lg p-4 shadow-sm flex flex-col justify-center items-center border-gray-200 dark:border-gray-600">
                <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Overhead</div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {marginCalculatorActiveTab === 'annual' 
                    ? formatMarginCalculatorCurrency(currentScenario.annual.overhead)
                    : marginCalculatorActiveTab === 'quarterly'
                      ? formatMarginCalculatorCurrency(currentScenario.quarterly.overhead)
                      : formatMarginCalculatorCurrency(currentScenario.monthly.overhead)
                  }
                </div>
              </div>
              
              <div className="border rounded-lg p-4 shadow-sm flex flex-col justify-center items-center border-gray-200 dark:border-gray-600">
                <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Earnings Before Taxes</div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {marginCalculatorActiveTab === 'annual' 
                    ? formatMarginCalculatorCurrency(currentScenario.annual.ebt)
                    : marginCalculatorActiveTab === 'quarterly'
                      ? formatMarginCalculatorCurrency(currentScenario.quarterly.ebt)
                      : formatMarginCalculatorCurrency(currentScenario.monthly.ebt)
                  }
                </div>
              </div>
              
              <div className="border rounded-lg p-4 shadow-sm flex flex-col justify-center items-center border-gray-200 dark:border-gray-600">
                <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Taxes</div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {marginCalculatorActiveTab === 'annual' 
                    ? formatMarginCalculatorCurrency(currentScenario.annual.taxes)
                    : marginCalculatorActiveTab === 'quarterly'
                      ? formatMarginCalculatorCurrency(currentScenario.quarterly.taxes)
                      : formatMarginCalculatorCurrency(currentScenario.monthly.taxes)
                  }
                </div>
              </div>
              
              <div className="border rounded-lg p-4 shadow-sm flex flex-col justify-center items-center border-gray-200 dark:border-gray-600">
                <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Net Profit</div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {marginCalculatorActiveTab === 'annual' 
                    ? formatMarginCalculatorCurrency(currentScenario.annual.netProfit)
                    : marginCalculatorActiveTab === 'quarterly'
                      ? formatMarginCalculatorCurrency(currentScenario.quarterly.netProfit)
                      : formatMarginCalculatorCurrency(currentScenario.monthly.netProfit)
                  }
                </div>
              </div>
              
              <div className="border rounded-lg p-4 shadow-sm flex flex-col justify-center items-center border-gray-200 dark:border-gray-600">
                <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Profit Margin</div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {formatMarginCalculatorPercent(currentScenario.annual.profitMargin)}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Inputs Section */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-800 mb-8">
          <h2 className="text-xl font-semibold mb-4 text-center text-gray-700 dark:text-white">Inputs</h2>
          <div className="flex justify-center space-x-4">
            <div className="border rounded-lg p-4 shadow-sm w-64 text-center border-gray-200 dark:border-gray-600">
              <label className="block text-xl font-semibold mb-2 text-gray-700 dark:text-white">Assumed Tax Rate</label>
              <div className="relative rounded-md shadow-sm">
                <input
                  type="number"
                  name="taxRate"
                  value={marginCalculatorInputs.taxRate * 100}
                  onChange={handleMarginCalculatorInputChange}
                  className="block w-full rounded-md pl-3 pr-12 focus:border-blue-500 focus:ring-blue-500 text-lg text-center bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                  placeholder="20"
                  min="0"
                  max="100"
                  step="0.1"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="sm:text-sm text-gray-500 dark:text-gray-400">%</span>
                </div>
              </div>
            </div>
            
            <div className="border rounded-lg p-4 shadow-sm w-64 text-center border-gray-200 dark:border-gray-600">
              <label className="block text-xl font-semibold mb-2 text-gray-700 dark:text-white">Profitability Desire</label>
              <div className="relative rounded-md shadow-sm">
                <input
                  type="number"
                  name="profitabilityDesire"
                  value={marginCalculatorInputs.profitabilityDesire * 100}
                  onChange={handleMarginCalculatorInputChange}
                  className="block w-full rounded-md pl-3 pr-12 focus:border-blue-500 focus:ring-blue-500 text-lg text-center bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                  placeholder="11"
                  min="0"
                  max="100"
                  step="0.1"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="sm:text-sm text-gray-500 dark:text-gray-400">%</span>
                </div>
              </div>
            </div>
            
            <div className="border rounded-lg p-4 shadow-sm w-64 text-center border-gray-200 dark:border-gray-600">
              <label className="block text-xl font-semibold mb-2 text-gray-700 dark:text-white">Overhead</label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <span className="sm:text-sm text-gray-500 dark:text-gray-400">$</span>
                </div>
                <input
                  type="number"
                  name="overhead"
                  value={marginCalculatorInputs.overhead}
                  onChange={handleMarginCalculatorInputChange}
                  className="block w-full rounded-md pl-7 pr-3 focus:border-blue-500 focus:ring-blue-500 text-lg text-center bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                  placeholder="260000"
                  min="0"
                  step="1000"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Table Section */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-800">
          <h2 className="text-xl font-semibold mb-4 text-center text-gray-700 dark:text-white">Scenario Analysis</h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">GPM</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Break-Even Sales</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Sales to Profit Goal</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Gross Profit</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Overhead</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">EBT</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Taxes</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Net Profit</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Profit Margin</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {marginCalculatorScenarios.map((scenario, index) => (
                  <tr 
                    key={index} 
                    className={Math.abs(scenario.gpm - marginCalculatorInputs.startingGPM) < 0.0001 ? "bg-red-50 dark:bg-red-900/30" : ""}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {formatMarginCalculatorPercent(scenario.gpm)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {formatMarginCalculatorCurrency(scenario.salesBreakeven)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {scenario.salesToProfitGoal === Infinity 
                        ? "N/A" 
                        : formatMarginCalculatorCurrency(scenario.salesToProfitGoal)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {formatMarginCalculatorCurrency(scenario.annual.grossProfit)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {formatMarginCalculatorCurrency(scenario.annual.overhead)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {formatMarginCalculatorCurrency(scenario.annual.ebt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {formatMarginCalculatorCurrency(scenario.annual.taxes)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {formatMarginCalculatorCurrency(scenario.annual.netProfit)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {formatMarginCalculatorPercent(scenario.annual.profitMargin)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Add Campaign Modal Component
  const AddCampaignModal = ({ show, onClose, onAdd }) => {
    const [formData, setFormData] = useState({
      name: '',
      description: '',
      platform: '',
      budget: '',
      startDate: ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onAdd({
        ...formData,
        budget: parseFloat(formData.budget),
        status: 'active'
      });
      setFormData({ name: '', description: '', platform: '', budget: '', startDate: '' });
    };

    if (!show) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md mx-auto">
          <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white text-center">Add New Campaign</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Campaign Name</label>
              <input
                type="text"
                required
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe your campaign goals and target audience..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Platform</label>
              <select
                required
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={formData.platform}
                onChange={(e) => setFormData({...formData, platform: e.target.value})}
              >
                <option value="">Select Platform</option>
                <option value="Meta">Meta (Facebook/Instagram)</option>
                <option value="Google">Google Ads</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Budget ($)</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={formData.budget}
                onChange={(e) => setFormData({...formData, budget: e.target.value})}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
              <input
                type="date"
                required
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={formData.startDate}
                onChange={(e) => setFormData({...formData, startDate: e.target.value})}
              />
            </div>
            
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
              >
                Add Campaign
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Campaign Details Modal Component
  const CampaignDetailsModal = ({ campaign, onClose, onUpdate, onDelete }) => {
    const [activeTab, setActiveTab] = useState('details');
    const [editMode, setEditMode] = useState(false);
    const [metrics, setMetrics] = useState(campaign?.metrics || {});
    const [campaignData, setCampaignData] = useState({
      name: '',
      description: '',
      platform: '',
      startDate: '',
      endDate: '',
      status: '',
      spent: 0,
      budget: 0
    });

    useEffect(() => {
      if (campaign) {
        setMetrics(campaign.metrics);
        setCampaignData({
          name: campaign.name || '',
          description: campaign.description || '',
          platform: campaign.platform || '',
          startDate: campaign.startDate || '',
          endDate: campaign.endDate || '',
          status: campaign.status || '',
          spent: campaign.spent || 0,
          budget: campaign.budget || 0
        });
      }
    }, [campaign]);

    const handleSave = () => {
      const updateData = {
        ...campaignData,
        metrics: {
          ...metrics,
          impressions: metrics.impressions || 0,
          clicks: metrics.clicks || 0,
          conversions: metrics.conversions || 0,
          ctr: metrics.ctr || 0,
          cpc: metrics.cpc || 0,
          cpm: metrics.cpm || 0,
          roas: metrics.roas || 0,
          revenue: metrics.revenue || 0
        }
      };
      onUpdate(campaign.id, updateData);
      setEditMode(false);
    };

    const handleDelete = () => {
      if (window.confirm(`Are you sure you want to delete "${campaign.name}"? This action cannot be undone.`)) {
        onDelete(campaign.id);
        onClose();
      }
    };

    if (!campaign) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <div className="flex-1"></div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center flex-1">{campaign.name}</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex-1 flex justify-end"
            >
              ✕
            </button>
          </div>
            
          {/* Tabs */}
          <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 mb-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'details'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('metrics')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'metrics'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Metrics
            </button>
          </div>

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Name</label>
                  {editMode ? (
                    <input
                      type="text"
                      className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      value={campaignData.name}
                      onChange={(e) => setCampaignData({...campaignData, name: e.target.value})}
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                      <span className="text-gray-900 dark:text-white">{campaignData.name}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Platform</label>
                  {editMode ? (
                    <select
                      className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      value={campaignData.platform}
                      onChange={(e) => setCampaignData({...campaignData, platform: e.target.value})}
                    >
                      <option value="">Select Platform</option>
                      <option value="Meta">Meta (Facebook/Instagram)</option>
                      <option value="Google">Google Ads</option>
                    </select>
                  ) : (
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                      <span className="text-gray-900 dark:text-white">{campaignData.platform}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Date</label>
                  {editMode ? (
                    <input
                      type="date"
                      className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      value={campaignData.startDate}
                      onChange={(e) => setCampaignData({...campaignData, startDate: e.target.value})}
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                      <span className="text-gray-900 dark:text-white">{campaignData.startDate}</span>
                    </div>
                  )}
                </div>
                {campaignData.status === 'completed' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">End Date</label>
                    {editMode ? (
                      <input
                        type="date"
                        className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        value={campaignData.endDate}
                        onChange={(e) => setCampaignData({...campaignData, endDate: e.target.value})}
                      />
                    ) : (
                      <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                        <span className="text-gray-900 dark:text-white">{campaignData.endDate}</span>
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                  {editMode ? (
                    <select
                      className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      value={campaignData.status}
                      onChange={(e) => setCampaignData({...campaignData, status: e.target.value})}
                    >
                      <option value="">Select Status</option>
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="completed">Completed</option>
                    </select>
                  ) : (
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                      <span className="text-gray-900 dark:text-white">{campaignData.status.charAt(0).toUpperCase() + campaignData.status.slice(1)}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Budget</label>
                  {editMode ? (
                    <input
                      type="number"
                      className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      value={campaignData.budget}
                      onChange={(e) => setCampaignData({...campaignData, budget: parseFloat(e.target.value) || 0})}
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                      <span className="text-gray-900 dark:text-white">${campaignData.budget.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                {editMode ? (
                  <textarea
                    className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    rows="3"
                    value={campaignData.description}
                    onChange={(e) => setCampaignData({...campaignData, description: e.target.value})}
                  />
                ) : (
                  <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                    <span className="text-gray-900 dark:text-white">{campaignData.description || 'No description provided'}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Metrics Tab */}
          {activeTab === 'metrics' && (
            <div className="space-y-6">
              {editMode ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Expenditure</label>
                      <input
                        type="number"
                        className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        value={campaignData.spent}
                        onChange={(e) => setCampaignData({...campaignData, spent: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Reach</label>
                      <input
                        type="number"
                        className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        value={Math.round((metrics.impressions || 0) * 0.75)}
                        onChange={(e) => {
                          const reach = parseInt(e.target.value) || 0;
                          const impressions = Math.round(reach / 0.75);
                          setMetrics({...metrics, impressions: impressions});
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Clicks</label>
                      <input
                        type="number"
                        className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        value={metrics.clicks || 0}
                        onChange={(e) => setMetrics({...metrics, clicks: parseInt(e.target.value) || 0})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Cost Per Click</label>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        value={campaignData.spent / (metrics.clicks || 1)}
                        onChange={(e) => {
                          const cpc = parseFloat(e.target.value) || 0;
                          const newClicks = Math.round(campaignData.spent / cpc) || 0;
                          setMetrics({...metrics, clicks: newClicks});
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">${campaignData.spent.toLocaleString()}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Expenditure</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{Math.round(metrics.impressions * 0.75).toLocaleString()}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Reach</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.clicks?.toLocaleString() || 0}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Clicks</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">${(campaignData.spent / (metrics.clicks || 1)).toFixed(2)}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Cost Per Click</p>
                  </div>
                </div>
              )}
            </div>
          )}
        
          <div className="flex justify-between mt-6">
            <button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
            >
              Delete Campaign
            </button>
            <div className="flex space-x-3">
              <button
                onClick={() => setEditMode(!editMode)}
                className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
              >
                {editMode ? 'Cancel Edit' : 'Edit'}
              </button>
              {editMode && (
                <button
                  onClick={handleSave}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
                >
                  Save
                </button>
              )}
              <button
                onClick={onClose}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPaidMediaView = () => {
    // Calculate overall metrics from campaigns (same as PaidMediaContent)
    const overallMetrics = campaigns.reduce((acc, campaign) => {
      const metrics = campaign.metrics || {};
      acc.impressions += metrics.impressions || 0;
      acc.clicks += metrics.clicks || 0;
      acc.spent += campaign.spent || 0;
      acc.revenue += metrics.revenue || 0;
      return acc;
    }, { impressions: 0, clicks: 0, spent: 0, revenue: 0 });

    overallMetrics.ctr = overallMetrics.impressions > 0 ? (overallMetrics.clicks / overallMetrics.impressions * 100).toFixed(1) : 0;
    overallMetrics.cpc = overallMetrics.clicks > 0 ? (overallMetrics.spent / overallMetrics.clicks).toFixed(2) : 0;
    overallMetrics.roas = overallMetrics.spent > 0 ? (overallMetrics.revenue / overallMetrics.spent).toFixed(2) : 0;
    overallMetrics.reach = Math.round(overallMetrics.impressions * 0.75); // Estimated reach (75% of impressions)

    // Generate chart data for paid media campaigns performance
    const generatePaidMediaChartData = () => {
      if (timeRange === 'currentYear') {
        // Show monthly data for current year
        const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        return monthOrder.map((month, index) => {
          let totalSpent = 0;
          let totalClicks = 0;
          
          campaigns.forEach(campaign => {
            // Check if campaign has data for this month
            // For now, we'll distribute the total campaign data across all months
            // In a real implementation, you'd have actual monthly breakdown data
            const monthlySpent = (campaign.spent || 0) / 12; // Distribute yearly spent across 12 months
            const monthlyClicks = ((campaign.metrics?.clicks || 0) / 12); // Distribute yearly clicks across 12 months
            totalSpent += monthlySpent;
            totalClicks += monthlyClicks;
          });
          
          return {
            month: month,
            spent: totalSpent,
            clicks: totalClicks,
            cpc: totalClicks > 0 ? totalSpent / totalClicks : 0
          };
        });
      } else {
        // Show yearly data
        const currentYear = new Date().getFullYear();
        const years = [currentYear - 2, currentYear - 1, currentYear];
        
        return years.map(year => {
          let totalSpent = 0;
          let totalClicks = 0;
          
          campaigns.forEach(campaign => {
            // Use actual campaign data for the current year
            // For previous years, we'll show a portion of current data as historical
            if (year === currentYear) {
              totalSpent += campaign.spent || 0;
              totalClicks += campaign.metrics?.clicks || 0;
            } else {
              // For previous years, show a portion of current data
              const yearMultiplier = year === currentYear - 1 ? 0.8 : 0.6;
              totalSpent += (campaign.spent || 0) * yearMultiplier;
              totalClicks += (campaign.metrics?.clicks || 0) * yearMultiplier;
            }
          });
          
          return {
            month: year.toString(),
            spent: totalSpent,
            clicks: totalClicks,
            cpc: totalClicks > 0 ? totalSpent / totalClicks : 0
          };
        });
      }
    };

    // Campaign Performance Chart Component
    const CampaignPerformanceChart = () => {
      const chartData = generatePaidMediaChartData();

      return (
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center">
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mr-4`}>
                Cost Per Click
              </h3>
              <div className="flex space-x-2">
                <button 
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    timeRange === 'currentYear' 
                      ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  onClick={() => setTimeRange('currentYear')}
                >
                  This Year
                </button>
                <button 
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    timeRange === 'allTime' 
                      ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  onClick={() => setTimeRange('allTime')}
                >
                  All Time
                </button>
              </div>
            </div>
          </div>
          
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [`$${value.toFixed(2)}`, 'Cost Per Click']}
                  labelFormatter={(label) => timeRange === 'allTime' ? `Year: ${label}` : `Month: ${label}`}
                />
                <Line 
                  type="monotone" 
                  dataKey="cpc" 
                  name="Cost Per Click" 
                  stroke="#DC2626" 
                  strokeWidth={2}
                  dot={{ r: 6 }}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    };

    // Always show the paid media tab content, even when there's no data

    // Metric Card Component (same as PaidMediaContent)

    // Campaign Card Component (same as PaidMediaContent)
    const CampaignCard = ({ campaign, onClick, isSelected }) => {
      const platformIcons = {
        'Meta': '/Meta.svg',
        'Google': '/googleads.svg'
      };

      return (
        <div 
          onClick={() => onClick(campaign)}
          className={`p-6 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
            isSelected 
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
              : `${darkMode ? 'border-gray-700 bg-gray-800 hover:border-gray-600' : 'border-gray-200 bg-white hover:border-gray-300'}`
          }`}
        >
          <div className="text-center">
            {/* Platform Image */}
            <div className="mb-4">
              <img 
                src={platformIcons[campaign.platform]} 
                alt={`${campaign.platform} icon`}
                className="w-12 h-12 mx-auto"
              />
            </div>
            
            {/* Campaign Name */}
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-3`}>{campaign.name}</h3>
            
            {/* Status Pill */}
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              campaign.status === 'active' 
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : campaign.status === 'paused'
                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
            }`}>
              {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
            </span>
          </div>
        </div>
      );
    };

    // Top Platforms Card Component (same as PaidMediaContent)
    const TopPlatformsCard = () => {
      const platformIcons = {
        meta: '/Meta.svg',
        google: '/googleads.svg'
      };

      // Calculate platform performance
      const platformData = [
        {
          platform: 'Meta',
          platformKey: 'meta',
          spent: campaigns.filter(c => c.platform === 'Meta').reduce((sum, c) => sum + (c.spent || 0), 0),
          revenue: campaigns.filter(c => c.platform === 'Meta').reduce((sum, c) => sum + (c.metrics?.revenue || 0), 0)
        },
        {
          platform: 'Google',
          platformKey: 'google',
          spent: campaigns.filter(c => c.platform === 'Google').reduce((sum, c) => sum + (c.spent || 0), 0),
          revenue: campaigns.filter(c => c.platform === 'Google').reduce((sum, c) => sum + (c.metrics?.revenue || 0), 0)
        }
      ].sort((a, b) => b.revenue - a.revenue);

      return (
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
          <h3 className={`text-xl font-bold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Top Platforms
          </h3>
          
          <div className="space-y-3">
            {platformData.map((platform, index) => (
              <div key={platform.platform} className={`p-3 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-4 ${
                    index === 0 ? 'bg-yellow-500 text-white' : 'bg-gray-400 text-white'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="w-8 h-8 mr-4 flex items-center justify-center">
                    <img 
                      src={platformIcons[platform.platformKey]} 
                      alt={`${platform.platform} icon`}
                      className="w-6 h-6"
                    />
                  </div>
                  <div className="flex-1">
                    <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {platform.platform}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };

    // Total Ads Count Card Component (same as PaidMediaContent)
    const TotalAdsCountCard = () => {
      const metaAds = campaigns.filter(c => c.platform === 'Meta').length;
      const googleAds = campaigns.filter(c => c.platform === 'Google').length;

      return (
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4`}>
          <h3 className={`text-lg font-bold mb-3 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Total Ads Count
          </h3>
          
          <div className="grid grid-cols-2 gap-3">
            <div className={`text-center p-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg`}>
              <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{metaAds}</div>
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Meta</div>
            </div>
            <div className={`text-center p-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg`}>
              <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{googleAds}</div>
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Google</div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="w-full">
        {/* Overview Tab (same layout as PaidMediaContent) */}
        <div className="space-y-6">
          {/* Campaign Performance Chart and Sidebar */}
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div className="col-span-3">
              <CampaignPerformanceChart />
            </div>
            <div className="col-span-1 flex flex-col h-full">
              <div className="flex-1 space-y-4">
                <TopPlatformsCard />
                <TotalAdsCountCard />
              </div>
            </div>
          </div>

          {/* Platform Performance Cards (same as PaidMediaContent) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Expenditure Card */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 text-center`}>Expenditure</h3>
              <div className="space-y-3">
                <div className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <div className="flex items-center">
                    <img src="/Meta.svg" alt="Meta" className="w-6 h-6 mr-2" />
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Meta</span>
                  </div>
                  <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    ${campaigns.filter(c => c.platform === 'Meta').reduce((sum, c) => sum + (c.spent || 0), 0).toLocaleString()}
                  </span>
                </div>
                <div className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <div className="flex items-center">
                    <img src="/googleads.svg" alt="Google" className="w-6 h-6 mr-2" />
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Google</span>
                  </div>
                  <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    ${campaigns.filter(c => c.platform === 'Google').reduce((sum, c) => sum + (c.spent || 0), 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Reach Card */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 text-center`}>Reach</h3>
              <div className="space-y-3">
                <div className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <div className="flex items-center">
                    <img src="/Meta.svg" alt="Meta" className="w-6 h-6 mr-2" />
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Meta</span>
                  </div>
                  <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {Math.round(campaigns.filter(c => c.platform === 'Meta').reduce((sum, c) => sum + (c.metrics?.impressions || 0), 0) * 0.75).toLocaleString()}
                  </span>
                </div>
                <div className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <div className="flex items-center">
                    <img src="/googleads.svg" alt="Google" className="w-6 h-6 mr-2" />
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Google</span>
                  </div>
                  <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {Math.round(campaigns.filter(c => c.platform === 'Google').reduce((sum, c) => sum + (c.metrics?.impressions || 0), 0) * 0.75).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Clicks Card */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 text-center`}>Clicks</h3>
              <div className="space-y-3">
                <div className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <div className="flex items-center">
                    <img src="/Meta.svg" alt="Meta" className="w-6 h-6 mr-2" />
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Meta</span>
                  </div>
                  <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {campaigns.filter(c => c.platform === 'Meta').reduce((sum, c) => sum + (c.metrics?.clicks || 0), 0).toLocaleString()}
                  </span>
                </div>
                <div className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <div className="flex items-center">
                    <img src="/googleads.svg" alt="Google" className="w-6 h-6 mr-2" />
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Google</span>
                  </div>
                  <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {campaigns.filter(c => c.platform === 'Google').reduce((sum, c) => sum + (c.metrics?.clicks || 0), 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Cost Per Click Card */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 text-center`}>Cost Per Click</h3>
              <div className="space-y-3">
                <div className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <div className="flex items-center">
                    <img src="/Meta.svg" alt="Meta" className="w-6 h-6 mr-2" />
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Meta</span>
                  </div>
                  <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    ${(campaigns.filter(c => c.platform === 'Meta').reduce((sum, c) => sum + (c.spent || 0), 0) / 
                        campaigns.filter(c => c.platform === 'Meta').reduce((sum, c) => sum + (c.metrics?.clicks || 0), 0) || 0).toFixed(2)}
                  </span>
                </div>
                <div className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <div className="flex items-center">
                    <img src="/googleads.svg" alt="Google" className="w-6 h-6 mr-2" />
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Google</span>
                  </div>
                  <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    ${(campaigns.filter(c => c.platform === 'Google').reduce((sum, c) => sum + (c.spent || 0), 0) / 
                        campaigns.filter(c => c.platform === 'Google').reduce((sum, c) => sum + (c.metrics?.clicks || 0), 0) || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Campaigns Grid (same as PaidMediaContent) */}
          <div className="space-y-6">
            <div className="flex justify-center items-center relative">
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>All Campaigns</h2>
              <button
                onClick={() => setShowAddCampaign(true)}
                className="absolute right-0 bg-red-600 hover:bg-red-700 text-white rounded-full p-3 transition duration-300 flex items-center justify-center shadow-lg hover:shadow-xl"
                title="Add New Campaign"
              >
                <Plus className="w-6 h-6" />
              </button>
            </div>
            
            {/* Campaign Status Overview */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 text-center`}>Campaign Status</h3>
              <div className="grid grid-cols-3 gap-4">
                {['active', 'paused', 'completed'].map((status) => {
                  const statusCampaigns = campaigns.filter(c => c.status === status);
                  return (
                    <div key={status} className={`text-center p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg`}>
                      <span className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{statusCampaigns.length}</span>
                      <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} capitalize mt-1`}>{status}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {campaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  onClick={() => setSelectedCampaign(campaign)}
                  isSelected={selectedCampaign?.id === campaign.id}
                />
              ))}
            </div>
          </div>

          {/* Campaign Details Modal */}
          <CampaignDetailsModal 
            campaign={selectedCampaign}
            onClose={() => setSelectedCampaign(null)}
            onUpdate={updateCampaignMetrics}
            onDelete={deleteCampaign}
          />

          {/* Add Campaign Modal */}
          <AddCampaignModal 
            show={showAddCampaign}
            onClose={() => setShowAddCampaign(false)}
            onAdd={addCampaign}
          />

        </div>
      </div>
    );
  };

  const renderNewsletterView = () => {
    // Calculate overall metrics from newsletter campaigns (same as NewsletterContent)
    const overallMetrics = newsletterCampaigns.reduce((acc, campaign) => {
      acc.subscribers += campaign.subscribers || 0;
      acc.sent += campaign.sent || 0;
      acc.revenue += campaign.metrics?.revenue || 0;
      acc.totalOpens += Math.round(((campaign.sent || 0) * (campaign.metrics?.openRate || 0)) / 100);
      acc.totalClicks += Math.round(((campaign.sent || 0) * (campaign.metrics?.clickRate || 0)) / 100);
      return acc;
    }, { subscribers: 0, sent: 0, revenue: 0, totalOpens: 0, totalClicks: 0 });

    overallMetrics.avgOpenRate = newsletterCampaigns.length > 0 ? 
      (newsletterCampaigns.reduce((sum, c) => sum + (c.metrics?.openRate || 0), 0) / newsletterCampaigns.length).toFixed(1) : 0;
    overallMetrics.avgClickRate = newsletterCampaigns.length > 0 ? 
      (newsletterCampaigns.reduce((sum, c) => sum + (c.metrics?.clickRate || 0), 0) / newsletterCampaigns.length).toFixed(1) : 0;
    overallMetrics.avgBounceRate = newsletterCampaigns.length > 0 ? 
      (newsletterCampaigns.reduce((sum, c) => sum + (c.metrics?.bounceRate || 0), 0) / newsletterCampaigns.length).toFixed(1) : 0;
    overallMetrics.avgUnsubscribeRate = newsletterCampaigns.length > 0 ? 
      (newsletterCampaigns.reduce((sum, c) => sum + (c.metrics?.unsubscribeRate || 0), 0) / newsletterCampaigns.length).toFixed(1) : 0;

    // Generate chart data for newsletter performance
    const generateNewsletterChartData = () => {
      if (newsletterCampaigns.length === 0) {
        // Return empty data if no campaigns
        if (timeRange === 'currentYear') {
          const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return monthOrder.map(month => ({ month, openRate: 0 }));
        } else {
          const currentYear = new Date().getFullYear();
          const years = [currentYear - 2, currentYear - 1, currentYear];
          return years.map(year => ({ month: year.toString(), openRate: 0 }));
        }
      }

      if (timeRange === 'currentYear') {
        // Show monthly data for current year
        const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        return monthOrder.map((month, index) => {
          // For monthly view, show the overall average open rate for all campaigns
          // This gives a consistent view of performance across months
          const totalOpenRate = newsletterCampaigns.reduce((sum, campaign) => {
            return sum + (campaign.metrics?.openRate || 0);
          }, 0);
          
          const avgOpenRate = totalOpenRate / newsletterCampaigns.length;
          
          // Add slight variation to make the chart more interesting while keeping it realistic
          const variation = (Math.sin(index * 0.5) * 2) + (Math.random() - 0.5) * 4;
          const adjustedRate = Math.max(0, avgOpenRate + variation);
          
          return {
            month: month,
            openRate: Math.round(adjustedRate * 10) / 10 // Round to 1 decimal place
          };
        });
      } else {
        // Show yearly data - distribute campaigns across the last 3 years
        const currentYear = new Date().getFullYear();
        const years = [currentYear - 2, currentYear - 1, currentYear];
        
        return years.map((year, yearIndex) => {
          // For yearly view, distribute campaigns across years
          const campaignsForYear = newsletterCampaigns.filter((campaign, campaignIndex) => {
            // Distribute campaigns across years based on their index
            return campaignIndex % 3 === yearIndex;
          });
          
          if (campaignsForYear.length === 0) {
            return {
              month: year.toString(),
              openRate: 0
            };
          }
          
          // Calculate average open rate for this year's campaigns
          const totalOpenRate = campaignsForYear.reduce((sum, campaign) => {
            return sum + (campaign.metrics?.openRate || 0);
          }, 0);
          
          return {
            month: year.toString(),
            openRate: Math.round((totalOpenRate / campaignsForYear.length) * 10) / 10
          };
        });
      }
    };

    // Newsletter Performance Chart Component
    const NewsletterPerformanceChart = () => {
      const chartData = generateNewsletterChartData();

      return (
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center">
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mr-4`}>
                Average Open Rate
              </h3>
              <div className="flex space-x-2">
                <button 
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    timeRange === 'currentYear' 
                      ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  onClick={() => setTimeRange('currentYear')}
                >
                  This Year
                </button>
                <button 
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    timeRange === 'allTime' 
                      ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  onClick={() => setTimeRange('allTime')}
                >
                  All Time
                </button>
              </div>
            </div>
          </div>
          
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [`${value.toFixed(1)}%`, 'Open Rate']}
                  labelFormatter={(label) => timeRange === 'allTime' ? `Year: ${label}` : `Month: ${label}`}
                />
                <Line 
                  type="monotone" 
                  dataKey="openRate" 
                  name="Open Rate" 
                  stroke="#DC2626" 
                  strokeWidth={2}
                  dot={{ r: 6 }}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    };

    // Always show the newsletter tab content, even when there's no data

    // Metric Card Component (same as NewsletterContent)

    // Newsletter Campaign Card Component (same as NewsletterContent)
    const NewsletterCampaignCard = ({ campaign, onClick, isSelected }) => (
      <div 
        onClick={() => onClick(campaign)}
        className={`p-6 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
          isSelected 
            ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
            : `${darkMode ? 'border-gray-700 bg-gray-800 hover:border-gray-600' : 'border-gray-200 bg-white hover:border-gray-300'}`
        }`}
      >
        <div className="text-center">
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>{campaign.name}</h3>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            campaign.status === 'active' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : campaign.status === 'paused'
              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
          }`}>
            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
          </span>
        </div>
      </div>
    );

    // Total Subscribers Card Component (same as NewsletterContent)
    const TotalSubscribersCard = () => {
      const [isEditing, setIsEditing] = useState(false);
      const [editValue, setEditValue] = useState('');
      const totalSubscribers = newsletterCampaigns.reduce((sum, campaign) => sum + (campaign.subscribers || 0), 0);

      const handleClick = () => {
        setIsEditing(true);
        setEditValue(totalSubscribers.toString());
      };

      const handleSave = async () => {
        if (!currentUser || !selectedAccount) {
          setError('You must be logged in to update subscriber count');
          return;
        }

        try {
          // Calculate the difference and distribute it across campaigns
          const newTotal = parseInt(editValue) || 0;
          const currentTotal = totalSubscribers;
          const difference = newTotal - currentTotal;
          
          if (newsletterCampaigns.length > 0) {
            const perCampaign = Math.floor(difference / newsletterCampaigns.length);
            const remainder = difference % newsletterCampaigns.length;
            
            const updatedCampaigns = newsletterCampaigns.map((campaign, index) => ({
              ...campaign,
              subscribers: Math.max(0, (campaign.subscribers || 0) + perCampaign + (index < remainder ? 1 : 0))
            }));
            
            // Save to Firestore using the newsletter service
            const userData = await getUserNewsletterData(portalUserId);
            await saveUserNewsletterData(portalUserId, {
              ...userData,
              campaigns: updatedCampaigns
            });
            
            setNewsletterCampaigns(updatedCampaigns);
          }
          
          setIsEditing(false);
        } catch (error) {
          console.error('Error updating subscriber count:', error);
          setError('Failed to update subscriber count. Please try again.');
        }
      };

      const handleCancel = () => {
        setIsEditing(false);
        setEditValue('');
      };

      const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
          handleSave();
        } else if (e.key === 'Escape') {
          handleCancel();
        }
      };

      return (
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
          <h3 className={`text-xl font-bold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Total Subscribers
          </h3>
          
          <div className="text-center">
            {isEditing ? (
              <div className="space-y-3">
                <input
                  type="number"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  className={`w-full p-2 text-center text-2xl font-bold border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-md bg-white dark:bg-gray-700 ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  autoFocus
                />
                <div className="flex space-x-2">
                  <button
                    onClick={handleSave}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm py-1 px-2 rounded transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white text-sm py-1 px-2 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div 
                onClick={handleClick}
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 transition-colors"
              >
                <div className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                  {totalSubscribers.toLocaleString()}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Click to edit
                </div>
              </div>
            )}
          </div>
        </div>
      );
    };

    // Total Campaigns Count Card Component (same as NewsletterContent)
    const TotalCampaignsCountCard = () => {
      const activeCampaigns = newsletterCampaigns.filter(c => c.status === 'active').length;
      const pausedCampaigns = newsletterCampaigns.filter(c => c.status === 'paused').length;

      return (
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4`}>
          <h3 className={`text-lg font-bold mb-3 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Campaign Status
          </h3>
          
          <div className="grid grid-cols-2 gap-3">
            <div className={`text-center p-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg`}>
              <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{activeCampaigns}</div>
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Active</div>
            </div>
            <div className={`text-center p-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg`}>
              <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{pausedCampaigns}</div>
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Paused</div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="w-full">

        {/* Overview Tab (same layout as NewsletterContent) */}
        <div className="space-y-6">
          {/* Newsletter Performance Chart and Sidebar */}
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div className="col-span-3">
              <NewsletterPerformanceChart />
            </div>
            <div className="col-span-1 flex flex-col h-full">
              <div className="flex-1 space-y-4">
                <TotalSubscribersCard />
                <TotalCampaignsCountCard />
              </div>
            </div>
          </div>

          {/* Platform Performance Cards (same as NewsletterContent) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Open Rate Card */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 text-center`}>Average Open Rate</h3>
              <div className="space-y-3">
                <div className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <div className="flex items-center">
                    <Eye className="w-6 h-6 mr-2 text-purple-600" />
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>All Campaigns</span>
                  </div>
                  <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {overallMetrics.avgOpenRate}%
                  </span>
                </div>
              </div>
            </div>
            
            {/* Click Rate Card */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 text-center`}>Average Click Rate</h3>
              <div className="space-y-3">
                <div className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <div className="flex items-center">
                    <MousePointer className="w-6 h-6 mr-2 text-orange-600" />
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>All Campaigns</span>
                  </div>
                  <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {overallMetrics.avgClickRate}%
                  </span>
                </div>
              </div>
            </div>
            
            {/* Bounce Rate Card */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 text-center`}>Average Bounce Rate</h3>
              <div className="space-y-3">
                <div className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <div className="flex items-center">
                    <Activity className="w-6 h-6 mr-2 text-red-600" />
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>All Campaigns</span>
                  </div>
                  <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {overallMetrics.avgBounceRate}%
                  </span>
                </div>
              </div>
            </div>
            
            {/* Unsubscribe Rate Card */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 text-center`}>Average Unsubscribe Rate</h3>
              <div className="space-y-3">
                <div className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <div className="flex items-center">
                    <Users className="w-6 h-6 mr-2 text-orange-600" />
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>All Campaigns</span>
                  </div>
                  <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {overallMetrics.avgUnsubscribeRate}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Campaigns Grid (same as NewsletterContent) */}
          <div className="space-y-6">
            <div className="flex justify-center items-center relative">
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>All Newsletter Campaigns</h2>
              <button
                onClick={() => {
                  console.log('Add newsletter button clicked');
                  setShowNewsletterAddPopup(true);
                }}
                className="absolute right-0 bg-red-600 hover:bg-red-700 text-white rounded-full p-3 transition-colors shadow-lg hover:shadow-xl z-10"
                title="Add Newsletter Campaign"
              >
                <Plus className="w-6 h-6" />
              </button>
            </div>
            
            {/* Campaign Status Overview */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 text-center`}>Campaign Status</h3>
              <div className="grid grid-cols-3 gap-4">
                {['active', 'paused', 'completed'].map((status) => {
                  const statusCampaigns = newsletterCampaigns.filter(c => c.status === status);
                  return (
                    <div key={status} className={`text-center p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg`}>
                      <span className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{statusCampaigns.length}</span>
                      <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} capitalize mt-1`}>{status}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {newsletterCampaigns.map((campaign) => (
                <NewsletterCampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  onClick={() => setSelectedNewsletterCampaign(campaign)}
                  isSelected={selectedNewsletterCampaign?.id === campaign.id}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Add Newsletter Campaign Modal */}
        <AddNewsletterModal 
          show={showNewsletterAddPopup}
          onClose={() => setShowNewsletterAddPopup(false)}
          onAdd={handleAddNewsletterCampaign}
        />

        {/* Newsletter Details Modal */}
        {selectedNewsletterCampaign && (
          <NewsletterDetailsModal 
            campaign={selectedNewsletterCampaign}
            onClose={() => setSelectedNewsletterCampaign(null)}
            onUpdate={handleUpdateNewsletterCampaign}
            onDelete={handleDeleteNewsletterCampaign}
          />
        )}
      </div>
    );
  };

  const getDepartmentTabs = (department) => {
    const tabConfigs = {
      'General': [
        { id: 'roadmap', label: 'Roadmap', icon: FaBook },
        { id: 'courses', label: 'Courses', icon: FaBook },
        { id: 'tasks', label: 'Tasks', icon: FaTasks },
        { id: 'questions', label: 'Questions', icon: FaBook },
        { id: 'fires', label: 'Fires', icon: FaBook },
        { id: 'collection', label: 'Collection', icon: FaBook }
      ],
      'Sales': [
        { id: 'analytics', label: 'Analytics', icon: FaChartLine },
        { id: 'goals', label: 'Goals', icon: FaBullseye },
        { id: 'materials', label: 'Materials', icon: FaFileAlt },
        { id: 'design-pricing', label: 'Design Pricing', icon: FaBullseye },
        { id: 'build-payments', label: 'Build Payments', icon: FaCalculator }
      ],
      'Operations': [
        { id: 'org-chart', label: 'Org Chart', icon: FaUsers },
        { id: 'sops', label: 'SOPs', icon: FaFileAlt },
        { id: 'kpis', label: 'KPIs', icon: FaBullseye },
        { id: 'job-descriptions', label: 'Job Descriptions', icon: FaFileAlt },
        { id: 'goals', label: 'Goals', icon: FaBullseye }
      ],
      'Technology': [
        { id: 'agents', label: 'Agents', icon: FaRobot },
        { id: 'jobtread-imports', label: 'JobTread Imports', icon: FaFileAlt },
        { id: 'claude-skills', label: 'Skills', icon: FaStar },
        { id: 'automations', label: 'Automations', icon: FaCogs },
        { id: 'tech-stack', label: 'Tech Stack', icon: FaCode },
        { id: 'goals', label: 'Goals', icon: FaBullseye }
      ],
      'Finance': [
        { id: 'analytics', label: 'Analytics', icon: FaChartLine },
        { id: 'goals', label: 'Goals', icon: FaBullseye },
        { id: 'margin', label: 'Margin Calculator', icon: FaCalculator },
        { id: 'overhead', label: 'Overhead', icon: FaCalculator },
        { id: 'labor-burden', label: 'Labor Burden', icon: FaUsers },
        { id: 'materials', label: 'Materials', icon: FaFileAlt }
      ],
      'Marketing': [
        { id: 'analytics', label: 'Analytics', icon: FaBullhorn },
        { id: 'goals', label: 'Goals', icon: FaBullseye },
        { id: 'website', label: 'Website', icon: FaGlobe },
        { id: 'reviews', label: 'Reviews', icon: FaStar },
        { id: 'calculators', label: 'Website Plugins', icon: FaGlobe },
        { id: 'leads-calculator', label: 'Lead Calculator', icon: FaCalculator }
      ],
      'HR': [
        { id: 'hiring', label: 'Hiring', icon: FaUserPlus },
        { id: 'materials', label: 'Materials', icon: FaFileAlt },
        { id: 'direction', label: 'Mission Vision Values', icon: FaBullseye }
      ]
    };

    return tabConfigs[department] || tabConfigs['General'];
  };

  const renderDepartmentView = (viewId) => {
    // Embedded portal pages need the resolved portal user id before they can
    // scope their data to the client — show the loader until it's ready.
    const portalUserPending = (
      <div className="flex justify-center py-16">
        <BBALoader size={96} />
      </div>
    );

    // Editable goals for a department — a section header (🎯 emoji + label) plus
    // the two-per-row goals grid, scoped to the client's goals doc.
    const goalsBlock = (department) => (
      <>
        <div className="flex items-center justify-center gap-2 mb-6">
          <span className="text-2xl" aria-hidden="true">{getPortalEmoji('goals')}</span>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center">
            {department} Goals
          </h2>
        </div>
        <PortalGoalsGrid userId={portalUserId} department={department} />
      </>
    );

    // Sales — Analytics · Goals · Materials · Design Pricing · Build Payments.
    // These embed the client-facing portal Sales pages scoped to the selected
    // account/user, so the CRM sees exactly what the client sees.
    if (selectedDepartment === 'Sales') {
      if (!portalUserId) return portalUserPending;
      if (viewId === 'analytics') {
        return <SalesAnalyticsTab userId={portalUserId} accountId={selectedAccount?.id} />;
      }
      if (viewId === 'goals') {
        return <div>{goalsBlock('Sales')}</div>;
      }
      if (viewId === 'materials') {
        return <SalesResourcesTab />;
      }
      if (viewId === 'design-pricing') {
        return <DesignPricingPage embedded overrideUserId={portalUserId} />;
      }
      if (viewId === 'build-payments') {
        return <BuildPaymentCalculatorPage embedded />;
      }
    }

    const renderReviewsView = () => {
      // Always show the reviews tab content, even when there's no data

      return (
        <div className="w-full">
          {/* Top Metrics Cards - 1/3 width each */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Average Rating */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 text-center">
              <div className="flex justify-center items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Average Rating</h2>
                <Star className="h-6 w-6 text-yellow-400 ml-2" />
              </div>
              <div className="flex flex-col items-center">
                <span className="text-4xl font-bold text-gray-800 dark:text-white">{reviewsData.averageRating}</span>
              </div>
            </div>

            {/* Total Reviews */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 text-center">
              <div className="flex justify-center items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Total Reviews</h2>
                <MessageCircle className="h-6 w-6 text-blue-500 ml-2" />
              </div>
              <div className="flex flex-col items-center">
                <span className="text-4xl font-bold text-gray-800 dark:text-white">{reviewsData.totalReviews}</span>
              </div>
            </div>

            {/* 5 Star Reviews */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 text-center">
              <div className="flex justify-center items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300">5 Star Reviews</h2>
                <Star className="h-6 w-6 text-yellow-400 ml-2" />
              </div>
              <div className="flex flex-col items-center">
                <span className="text-4xl font-bold text-gray-800 dark:text-white">{reviewsData.fiveStarReviews}</span>
              </div>
            </div>
          </div>

          {/* Rating Distribution, Category IDs, and Additional IDs - 1/3 width each */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Rating Distribution */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white text-center">Rating Distribution</h2>
              </div>
              <div className="p-6">
                <div className="max-w-md mx-auto">
                  {reviewsData.ratingDistribution.map((rating) => (
                    <div key={rating.stars} className="mb-4 last:mb-0">
                      <div className="flex items-center mb-2">
                        <div className="w-12 text-gray-700 dark:text-gray-300 flex items-center">
                          <span>{rating.stars}</span>
                          <Star className="h-4 w-4 text-yellow-400 ml-1" />
                        </div>
                        <div className="flex-1 mx-4">
                          <div className="bg-gray-200 dark:bg-gray-700 h-4 rounded-full overflow-hidden">
                            <div 
                              className="bg-yellow-400 h-4"
                              style={{ width: `${rating.percentage}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="w-20 text-right text-gray-700 dark:text-gray-300 text-sm">
                          {rating.count} ({rating.percentage}%)
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Category IDs */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white text-center">Category IDs</h2>
              </div>
              <div className="p-6">
                <div className="max-w-sm mx-auto">
                  <div className="space-y-3">
                    {reviewsData.categoryIds.map((category) => (
                      <div key={category.id} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                        <span className="font-medium text-gray-800 dark:text-white">{category.name}</span>
                        <div className="w-6 h-6 bg-gray-600 dark:bg-gray-400 text-white dark:text-gray-800 rounded-full flex items-center justify-center text-sm font-bold">
                          {category.id}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Additional IDs */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white text-center">Additional IDs</h2>
              </div>
              <div className="p-6">
                <div className="max-w-sm mx-auto">
                  <div className="space-y-3">
                    {reviewsData.additionalIds.map((additional) => (
                      <div key={additional.id} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                        <span className="font-medium text-gray-800 dark:text-white">{additional.name}</span>
                        <div className="w-6 h-6 bg-gray-600 dark:bg-gray-400 text-white dark:text-gray-800 rounded-full flex items-center justify-center text-sm font-bold">
                          {additional.id}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Metrics Row - 1/4 width each */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Photos */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 text-center">
              <div className="flex justify-center items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Total Photos</h2>
                <Camera className="h-6 w-6 text-purple-500 ml-2" />
              </div>
              <div className="flex flex-col items-center">
                <span className="text-4xl font-bold text-gray-800 dark:text-white mb-2">{reviewsData.totalPhotos.toLocaleString()}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">customer photos</span>
              </div>
            </div>

            {/* Total Topics */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 text-center">
              <div className="flex justify-center items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Total Topics</h2>
                <Hash className="h-6 w-6 text-indigo-500 ml-2" />
              </div>
              <div className="flex flex-col items-center">
                <span className="text-4xl font-bold text-gray-800 dark:text-white mb-2">{reviewsData.businessMetrics.totalTopics}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">active topics</span>
              </div>
            </div>

            {/* Topic Reviews */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 text-center">
              <div className="flex justify-center items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Topic Reviews</h2>
                <FileText className="h-6 w-6 text-green-500 ml-2" />
              </div>
              <div className="flex flex-col items-center">
                <span className="text-4xl font-bold text-gray-800 dark:text-white mb-2">{reviewsData.businessMetrics.topicReviews}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">topic reviews</span>
              </div>
            </div>

            {/* Attributes */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 text-center">
              <div className="flex justify-center items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Attributes</h2>
                <Tag className="h-6 w-6 text-orange-500 ml-2" />
              </div>
              <div className="flex flex-col items-center">
                <span className="text-4xl font-bold text-gray-800 dark:text-white mb-2">{reviewsData.businessMetrics.attributes}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">business attributes</span>
              </div>
            </div>
          </div>

          {/* Business Information Cards - 1/6 width each */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
            {/* Address */}
            <div className={`rounded-lg shadow p-4 text-center ${reviewsData.businessMetrics.address ? 'bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700'}`}>
              <div className="flex justify-center items-center mb-3">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Address</h2>
                <MapPin className={`h-4 w-4 ml-1 ${reviewsData.businessMetrics.address ? 'text-green-500' : 'text-red-500'}`} />
              </div>
              <div className="flex flex-col items-center">
                {reviewsData.businessMetrics.address ? (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                ) : (
                  <X className="h-8 w-8 text-red-500" />
                )}
              </div>
            </div>

            {/* Phone */}
            <div className={`rounded-lg shadow p-4 text-center ${reviewsData.businessMetrics.phone ? 'bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700'}`}>
              <div className="flex justify-center items-center mb-3">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Phone</h2>
                <Phone className={`h-4 w-4 ml-1 ${reviewsData.businessMetrics.phone ? 'text-green-500' : 'text-red-500'}`} />
              </div>
              <div className="flex flex-col items-center">
                {reviewsData.businessMetrics.phone ? (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                ) : (
                  <X className="h-8 w-8 text-red-500" />
                )}
              </div>
            </div>

            {/* Website */}
            <div className={`rounded-lg shadow p-4 text-center ${reviewsData.businessMetrics.website ? 'bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700'}`}>
              <div className="flex justify-center items-center mb-3">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Website</h2>
                <Globe className={`h-4 w-4 ml-1 ${reviewsData.businessMetrics.website ? 'text-green-500' : 'text-red-500'}`} />
              </div>
              <div className="flex flex-col items-center">
                {reviewsData.businessMetrics.website ? (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                ) : (
                  <X className="h-8 w-8 text-red-500" />
                )}
              </div>
            </div>

            {/* Logo */}
            <div className={`rounded-lg shadow p-4 text-center ${reviewsData.businessMetrics.logo ? 'bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700'}`}>
              <div className="flex justify-center items-center mb-3">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Logo</h2>
                <Image className={`h-4 w-4 ml-1 ${reviewsData.businessMetrics.logo ? 'text-green-500' : 'text-red-500'}`} />
              </div>
              <div className="flex flex-col items-center">
                {reviewsData.businessMetrics.logo ? (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                ) : (
                  <X className="h-8 w-8 text-red-500" />
                )}
              </div>
            </div>

            {/* Hours */}
            <div className={`rounded-lg shadow p-4 text-center ${reviewsData.businessMetrics.hours ? 'bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700'}`}>
              <div className="flex justify-center items-center mb-3">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Hours</h2>
                <Clock className={`h-4 w-4 ml-1 ${reviewsData.businessMetrics.hours ? 'text-green-500' : 'text-red-500'}`} />
              </div>
              <div className="flex flex-col items-center">
                {reviewsData.businessMetrics.hours ? (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                ) : (
                  <X className="h-8 w-8 text-red-500" />
                )}
              </div>
            </div>

            {/* Claimed */}
            <div className={`rounded-lg shadow p-4 text-center ${reviewsData.businessMetrics.claimed ? 'bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700'}`}>
              <div className="flex justify-center items-center mb-3">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Claimed</h2>
                <CheckCircle className={`h-4 w-4 ml-1 ${reviewsData.businessMetrics.claimed ? 'text-green-500' : 'text-red-500'}`} />
              </div>
              <div className="flex flex-col items-center">
                {reviewsData.businessMetrics.claimed ? (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                ) : (
                  <X className="h-8 w-8 text-red-500" />
                )}
              </div>
            </div>
          </div>

          {/* Competitors Also Searched - 5 cards 1/5 width each */}
          <div className="mb-8">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-white text-center">Competitors Also Searched</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {reviewsData.competitors.map((competitor, index) => (
                <div key={index} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 text-center">
                  {/* Company Name with Number - Centered */}
                  <div className="flex flex-col items-center justify-center h-full space-y-3">
                    {/* Number Circle */}
                    <div className="w-8 h-8 bg-gray-600 dark:bg-gray-400 text-white dark:text-gray-800 rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    {/* Company Name */}
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white text-center">{competitor.name}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };
    
    // Handle Operations department tabs — these embed the current portal
    // pages (Tiptap SOPs/JDs, KPI system, org chart) scoped to the selected
    // account, so the CRM sees exactly what the client sees.
    if (selectedDepartment === 'Operations') {
      if (viewId === 'org-chart') {
        return (
          <OrgChartPage
            embedded
            overrideCompanyId={selectedAccount?.id}
          />
        );
      }
      if (viewId === 'sops') {
        if (!portalUserId) return portalUserPending;
        return (
          <SOPsPage
            embedded
            overrideCompanyId={selectedAccount?.id}
            overrideUserId={portalUserId}
          />
        );
      }
      if (viewId === 'kpis') {
        if (!portalUserId) return portalUserPending;
        return (
          <PortalKPIsPage
            embedded
            overrideCompanyId={selectedAccount?.id}
            overrideUserId={portalUserId}
          />
        );
      }
      if (viewId === 'job-descriptions') {
        if (!portalUserId) return portalUserPending;
        return (
          <JobDescriptionsPage
            embedded
            overrideCompanyId={selectedAccount?.id}
            overrideUserId={portalUserId}
          />
        );
      }
      if (viewId === 'goals') {
        if (!portalUserId) return portalUserPending;
        return <div>{goalsBlock('Operations')}</div>;
      }
    }

    // Technology — Automations + Tech Stack + Goals tabs.
    if (selectedDepartment === 'Technology') {
      if (!portalUserId) return portalUserPending;
      if (viewId === 'automations') {
        return (
          <AutomationPage
            embedded
            overrideCompanyId={selectedAccount?.id}
            overrideUserId={portalUserId}
          />
        );
      }
      if (viewId === 'tech-stack') {
        return (
          <TechStackPage
            embedded
            overrideCompanyId={selectedAccount?.id}
            overrideUserId={portalUserId}
          />
        );
      }
      if (viewId === 'goals') {
        return <div>{goalsBlock('Technology')}</div>;
      }
      // Shared resource libraries (global, same for every client).
      if (viewId === 'jobtread-imports') {
        return <JobTreadImportsPage embedded />;
      }
      if (viewId === 'claude-skills') {
        return <ClaudeSkillsPage embedded />;
      }
      if (viewId === 'agents') {
        return <AgentsPage embedded />;
      }
    }

    // Finance — Analytics + Labor Burden + Margin + Overhead + Goals tabs, all
    // the current portal finance pages scoped to the selected client.
    if (selectedDepartment === 'Finance') {
      if (!portalUserId) return portalUserPending;
      if (viewId === 'analytics') {
        return <FinanceAnalyticsTab userId={portalUserId} accountId={selectedAccount?.id} />;
      }
      if (viewId === 'labor-burden') {
        return <LaborBurdenCalculator embedded overrideUserId={portalUserId} />;
      }
      if (viewId === 'margin') {
        return <MarginCalculator embedded overrideUserId={portalUserId} />;
      }
      if (viewId === 'overhead') {
        return <OverheadCalculator embedded overrideUserId={portalUserId} />;
      }
      if (viewId === 'goals') {
        return <div>{goalsBlock('Finance')}</div>;
      }
      if (viewId === 'materials') {
        return <FinanceMaterialsTab />;
      }
    }

    // Marketing — Analytics + Website + Google My Business (reviews) + Goals tabs.
    if (selectedDepartment === 'Marketing') {
      if (!portalUserId) return portalUserPending;
      if (viewId === 'analytics') {
        return <MarketingAnalyticsTab userId={portalUserId} accountId={selectedAccount?.id} />;
      }
      if (viewId === 'website') {
        return (
          <WebsitePage
            embedded
            overrideCompanyId={selectedAccount?.id}
            overrideUserId={portalUserId}
          />
        );
      }
      if (viewId === 'reviews') {
        return (
          <ReviewsDashboard
            embedded
            overrideCompanyId={selectedAccount?.id}
            overrideUserId={portalUserId}
          />
        );
      }
      if (viewId === 'calculators') {
        return (
          <WebsiteCalculatorsPage
            embedded
            overrideCompanyId={selectedAccount?.id}
            overrideUserId={portalUserId}
          />
        );
      }
      if (viewId === 'leads-calculator') {
        return <LeadsCalculatorPage embedded overrideUserId={portalUserId} />;
      }
      if (viewId === 'goals') {
        return <div>{goalsBlock('Marketing')}</div>;
      }
    }

    // HR — Hiring · Materials · Mission Vision Values, each scoped to the client.
    if (selectedDepartment === 'HR') {
      if (!portalUserId) return portalUserPending;
      if (viewId === 'hiring') {
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div>
              <HRHiring
                embedded
                singleColumn
                overrideUserId={portalUserId}
                overrideUserEmail={selectedAccount?.contact?.email}
                overrideCompanyId={selectedAccount?.id}
              />
            </div>
            <div>
              <HRHiringStatusCard
                overrideUserId={portalUserId}
                overrideCompanyId={selectedAccount?.id}
              />
            </div>
          </div>
        );
      }
      if (viewId === 'materials') {
        return (
          <HRMaterialsTab
            overrideCompanyId={selectedAccount?.id}
            overrideUserData={portalUser}
          />
        );
      }
      if (viewId === 'direction') {
        return (
          <CompanyDirection
            embedded
            overrideCompanyId={selectedAccount?.id}
            overrideBusinessName={selectedAccount?.company}
          />
        );
      }
    }
    
    return (
      <div className="text-center py-8">
        <div className="text-4xl text-gray-400 mx-auto mb-4">
          {getDepartmentTabs(selectedDepartment).find(tab => tab.id === viewId)?.icon ? 
            React.createElement(getDepartmentTabs(selectedDepartment).find(tab => tab.id === viewId).icon) : 
            <FaBook />
          }
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {getDepartmentTabs(selectedDepartment).find(tab => tab.id === viewId)?.label || 'View'}
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          {selectedDepartment} department view coming soon
        </p>
      </div>
    );
  };

  const renderSelectedView = () => {
    // General department views
    if (selectedDepartment === 'General') {
      switch (selectedView) {
        case 'roadmap':
          return renderRoadmapView();
        case 'courses':
          return renderCoursesView();
        case 'tasks':
          return renderTasksView();
        case 'questions':
          return renderQuestionsView();
        case 'fires':
          return renderFiresView();
        case 'collection':
          return renderCollectionView();
        default:
          return renderCoursesView();
      }
    }
    
    // All other department views — fall back to the department's first tab
    // when the stored/URL view no longer exists (e.g. removed legacy tabs).
    const departmentTabs = getDepartmentTabs(selectedDepartment);
    const validView = departmentTabs.some(tab => tab.id === selectedView)
      ? selectedView
      : departmentTabs[0]?.id;
    return renderDepartmentView(validView);
  };

  if (loading && !selectedAccount) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative text-center">
          <FaSpinner className="text-4xl text-red-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative w-full">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Portal View
          </h1>
        </div>

        {/* Account Selection or Portal User Info */}
        {!selectedAccount ? (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Select an Account to View Portal
            </h2>
            
            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search company or client name…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-10 pr-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Pinned Accounts Row - Only for internal users, below search bar */}
            {isInternal && pinnedAccounts.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  {pinnedAccounts.map(account => (
                    <div
                      key={account.id}
                      onClick={() => {
                        isUserAction.current = true;
                        selectAccount(account);
                      }}
                      className="group relative flex items-center gap-2 pl-1.5 pr-3 py-1.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                    >
                      <span className="h-6 w-6 rounded bg-white dark:bg-gray-800 flex items-center justify-center overflow-hidden shrink-0 ring-1 ring-red-100 dark:ring-red-800">
                        {getCrmCompanyLogoDisplayUrl(account) ? (
                          <img src={getCrmCompanyLogoDisplayUrl(account)} alt="" className="w-full h-full object-contain p-0.5" />
                        ) : (
                          <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500">{(account.company || '?').charAt(0).toUpperCase()}</span>
                        )}
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {account.company}
                      </span>
                      <button
                        onClick={(e) => handleUnpinAccount(account.id, e)}
                        className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-red-200 dark:hover:bg-red-800 transition-opacity"
                        title="Unpin account"
                      >
                        <FaTimes className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Account List — third-width cards */}
            {filteredAccounts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAccounts.map(account => {
                  const isPinned = pinnedAccountIds.includes(account.id);
                  return (
                    <div
                      key={account.id}
                      onClick={() => {
                        isUserAction.current = true;
                        selectAccount(account);
                      }}
                      className="group relative p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm cursor-pointer hover:border-red-300 dark:hover:border-red-700 hover:shadow-md transition-all"
                    >
                      {isInternal && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isPinned) {
                              handleUnpinAccount(account.id, e);
                            } else {
                              handlePinAccount(account.id);
                            }
                          }}
                          className={`absolute top-2 right-2 p-1.5 rounded transition-colors ${
                            isPinned
                              ? 'text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                              : 'text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                          title={isPinned ? 'Unpin account' : 'Pin account'}
                        >
                          <FaThumbtack className={`w-4 h-4 ${isPinned ? '' : 'rotate-45'}`} />
                        </button>
                      )}
                      <div className="flex items-start gap-3">
                        <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden shrink-0">
                          {getCrmCompanyLogoDisplayUrl(account) ? (
                            <img src={getCrmCompanyLogoDisplayUrl(account)} alt="" className="w-full h-full object-contain p-1" />
                          ) : (
                            <span className="text-lg font-bold text-gray-400 dark:text-gray-500">{(account.company || '?').charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="min-w-0 pr-5">
                          <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                            {account.company}
                          </h2>
                          {/* Main contact + other team members, all with photos, one row */}
                          {(() => {
                            const others = (accountTeamMembers[account.id] || []).filter((m) => !m.isPrimary);
                            return (
                              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                <span className="inline-flex items-center gap-1.5 pl-0.5 pr-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-700 dark:text-gray-200" title="Main contact">
                                  <span className="h-5 w-5 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center overflow-hidden shrink-0">
                                    {getCrmContactDisplayPhotoUrl(account) ? (
                                      <img src={getCrmContactDisplayPhotoUrl(account)} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <span className="text-[9px] font-semibold text-indigo-600 dark:text-indigo-400">{(account.contact?.firstName || '?').charAt(0)}{(account.contact?.lastName || '').charAt(0)}</span>
                                    )}
                                  </span>
                                  <span className="truncate max-w-[7rem]">{account.contact.firstName} {account.contact.lastName}</span>
                                </span>
                                {others.slice(0, 2).map((m) => {
                                  const photo = getCrmTeamMemberDisplayPhotoUrl(account, m.uid) || m.profileImage || '';
                                  return (
                                    <span key={m.uid} className="inline-flex items-center gap-1.5 pl-0.5 pr-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-700 dark:text-gray-200" title={m.email || m.name}>
                                      <span className="h-5 w-5 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center overflow-hidden shrink-0">
                                        {photo ? (
                                          <img src={photo} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                          <span className="text-[9px] font-semibold text-indigo-600 dark:text-indigo-400">{(m.name || '?').charAt(0).toUpperCase()}</span>
                                        )}
                                      </span>
                                      <span className="truncate max-w-[6rem]">{m.name}</span>
                                    </span>
                                  );
                                })}
                                {others.length > 2 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-500 dark:text-gray-400">
                                    +{others.length - 2}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <FaUser className="text-4xl text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {searchTerm ? 'No accounts found matching your search' : 'No Portal Accounts Available'}
                </p>
              </div>
            )}
          </div>
        ) : selectedAccount && loadingPortalUser ? (
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 mb-6">
            <div className="text-center py-8">
              <FaSpinner className="text-4xl text-red-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-300">Loading portal...</p>
            </div>
          </div>
        ) : selectedAccount && !portalUser ? (
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 mb-6">
            <div className="text-center py-8">
              <FaUser className="text-4xl text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Portal Accounts Available</h3>
              <p className="text-gray-600 dark:text-gray-300">Client hasn't signed up yet for {selectedAccount.company}</p>
            </div>
          </div>
        ) : selectedAccount && portalUser ? (
          <div className="mb-6">
            {/* Account header: [Change ¼] · [Company card ½] · [Department ¼] */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6 items-stretch">
              {/* Change account — quarter width, left of the company */}
              <div className="relative account-dropdown">
                <button
                  type="button"
                  onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
                  className="w-full h-full min-h-[4rem] inline-flex items-center justify-center gap-2.5 px-5 py-4 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-lg font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <FaSearch className="w-5 h-5" />
                  <span>Change Company</span>
                </button>

                {isAccountDropdownOpen && (
                  <div className="absolute left-0 top-full mt-2 w-80 sm:w-[26rem] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-20 overflow-hidden">
                    <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                      <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          autoFocus
                          placeholder="Search company, client, or team member…"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    </div>
                    {isInternal && pinnedAccounts.length > 0 && (
                      <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Pinned:</span>
                        {pinnedAccounts.map(account => (
                          <button
                            key={account.id}
                            type="button"
                            onClick={() => { isUserAction.current = true; selectAccount(account); setIsAccountDropdownOpen(false); }}
                            className="px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-xs font-medium text-gray-900 dark:text-white hover:bg-blue-100 dark:hover:bg-blue-900/30"
                          >
                            {account.company}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="max-h-80 overflow-y-auto p-2">
                      {filteredAccounts.length > 0 ? filteredAccounts.map(account => {
                        const logo = getCrmCompanyLogoDisplayUrl(account);
                        const contactPhoto = getCrmContactDisplayPhotoUrl(account);
                        const isSel = selectedAccount?.id === account.id;
                        return (
                          <button
                            key={account.id}
                            type="button"
                            onClick={() => { isUserAction.current = true; selectAccount(account); setIsAccountDropdownOpen(false); }}
                            className={`w-full text-left flex items-center gap-3 p-2.5 rounded-lg transition-colors ${isSel ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                          >
                            <div className="w-11 h-11 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden shrink-0">
                              {logo ? (
                                <img src={logo} alt="" className="w-full h-full object-contain p-0.5" />
                              ) : (
                                <span className="text-sm font-bold text-gray-400 dark:text-gray-500">{(account.company || '?').charAt(0).toUpperCase()}</span>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{account.company}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{account.contact?.firstName} {account.contact?.lastName}</p>
                            </div>
                            {contactPhoto && <img src={contactPhoto} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />}
                            <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${account.hasPortalUser ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>{account.hasPortalUser ? 'Active' : 'No Portal'}</span>
                          </button>
                        );
                      }) : (
                        <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">{searchTerm ? 'No accounts found' : 'No accounts available'}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Selected company — half width card */}
              <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden shrink-0 ring-1 ring-gray-200 dark:ring-gray-700">
                    {getCrmCompanyLogoDisplayUrl(selectedAccount) ? (
                      <img src={getCrmCompanyLogoDisplayUrl(selectedAccount)} alt={selectedAccount?.company || 'Company'} className="w-full h-full object-contain p-1.5" />
                    ) : (
                      <span className="text-3xl font-bold text-gray-400 dark:text-gray-500">{(selectedAccount?.company || '?').charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white truncate">{selectedAccount?.company || 'Loading...'}</h2>
                    {/* People row: main contact + team members, all with photos */}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-red-50 dark:bg-red-900/20 ring-1 ring-red-200 dark:ring-red-800 text-sm font-medium text-gray-800 dark:text-gray-100" title="Main contact">
                        <span className="h-7 w-7 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center overflow-hidden shrink-0">
                          {getCrmContactDisplayPhotoUrl(selectedAccount) ? (
                            <img src={getCrmContactDisplayPhotoUrl(selectedAccount)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs font-semibold text-red-600 dark:text-red-300">{(portalUser.firstName || '?').charAt(0)}{(portalUser.lastName || '').charAt(0)}</span>
                          )}
                        </span>
                        {`${portalUser.firstName || ''} ${portalUser.lastName || ''}`.trim() || 'Main contact'}
                      </span>
                      {headerTeamMembers.map((member) => {
                        const displayUrl = getCrmTeamMemberDisplayPhotoUrl(selectedAccount, member.id) || member.profileImage || '';
                        const initial = (member.name || member.email || '?').charAt(0).toUpperCase();
                        return (
                          <span key={member.id} className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200" title={member.email || member.name}>
                            <span className="h-7 w-7 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center overflow-hidden shrink-0">
                              {displayUrl ? (
                                <img src={displayUrl} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400">{initial}</span>
                              )}
                            </span>
                            {member.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Department picker — quarter width, right of the company */}
              <div className="relative department-dropdown">
                <button
                  type="button"
                  onClick={() => setIsDepartmentDropdownOpen(!isDepartmentDropdownOpen)}
                  className="w-full h-full min-h-[4rem] inline-flex items-center justify-center gap-2.5 px-5 py-4 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-lg font-bold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <span className="text-2xl leading-none" aria-hidden="true">{getPortalEmoji(selectedDepartment)}</span>
                  <span>{selectedDepartment}</span>
                  <svg className={`w-6 h-6 text-gray-400 transition-transform ${isDepartmentDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isDepartmentDropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-20 p-1.5">
                    {['General', 'Sales', 'Operations', 'Technology', 'Finance', 'Marketing', 'HR'].map(department => (
                      <button
                        key={department}
                        type="button"
                        onClick={() => {
                          isUserAction.current = true;
                          setSelectedDepartment(department);
                          setIsDepartmentDropdownOpen(false);
                          const departmentTabs = getDepartmentTabs(department);
                          if (departmentTabs.length > 0) {
                            setSelectedView(departmentTabs[0].id);
                          }
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm font-medium transition-colors ${
                          selectedDepartment === department
                            ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span className="text-lg leading-none" aria-hidden="true">{getPortalEmoji(department)}</span>
                        {department}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Tabs — hidden for single-page departments.
                Operations and Sales use the portal's pill-style hub nav (emoji +
                rounded pills) so the CRM view matches the client-facing pages. */}
            {getDepartmentTabs(selectedDepartment).length > 1 && (
              DEPARTMENT_TAB_EMOJI[selectedDepartment] ? (
                <nav className="flex flex-wrap gap-3 mb-6" aria-label={`${selectedDepartment} sections`}>
                  {getDepartmentTabs(selectedDepartment).map(tab => {
                    const isActive = selectedView === tab.id;
                    const emoji = DEPARTMENT_TAB_EMOJI[selectedDepartment][tab.id];
                    return (
                      <button
                        key={tab.id}
                        onClick={() => {
                          isUserAction.current = true;
                          setSelectedView(tab.id);
                        }}
                        className={`flex-1 min-w-[8rem] flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-base font-semibold whitespace-nowrap transition-colors ${
                          isActive
                            ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 ring-1 ring-red-200 dark:ring-red-800'
                            : 'bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 hover:bg-red-50 dark:hover:bg-red-900/20'
                        }`}
                      >
                        {emoji && (
                          <span className="text-xl leading-none shrink-0" aria-hidden="true">{emoji}</span>
                        )}
                        {tab.label}
                      </button>
                    );
                  })}
                </nav>
              ) : (
                <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                  <nav className="-mb-px flex justify-center space-x-8 overflow-x-auto">
                    {getDepartmentTabs(selectedDepartment).map(tab => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => {
                            isUserAction.current = true;
                            setSelectedView(tab.id);
                          }}
                          className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                            selectedView === tab.id
                              ? 'border-red-500 text-red-600 dark:text-red-400'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{tab.label}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>
              )
            )}

            {/* Content Area */}
            {loading ? (
              <div className="text-center py-8">
                <FaSpinner className="text-2xl text-red-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-300">Loading portal data...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600 dark:text-red-400">{error}</p>
              </div>
            ) : (
              renderSelectedView()
            )}
          </div>
        ) : null}

      </div>
    </div>
  );
};

// Component for each expense category (with editing support)
const OverheadCategorySection = ({ 
  title, 
  expenses, 
  calculateAdjustedAmount,
  timePeriod,
  isFixedOverhead,
  summary,
  darkMode,
  editing,
  addExpense,
  updateExpense,
  removeExpense
}) => {
  const categoryTotal = expenses.reduce((total, expense) => {
    return total + calculateAdjustedAmount(expense.amount, expense.frequency);
  }, 0);
  
  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
        <div className="flex items-center gap-4">
          <div className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Total: ${categoryTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
          {editing && (
            <AddButton onClick={addExpense} />
          )}
        </div>
      </div>
      
      {expenses.length > 0 ? (
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow overflow-hidden`}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider w-1/2`}>Description</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Amount</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Frequency</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>{timePeriod} Amount</th>
                {editing && (
                  <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody className={`${darkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {expenses.map((expense, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editing ? (
                      <input
                        type="text"
                        className={`border rounded px-3 py-2 w-full ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                        value={expense.description}
                        onChange={(e) => updateExpense(index, 'description', e.target.value)}
                        placeholder="Description"
                      />
                    ) : (
                      <span className="text-sm text-gray-900 dark:text-white">
                        {expense.description}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editing ? (
                      <div className="flex items-center">
                        <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} mr-2`}>$</span>
                        <input
                          type="number"
                          className={`border rounded px-3 py-2 w-full ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                          value={expense.amount || ''}
                          onChange={(e) => updateExpense(index, 'amount', e.target.value)}
                          placeholder="0.00"
                          min="0"
                          step="100"
                        />
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-300">
                        ${expense.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editing ? (
                      <select
                        className={`border rounded px-3 py-2 w-full ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                        value={expense.frequency}
                        onChange={(e) => updateExpense(index, 'frequency', e.target.value)}
                      >
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Quarterly">Quarterly</option>
                        <option value="Annual">Annual</option>
                      </select>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-300">
                        {expense.frequency}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    ${calculateAdjustedAmount(expense.amount, expense.frequency).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </td>
                  {editing && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => removeExpense(index)}
                        className={`${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-700'} transition-colors`}
                        title="Remove expense"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-6 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {editing ? (
            <div>
              <p className="mb-4">No expenses in this category yet.</p>
              <AddButton onClick={addExpense} />
            </div>
          ) : (
            <p>No expenses in this category.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PortalView;
